import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { formatCurrency, formatPercentage, formatDate } from '../utils/helpers';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';
import { 
  Plus, Megaphone, Tag, TrendingUp, TrendingDown, Percent, Target, 
  FileText, Calendar, Users, Eye, ShoppingCart, Instagram, Facebook,
  Mail, Globe, Smartphone, BarChart3, ArrowUpRight, ArrowDownRight, Edit, Trash2,
  Link, Link2, Search, Heart, MessageCircle, Share2, Clock, Sparkles, ExternalLink, Loader2, Zap, RefreshCw,
  MapPin, Phone, Building2, Euro, Receipt, DollarSign, X, Check, Music, PartyPopper, Utensils, Settings, Wallet, AlertTriangle, Linkedin,
  Hash, Lightbulb, Palette, BookOpen, PenLine, MessageSquare, Wand2, Copy, Archive, ArchiveRestore
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const COLORS = ['#C2410C', '#1A2E05', '#D97706', '#0891B2', '#7C3AED', '#DB2777', '#57534E'];

const channelIcons = {
  instagram: Instagram,
  facebook: Facebook,
  email: Mail,
  website: Globe,
  social_media: Smartphone,
  google_ads: Globe,
  flyer: FileText,
  tiktok: Smartphone,
  linkedin: Linkedin,
  twitter: Globe
};

const channelLabels = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  email: 'E-mail',
  website: 'Website',
  social_media: 'Social Media',
  google_ads: 'Google Ads',
  flyer: 'Flyers/Print',
  overig: 'Overig'
};

const typeLabels = {
  actie: 'Actie',
  korting: 'Korting',
  upsell: 'Upsell/Cross-sell',
  campagne: 'Campagne',
  evenement: 'Evenement'
};

const MarketingActiesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [channelDetailOpen, setChannelDetailOpen] = useState(false);
  const [selectedChannelDetail, setSelectedChannelDetail] = useState(null);
  const [campaignDetailOpen, setCampaignDetailOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  
  // Marketing Budget state
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetYear, setBudgetYear] = useState(new Date().getFullYear());
  const [marketingBudget, setMarketingBudget] = useState(null);
  const [budgetSpending, setBudgetSpending] = useState(null);
  const [budgetForm, setBudgetForm] = useState({
    annual_budget: '',
    distribution_type: 'equal'
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [linkCostDialogOpen, setLinkCostDialogOpen] = useState(false);
  const [linkRevenueDialogOpen, setLinkRevenueDialogOpen] = useState(false);
  const [availableCosts, setAvailableCosts] = useState([]);
  const [availableRevenue, setAvailableRevenue] = useState([]);
  const [showEventAnalytics, setShowEventAnalytics] = useState(true);
  const [eventSortBy, setEventSortBy] = useState('date'); // date, revenue, profit, guests, margin
  const [eventSortOrder, setEventSortOrder] = useState('desc');
  // Manual cost/revenue entry for events
  const [manualCostDialogOpen, setManualCostDialogOpen] = useState(false);
  const [manualRevenueDialogOpen, setManualRevenueDialogOpen] = useState(false);
  const [manualCostForm, setManualCostForm] = useState({
    description: '',
    amount: '',
    category: 'overig',
    date: new Date().toISOString().split('T')[0]
  });
  const [manualRevenueForm, setManualRevenueForm] = useState({
    description: '',
    amount: '',
    category: 'tickets',
    date: new Date().toISOString().split('T')[0],
    guests: ''
  });
  const [eventForm, setEventForm] = useState({
    name: '',
    event_type: 'feest',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    start_time: '18:00',
    end_time: '23:00',
    location: '',
    expected_guests: '',
    actual_guests: '',
    organizer_name: '',
    organizer_email: '',
    organizer_phone: '',
    organizer_company: '',
    ticket_price: '',
    cost_per_person: '',
    deposit_paid: '',
    status: 'gepland',
    notes: ''
  });

  // Arrangements state
  const [arrangements, setArrangements] = useState([]);
  const [arrangementStats, setArrangementStats] = useState(null);
  const [arrangementDialogOpen, setArrangementDialogOpen] = useState(false);
  const [arrangementDetailOpen, setArrangementDetailOpen] = useState(false);
  const [selectedArrangement, setSelectedArrangement] = useState(null);
  const [editingArrangement, setEditingArrangement] = useState(false);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [arrangementToBook, setArrangementToBook] = useState(null);
  const [linkCostArrDialogOpen, setLinkCostArrDialogOpen] = useState(false);
  const [linkRevenueArrDialogOpen, setLinkRevenueArrDialogOpen] = useState(false);
  const [arrangementForm, setArrangementForm] = useState({
    name: '',
    arrangement_type: 'feestmenu',
    description: '',
    price_type: 'per_persoon',
    price_per_person: '',
    fixed_price: '',
    cost_per_person: '',
    cost_fixed: '',
    min_guests: '',
    max_guests: '',
    included_items: [],
    status: 'beschikbaar',
    is_template: true,
    notes: ''
  });
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    booking_date: '',
    booking_time: '',
    actual_guests: '',
    deposit_paid: '',
    notes: ''
  });
  const [newIncludedItem, setNewIncludedItem] = useState('');
  const [arrangementSortBy, setArrangementSortBy] = useState('name'); // name, bookings, revenue, margin, margin_pct
  const [arrangementSortOrder, setArrangementSortOrder] = useState('desc');
  const [showArrangementAnalytics, setShowArrangementAnalytics] = useState(true);
  const [showArchivedBookings, setShowArchivedBookings] = useState(false); // Toggle for archived bookings
  // Manual cost/revenue entry for arrangements
  const [arrManualCostDialogOpen, setArrManualCostDialogOpen] = useState(false);
  const [arrManualRevenueDialogOpen, setArrManualRevenueDialogOpen] = useState(false);
  const [arrManualCostForm, setArrManualCostForm] = useState({
    description: '',
    amount: '',
    category: 'overig',
    date: new Date().toISOString().split('T')[0]
  });
  const [arrManualRevenueForm, setArrManualRevenueForm] = useState({
    description: '',
    amount: '',
    category: 'boeking',
    date: new Date().toISOString().split('T')[0],
    guests: ''
  });

  // Social Media state
  const [socialChannels, setSocialChannels] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [socialAnalytics, setSocialAnalytics] = useState(null);
  const [socialUrl, setSocialUrl] = useState('');
  const [analyzingUrl, setAnalyzingUrl] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [postForm, setPostForm] = useState({
    post_date: new Date().toISOString().split('T')[0],
    post_time: '12:00',
    content_type: 'image',
    description: '',
    likes: '',
    comments: '',
    shares: '',
    reach: ''
  });

  // Social Media Strategy state
  const [socialStrategy, setSocialStrategy] = useState(null);
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [activeStrategySection, setActiveStrategySection] = useState('pillars');
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [contentIdeas, setContentIdeas] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [aiTips, setAiTips] = useState([]);
  const [newPillarName, setNewPillarName] = useState('');
  const [newHashtag, setNewHashtag] = useState('');
  const [selectedHashtagGroup, setSelectedHashtagGroup] = useState(null);

  // Marketing Plan state
  const [marketingPlan, setMarketingPlan] = useState(null);
  const [planYear, setPlanYear] = useState(new Date().getFullYear());
  const [activePlanSection, setActivePlanSection] = useState('overview');
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [generatingSwot, setGeneratingSwot] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [planRecommendations, setPlanRecommendations] = useState([]);
  const [editingGoal, setEditingGoal] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);
  const [newSeasonDialogOpen, setNewSeasonDialogOpen] = useState(false);
  const [newSeasonForm, setNewSeasonForm] = useState({
    name: '',
    season: 'custom',
    suggested_start: '',
    suggested_end: '',
    description: '',
    suggested_actions: '',
    suggested_posts: '',
    suggested_decorations: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    type: 'actie',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    revenue_generated: '',
    cost: '',
    description: '',
    channel: '',
    target_audience: '',
    discount_percentage: '',
    discount_code: ''
  });

  // Event type labels
  const eventTypeLabels = {
    feest: 'Feest/Party',
    live_muziek: 'Live Muziek',
    thema_avond: 'Thema Avond',
    workshop: 'Workshop',
    proeverij: 'Proeverij',
    seizoen: 'Seizoensevenement',
    corporate: 'Zakelijk/Corporate',
    prive: 'Privé Feest',
    overig: 'Overig'
  };

  const eventStatusColors = {
    gepland: 'bg-blue-100 text-blue-700',
    bevestigd: 'bg-emerald-100 text-emerald-700',
    afgelopen: 'bg-stone-100 text-stone-700',
    geannuleerd: 'bg-rose-100 text-rose-700'
  };

  // Arrangement type labels
  const arrangementTypeLabels = {
    feestmenu: 'Feestmenu',
    catering: 'Catering',
    vergaderpakket: 'Vergaderpakket',
    groepsdeal: 'Groepsdeal',
    high_tea: 'High Tea',
    bbq: 'BBQ Arrangement',
    buffet: 'Buffet',
    walking_dinner: 'Walking Dinner',
    borrel: 'Borrel Pakket',
    lunch: 'Lunch Arrangement',
    diner: 'Diner Arrangement',
    overig: 'Overig'
  };

  const arrangementStatusColors = {
    beschikbaar: 'bg-emerald-100 text-emerald-700',
    geboekt: 'bg-blue-100 text-blue-700',
    afgerond: 'bg-stone-100 text-stone-700',
    geannuleerd: 'bg-rose-100 text-rose-700'
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'social') {
      fetchSocialData();
      fetchSocialStrategy();
    }
    if (activeTab === 'events') {
      fetchEventsData();
    }
    if (activeTab === 'arrangements') {
      fetchArrangementsData();
    }
    if (activeTab === 'plan') {
      fetchMarketingPlan(planYear);
      // Also fetch budget data to link with plan
      fetchBudgetData(planYear);
    }
  }, [activeTab, selectedChannel, planYear]);

  const fetchData = async () => {
    try {
      const [campaignsRes, statsRes] = await Promise.all([
        api.get('/campaigns'),
        api.get('/campaigns/stats').catch(() => ({ data: null }))
      ]);
      setCampaigns(campaignsRes.data);
      setStats(statsRes.data);
      // Also fetch budget data
      fetchBudgetData(budgetYear);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetData = async (year) => {
    try {
      const [budgetRes, spendingRes] = await Promise.all([
        api.get(`/marketing-budget/${year}`).catch(() => ({ data: null })),
        api.get(`/marketing-budget/${year}/spending`).catch(() => ({ data: null }))
      ]);
      setMarketingBudget(budgetRes.data);
      setBudgetSpending(spendingRes.data);
      if (budgetRes.data?.annual_budget) {
        setBudgetForm({
          annual_budget: budgetRes.data.annual_budget.toString(),
          distribution_type: budgetRes.data.distribution_type || 'equal'
        });
      }
      
      // Check for budget alerts
      if (spendingRes.data?.monthly) {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        
        // Only show alerts for current year
        if (year === currentYear) {
          const currentMonthData = spendingRes.data.monthly.find(m => m.month === currentMonth);
          
          if (currentMonthData && currentMonthData.budget > 0) {
            const percentage = currentMonthData.percentage;
            
            if (percentage >= 100) {
              toast.error(`⚠️ Marketing budget voor deze maand is overschreden! (${percentage.toFixed(0)}%)`, {
                duration: 6000
              });
            } else if (percentage >= 80) {
              toast.warning(`⚠️ Marketing budget voor deze maand is bijna op (${percentage.toFixed(0)}%)`, {
                duration: 5000
              });
            }
          }
          
          // Also check total year budget
          const totalPercentage = spendingRes.data.total_budget > 0 
            ? (spendingRes.data.total_spent / spendingRes.data.total_budget) * 100 
            : 0;
            
          if (totalPercentage >= 100) {
            toast.error(`⚠️ Jaarlijks marketing budget is overschreden! (${totalPercentage.toFixed(0)}%)`, {
              duration: 6000
            });
          } else if (totalPercentage >= 90) {
            toast.warning(`⚠️ Jaarlijks marketing budget is bijna op (${totalPercentage.toFixed(0)}%)`, {
              duration: 5000
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching budget:', error);
    }
  };

  const handleSaveBudget = async () => {
    try {
      await api.post('/marketing-budget', {
        year: budgetYear,
        annual_budget: parseFloat(budgetForm.annual_budget) || 0,
        distribution_type: budgetForm.distribution_type
      });
      toast.success('Marketing budget opgeslagen');
      setBudgetDialogOpen(false);
      fetchBudgetData(budgetYear);
    } catch (error) {
      toast.error('Fout bij opslaan budget');
    }
  };

  const handleUpdateMonthlyBudget = async (month, amount) => {
    try {
      await api.put(`/marketing-budget/${budgetYear}/monthly?month=${month}&amount=${amount}`);
      toast.success('Maandbudget bijgewerkt');
      fetchBudgetData(budgetYear);
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const fetchEventsData = async () => {
    try {
      const [eventsRes, statsRes] = await Promise.all([
        api.get('/events'),
        api.get('/events/stats/summary').catch(() => ({ data: null }))
      ]);
      setEvents(eventsRes.data);
      setEventStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchAvailableCosts = async () => {
    try {
      const [invoicesRes, ordersRes] = await Promise.all([
        api.get('/invoices').catch(() => ({ data: [] })),
        api.get('/orders').catch(() => ({ data: [] }))
      ]);
      const costs = [
        ...invoicesRes.data.map(i => ({ ...i, costType: 'factuur', displayName: `${i.supplier_name} - ${formatCurrency(i.total_amount)}` })),
        ...ordersRes.data.filter(o => o.status === 'ontvangen').map(o => ({ ...o, costType: 'bestelling', displayName: `Bestelling ${o.order_number} - ${formatCurrency(o.total_amount)}` }))
      ];
      setAvailableCosts(costs);
    } catch (error) {
      console.error('Error fetching costs:', error);
    }
  };

  const fetchAvailableRevenue = async () => {
    try {
      const res = await api.get('/revenue');
      setAvailableRevenue(res.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    // Calculate totals based on guests
    const guests = parseInt(eventForm.actual_guests) || parseInt(eventForm.expected_guests) || 0;
    const ticketPrice = parseFloat(eventForm.ticket_price) || 0;
    const costPerPerson = parseFloat(eventForm.cost_per_person) || 0;
    const totalRevenue = guests * ticketPrice;
    const totalCosts = guests * costPerPerson;
    
    try {
      await api.post('/events', {
        ...eventForm,
        expected_guests: parseInt(eventForm.expected_guests) || 0,
        actual_guests: parseInt(eventForm.actual_guests) || 0,
        ticket_price: ticketPrice,
        cost_per_person: costPerPerson,
        total_revenue: totalRevenue,
        total_costs: totalCosts,
        deposit_paid: parseFloat(eventForm.deposit_paid) || 0
      });
      toast.success('Event aangemaakt');
      setEventDialogOpen(false);
      resetEventForm();
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij aanmaken event');
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    
    // Calculate totals based on guests
    const guests = parseInt(eventForm.actual_guests) || parseInt(eventForm.expected_guests) || 0;
    const ticketPrice = parseFloat(eventForm.ticket_price) || 0;
    const costPerPerson = parseFloat(eventForm.cost_per_person) || 0;
    const totalRevenue = guests * ticketPrice;
    const totalCosts = guests * costPerPerson;
    
    try {
      await api.put(`/events/${selectedEvent.id}`, {
        ...eventForm,
        expected_guests: parseInt(eventForm.expected_guests) || 0,
        actual_guests: parseInt(eventForm.actual_guests) || 0,
        ticket_price: ticketPrice,
        cost_per_person: costPerPerson,
        total_revenue: totalRevenue,
        total_costs: totalCosts,
        deposit_paid: parseFloat(eventForm.deposit_paid) || 0
      });
      toast.success('Event bijgewerkt');
      setEditingEvent(false);
      fetchEventsData();
      // Refresh selected event
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
    } catch (error) {
      toast.error('Fout bij bijwerken event');
    }
  };

  // Calculate event totals for preview
  const calculateEventTotals = () => {
    const guests = parseInt(eventForm.actual_guests) || parseInt(eventForm.expected_guests) || 0;
    const ticketPrice = parseFloat(eventForm.ticket_price) || 0;
    const costPerPerson = parseFloat(eventForm.cost_per_person) || 0;
    
    const revenue = guests * ticketPrice;
    const costs = guests * costPerPerson;
    const profit = revenue - costs;
    const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0;
    
    return { revenue, costs, profit, marginPct, guests };
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Event verwijderen?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event verwijderd');
      setEventDetailOpen(false);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleLinkCost = async (costId) => {
    if (!selectedEvent) return;
    try {
      await api.post(`/events/${selectedEvent.id}/link-cost?cost_id=${costId}`);
      toast.success('Kosten gekoppeld');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij koppelen');
    }
  };

  const handleUnlinkCost = async (costId) => {
    if (!selectedEvent) return;
    try {
      await api.post(`/events/${selectedEvent.id}/unlink-cost?cost_id=${costId}`);
      toast.success('Kosten ontkoppeld');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij ontkoppelen');
    }
  };

  const handleLinkRevenue = async (revenueId) => {
    if (!selectedEvent) return;
    try {
      await api.post(`/events/${selectedEvent.id}/link-revenue?revenue_id=${revenueId}`);
      toast.success('Omzet gekoppeld');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij koppelen');
    }
  };

  const handleUnlinkRevenue = async (revenueId) => {
    if (!selectedEvent) return;
    try {
      await api.post(`/events/${selectedEvent.id}/unlink-revenue?revenue_id=${revenueId}`);
      toast.success('Omzet ontkoppeld');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij ontkoppelen');
    }
  };

  // Manual cost/revenue handlers for events
  const handleAddManualCost = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    try {
      await api.post('/event-costs', {
        event_id: selectedEvent.id,
        event_name: selectedEvent.name,
        description: manualCostForm.description,
        amount: parseFloat(manualCostForm.amount) || 0,
        category: manualCostForm.category,
        date: manualCostForm.date
      });
      toast.success('Kosten toegevoegd');
      setManualCostDialogOpen(false);
      setManualCostForm({ description: '', amount: '', category: 'overig', date: new Date().toISOString().split('T')[0] });
      // Refresh event data
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij toevoegen kosten');
    }
  };

  const handleDeleteManualCost = async (costId) => {
    try {
      await api.delete(`/event-costs/${costId}`);
      toast.success('Kosten verwijderd');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleAddManualRevenue = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    try {
      await api.post('/event-revenue', {
        event_id: selectedEvent.id,
        event_name: selectedEvent.name,
        description: manualRevenueForm.description,
        amount: parseFloat(manualRevenueForm.amount) || 0,
        category: manualRevenueForm.category,
        date: manualRevenueForm.date,
        guests: parseInt(manualRevenueForm.guests) || 0
      });
      toast.success('Omzet toegevoegd');
      setManualRevenueDialogOpen(false);
      setManualRevenueForm({ description: '', amount: '', category: 'tickets', date: new Date().toISOString().split('T')[0], guests: '' });
      // Refresh event data
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij toevoegen omzet');
    }
  };

  const handleDeleteManualRevenue = async (revenueId) => {
    try {
      await api.delete(`/event-revenue/${revenueId}`);
      toast.success('Omzet verwijderd');
      const res = await api.get(`/events/${selectedEvent.id}`);
      setSelectedEvent(res.data);
      fetchEventsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const openEventDetail = async (event) => {
    try {
      const res = await api.get(`/events/${event.id}`);
      setSelectedEvent(res.data);
      setEventDetailOpen(true);
      setEditingEvent(false);
    } catch (error) {
      toast.error('Fout bij laden details');
    }
  };

  const startEditEvent = () => {
    if (!selectedEvent) return;
    setEventForm({
      name: selectedEvent.name || '',
      event_type: selectedEvent.event_type || 'feest',
      description: selectedEvent.description || '',
      event_date: selectedEvent.event_date || '',
      start_time: selectedEvent.start_time || '',
      end_time: selectedEvent.end_time || '',
      location: selectedEvent.location || '',
      expected_guests: selectedEvent.expected_guests?.toString() || '',
      actual_guests: selectedEvent.actual_guests?.toString() || '',
      organizer_name: selectedEvent.organizer_name || '',
      organizer_email: selectedEvent.organizer_email || '',
      organizer_phone: selectedEvent.organizer_phone || '',
      organizer_company: selectedEvent.organizer_company || '',
      ticket_price: selectedEvent.ticket_price?.toString() || '',
      cost_per_person: selectedEvent.cost_per_person?.toString() || '',
      deposit_paid: selectedEvent.deposit_paid?.toString() || '',
      status: selectedEvent.status || 'gepland',
      notes: selectedEvent.notes || ''
    });
    setEditingEvent(true);
  };

  const resetEventForm = () => {
    setEventForm({
      name: '',
      event_type: 'feest',
      description: '',
      event_date: new Date().toISOString().split('T')[0],
      start_time: '18:00',
      end_time: '23:00',
      location: '',
      expected_guests: '',
      actual_guests: '',
      organizer_name: '',
      organizer_email: '',
      organizer_phone: '',
      organizer_company: '',
      ticket_price: '',
      cost_per_person: '',
      deposit_paid: '',
      status: 'gepland',
      notes: ''
    });
  };

  // ==================== ARRANGEMENTS FUNCTIONS ====================

  const fetchArrangementsData = async () => {
    try {
      const [arrangementsRes, statsRes] = await Promise.all([
        api.get('/arrangements'),
        api.get('/arrangements/stats/summary').catch(() => ({ data: null }))
      ]);
      setArrangements(arrangementsRes.data);
      setArrangementStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching arrangements:', error);
    }
  };

  const handleCreateArrangement = async (e) => {
    e.preventDefault();
    try {
      await api.post('/arrangements', {
        ...arrangementForm,
        price_per_person: parseFloat(arrangementForm.price_per_person) || 0,
        fixed_price: parseFloat(arrangementForm.fixed_price) || 0,
        cost_per_person: parseFloat(arrangementForm.cost_per_person) || 0,
        cost_fixed: parseFloat(arrangementForm.cost_fixed) || 0,
        min_guests: parseInt(arrangementForm.min_guests) || 0,
        max_guests: parseInt(arrangementForm.max_guests) || 0
      });
      toast.success('Arrangement aangemaakt');
      setArrangementDialogOpen(false);
      resetArrangementForm();
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleUpdateArrangement = async () => {
    if (!selectedArrangement) return;
    try {
      await api.put(`/arrangements/${selectedArrangement.id}`, {
        ...arrangementForm,
        price_per_person: parseFloat(arrangementForm.price_per_person) || 0,
        fixed_price: parseFloat(arrangementForm.fixed_price) || 0,
        cost_per_person: parseFloat(arrangementForm.cost_per_person) || 0,
        cost_fixed: parseFloat(arrangementForm.cost_fixed) || 0,
        min_guests: parseInt(arrangementForm.min_guests) || 0,
        max_guests: parseInt(arrangementForm.max_guests) || 0
      });
      toast.success('Arrangement bijgewerkt');
      setEditingArrangement(false);
      fetchArrangementsData();
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteArrangement = async (id) => {
    if (!window.confirm('Arrangement verwijderen?')) return;
    try {
      await api.delete(`/arrangements/${id}`);
      toast.success('Arrangement verwijderd');
      setArrangementDetailOpen(false);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleArchiveArrangement = async (id, currentArchived) => {
    try {
      await api.put(`/arrangements/${id}`, { archived: !currentArchived });
      toast.success(currentArchived ? 'Boeking uit archief gehaald' : 'Boeking gearchiveerd');
      fetchArrangementsData();
      // Close detail dialog if open
      if (selectedArrangement?.id === id) {
        setArrangementDetailOpen(false);
      }
    } catch (error) {
      toast.error('Fout bij archiveren');
    }
  };

  const handleBookArrangement = async (e) => {
    e.preventDefault();
    if (!arrangementToBook) return;
    
    // Bereken totalen
    const guests = parseInt(bookingForm.actual_guests) || 0;
    let totalRevenue = 0;
    let totalCosts = 0;
    
    if (arrangementToBook.price_type === 'per_persoon') {
      totalRevenue = guests * (arrangementToBook.price_per_person || 0);
      totalCosts = guests * (arrangementToBook.cost_per_person || 0);
    } else {
      totalRevenue = arrangementToBook.fixed_price || 0;
      totalCosts = arrangementToBook.cost_fixed || 0;
    }
    
    try {
      const params = new URLSearchParams({
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email,
        customer_phone: bookingForm.customer_phone,
        customer_company: bookingForm.customer_company,
        booking_date: bookingForm.booking_date,
        booking_time: bookingForm.booking_time,
        actual_guests: bookingForm.actual_guests,
        deposit_paid: bookingForm.deposit_paid || '0',
        total_revenue: totalRevenue.toString(),
        total_costs: totalCosts.toString(),
        notes: bookingForm.notes
      });
      await api.post(`/arrangements/${arrangementToBook.id}/book?${params.toString()}`);
      toast.success('Arrangement geboekt!');
      setBookDialogOpen(false);
      setBookingForm({ customer_name: '', customer_email: '', customer_phone: '', customer_company: '', booking_date: '', booking_time: '', actual_guests: '', deposit_paid: '', notes: '' });
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij boeken');
    }
  };

  const handleLinkCostToArrangement = async (costId) => {
    if (!selectedArrangement) return;
    try {
      await api.post(`/arrangements/${selectedArrangement.id}/link-cost?cost_id=${costId}`);
      toast.success('Kosten gekoppeld');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij koppelen');
    }
  };

  const handleUnlinkCostFromArrangement = async (costId) => {
    if (!selectedArrangement) return;
    try {
      await api.post(`/arrangements/${selectedArrangement.id}/unlink-cost?cost_id=${costId}`);
      toast.success('Kosten ontkoppeld');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij ontkoppelen');
    }
  };

  const handleLinkRevenueToArrangement = async (revenueId) => {
    if (!selectedArrangement) return;
    try {
      await api.post(`/arrangements/${selectedArrangement.id}/link-revenue?revenue_id=${revenueId}`);
      toast.success('Omzet gekoppeld');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij koppelen');
    }
  };

  const handleUnlinkRevenueFromArrangement = async (revenueId) => {
    if (!selectedArrangement) return;
    try {
      await api.post(`/arrangements/${selectedArrangement.id}/unlink-revenue?revenue_id=${revenueId}`);
      toast.success('Omzet ontkoppeld');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij ontkoppelen');
    }
  };

  // Manual cost/revenue handlers for arrangements
  const handleAddArrManualCost = async (e) => {
    e.preventDefault();
    if (!selectedArrangement) return;
    try {
      await api.post('/arrangement-costs', {
        arrangement_id: selectedArrangement.id,
        arrangement_name: selectedArrangement.name,
        description: arrManualCostForm.description,
        amount: parseFloat(arrManualCostForm.amount) || 0,
        category: arrManualCostForm.category,
        date: arrManualCostForm.date
      });
      toast.success('Kosten toegevoegd');
      setArrManualCostDialogOpen(false);
      setArrManualCostForm({ description: '', amount: '', category: 'overig', date: new Date().toISOString().split('T')[0] });
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij toevoegen kosten');
    }
  };

  const handleDeleteArrManualCost = async (costId) => {
    try {
      await api.delete(`/arrangement-costs/${costId}`);
      toast.success('Kosten verwijderd');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleAddArrManualRevenue = async (e) => {
    e.preventDefault();
    if (!selectedArrangement) return;
    try {
      await api.post('/arrangement-revenue', {
        arrangement_id: selectedArrangement.id,
        arrangement_name: selectedArrangement.name,
        description: arrManualRevenueForm.description,
        amount: parseFloat(arrManualRevenueForm.amount) || 0,
        category: arrManualRevenueForm.category,
        date: arrManualRevenueForm.date,
        guests: parseInt(arrManualRevenueForm.guests) || 0
      });
      toast.success('Omzet toegevoegd');
      setArrManualRevenueDialogOpen(false);
      setArrManualRevenueForm({ description: '', amount: '', category: 'boeking', date: new Date().toISOString().split('T')[0], guests: '' });
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij toevoegen omzet');
    }
  };

  const handleDeleteArrManualRevenue = async (revenueId) => {
    try {
      await api.delete(`/arrangement-revenue/${revenueId}`);
      toast.success('Omzet verwijderd');
      const res = await api.get(`/arrangements/${selectedArrangement.id}`);
      setSelectedArrangement(res.data);
      fetchArrangementsData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const openArrangementDetail = async (arrangement) => {
    try {
      const res = await api.get(`/arrangements/${arrangement.id}`);
      setSelectedArrangement(res.data);
      setArrangementDetailOpen(true);
      setEditingArrangement(false);
    } catch (error) {
      toast.error('Fout bij laden details');
    }
  };

  const startEditArrangement = () => {
    if (!selectedArrangement) return;
    setArrangementForm({
      name: selectedArrangement.name || '',
      arrangement_type: selectedArrangement.arrangement_type || 'feestmenu',
      description: selectedArrangement.description || '',
      price_type: selectedArrangement.price_type || 'per_persoon',
      price_per_person: selectedArrangement.price_per_person?.toString() || '',
      fixed_price: selectedArrangement.fixed_price?.toString() || '',
      cost_per_person: selectedArrangement.cost_per_person?.toString() || '',
      cost_fixed: selectedArrangement.cost_fixed?.toString() || '',
      min_guests: selectedArrangement.min_guests?.toString() || '',
      max_guests: selectedArrangement.max_guests?.toString() || '',
      included_items: selectedArrangement.included_items || [],
      status: selectedArrangement.status || 'beschikbaar',
      is_template: selectedArrangement.is_template,
      notes: selectedArrangement.notes || ''
    });
    setEditingArrangement(true);
  };

  const resetArrangementForm = () => {
    setArrangementForm({
      name: '',
      arrangement_type: 'feestmenu',
      description: '',
      price_type: 'per_persoon',
      price_per_person: '',
      fixed_price: '',
      cost_per_person: '',
      cost_fixed: '',
      min_guests: '',
      max_guests: '',
      included_items: [],
      status: 'beschikbaar',
      is_template: true,
      notes: ''
    });
    setNewIncludedItem('');
  };

  // Bereken totaal bedrag en kosten voor boeking
  const calculateBookingTotals = () => {
    if (!arrangementToBook || !bookingForm.actual_guests) return { revenue: 0, costs: 0, profit: 0 };
    const guests = parseInt(bookingForm.actual_guests) || 0;
    
    let revenue = 0;
    let costs = 0;
    
    if (arrangementToBook.price_type === 'per_persoon') {
      revenue = guests * (arrangementToBook.price_per_person || 0);
      costs = guests * (arrangementToBook.cost_per_person || 0);
    } else {
      revenue = arrangementToBook.fixed_price || 0;
      costs = arrangementToBook.cost_fixed || 0;
    }
    
    return { revenue, costs, profit: revenue - costs };
  };

  // Event Analytics Calculations
  const getEventAnalytics = () => {
    const now = new Date();
    const upcoming = events.filter(e => new Date(e.event_date) >= now && e.status !== 'geannuleerd');
    const past = events.filter(e => new Date(e.event_date) < now || e.status === 'afgelopen');
    const cancelled = events.filter(e => e.status === 'geannuleerd');
    
    // Calculate totals
    const totalRevenue = events.reduce((sum, e) => sum + (e.total_revenue || 0), 0);
    const totalCosts = events.reduce((sum, e) => sum + (e.total_costs || 0), 0);
    const totalProfit = totalRevenue - totalCosts;
    const avgMarginPct = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const totalGuests = events.reduce((sum, e) => sum + (e.actual_guests || e.expected_guests || 0), 0);
    
    // Events by type
    const byType = {};
    events.forEach(e => {
      if (!byType[e.event_type]) {
        byType[e.event_type] = { count: 0, revenue: 0, profit: 0 };
      }
      byType[e.event_type].count++;
      byType[e.event_type].revenue += e.total_revenue || 0;
      byType[e.event_type].profit += (e.total_revenue || 0) - (e.total_costs || 0);
    });
    
    // Sort events by different metrics
    const eventsWithMetrics = events.map(e => ({
      ...e,
      profit: (e.total_revenue || 0) - (e.total_costs || 0),
      margin_pct: e.total_revenue > 0 ? (((e.total_revenue - (e.total_costs || 0)) / e.total_revenue) * 100) : 0,
      guests: e.actual_guests || e.expected_guests || 0
    }));
    
    const byRevenue = [...eventsWithMetrics].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
    const byProfit = [...eventsWithMetrics].sort((a, b) => b.profit - a.profit);
    const byGuests = [...eventsWithMetrics].sort((a, b) => b.guests - a.guests);
    const byMargin = [...eventsWithMetrics].sort((a, b) => b.margin_pct - a.margin_pct);
    
    // Chart data - by event type
    const typeChartData = Object.entries(byType).map(([type, data]) => ({
      name: eventTypeLabels[type] || type,
      events: data.count,
      omzet: data.revenue,
      winst: data.profit
    }));
    
    // Monthly revenue chart
    const monthlyData = {};
    events.forEach(e => {
      const month = e.event_date?.substring(0, 7); // YYYY-MM
      if (month) {
        if (!monthlyData[month]) monthlyData[month] = { revenue: 0, costs: 0, count: 0 };
        monthlyData[month].revenue += e.total_revenue || 0;
        monthlyData[month].costs += e.total_costs || 0;
        monthlyData[month].count++;
      }
    });
    
    const monthlyChartData = Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, data]) => ({
        name: new Date(month + '-01').toLocaleDateString('nl-NL', { month: 'short' }),
        omzet: data.revenue,
        kosten: data.costs,
        events: data.count
      }));
    
    return {
      events: eventsWithMetrics,
      upcoming,
      past,
      cancelled,
      byRevenue,
      byProfit,
      byGuests,
      byMargin,
      byType,
      typeChartData,
      monthlyChartData,
      stats: {
        total: events.length,
        upcoming: upcoming.length,
        past: past.length,
        cancelled: cancelled.length,
        totalRevenue,
        totalCosts,
        totalProfit,
        avgMarginPct,
        totalGuests,
        avgRevenuePerEvent: events.length > 0 ? totalRevenue / events.length : 0
      }
    };
  };

  const getSortedEvents = () => {
    const analytics = getEventAnalytics();
    
    return analytics.events.sort((a, b) => {
      let comparison = 0;
      switch (eventSortBy) {
        case 'revenue':
          comparison = (b.total_revenue || 0) - (a.total_revenue || 0);
          break;
        case 'profit':
          comparison = b.profit - a.profit;
          break;
        case 'guests':
          comparison = b.guests - a.guests;
          break;
        case 'margin':
          comparison = b.margin_pct - a.margin_pct;
          break;
        default: // date
          comparison = new Date(b.event_date) - new Date(a.event_date);
      }
      return eventSortOrder === 'asc' ? -comparison : comparison;
    });
  };

  const addIncludedItem = () => {
    if (newIncludedItem.trim()) {
      setArrangementForm({
        ...arrangementForm,
        included_items: [...arrangementForm.included_items, newIncludedItem.trim()]
      });
      setNewIncludedItem('');
    }
  };

  const removeIncludedItem = (index) => {
    setArrangementForm({
      ...arrangementForm,
      included_items: arrangementForm.included_items.filter((_, i) => i !== index)
    });
  };

  // Arrangement Analytics Calculations
  const getArrangementAnalytics = () => {
    const templates = arrangements.filter(a => a.is_template);
    const bookings = arrangements.filter(a => !a.is_template);
    
    // Count bookings per template
    const bookingCounts = {};
    const revenuePerTemplate = {};
    const marginPerTemplate = {};
    
    templates.forEach(t => {
      bookingCounts[t.name] = 0;
      revenuePerTemplate[t.name] = 0;
      marginPerTemplate[t.name] = {
        margin: t.price_type === 'per_persoon' 
          ? (t.price_per_person || 0) - (t.cost_per_person || 0)
          : (t.fixed_price || 0) - (t.cost_fixed || 0),
        margin_pct: t.price_type === 'per_persoon' && t.price_per_person > 0
          ? ((t.price_per_person - (t.cost_per_person || 0)) / t.price_per_person) * 100
          : t.fixed_price > 0 
            ? ((t.fixed_price - (t.cost_fixed || 0)) / t.fixed_price) * 100
            : 0,
        price: t.price_type === 'per_persoon' ? t.price_per_person : t.fixed_price,
        cost: t.price_type === 'per_persoon' ? t.cost_per_person : t.cost_fixed,
        price_type: t.price_type
      };
    });
    
    bookings.forEach(b => {
      if (bookingCounts[b.name] !== undefined) {
        bookingCounts[b.name]++;
        revenuePerTemplate[b.name] += b.total_revenue || 0;
      }
    });
    
    // Create sorted arrays
    const byBookings = templates.map(t => ({
      ...t,
      bookings: bookingCounts[t.name] || 0,
      totalRevenue: revenuePerTemplate[t.name] || 0,
      margin: marginPerTemplate[t.name]?.margin || 0,
      margin_pct: marginPerTemplate[t.name]?.margin_pct || 0
    })).sort((a, b) => b.bookings - a.bookings);
    
    const byRevenue = [...byBookings].sort((a, b) => b.totalRevenue - a.totalRevenue);
    const byMargin = [...byBookings].sort((a, b) => b.margin - a.margin);
    const byMarginPct = [...byBookings].sort((a, b) => b.margin_pct - a.margin_pct);
    
    // Stats
    const totalBookings = bookings.length;
    const totalRevenue = bookings.reduce((sum, b) => sum + (b.total_revenue || 0), 0);
    const totalCosts = bookings.reduce((sum, b) => sum + (b.total_costs || 0), 0);
    const totalProfit = totalRevenue - totalCosts;
    const avgMarginPct = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    
    return {
      templates,
      bookings,
      byBookings,
      byRevenue,
      byMargin,
      byMarginPct,
      stats: {
        totalTemplates: templates.length,
        totalBookings,
        totalRevenue,
        totalCosts,
        totalProfit,
        avgMarginPct
      },
      chartData: byBookings.slice(0, 6).map(a => ({
        name: a.name.length > 15 ? a.name.substring(0, 15) + '...' : a.name,
        boekingen: a.bookings,
        omzet: a.totalRevenue,
        marge: a.margin_pct
      }))
    };
  };

  const getSortedArrangements = () => {
    const analytics = getArrangementAnalytics();
    const templates = analytics.templates.map(t => {
      const stats = analytics.byBookings.find(b => b.id === t.id) || t;
      return { ...t, ...stats };
    });
    
    return templates.sort((a, b) => {
      let comparison = 0;
      switch (arrangementSortBy) {
        case 'bookings':
          comparison = (b.bookings || 0) - (a.bookings || 0);
          break;
        case 'revenue':
          comparison = (b.totalRevenue || 0) - (a.totalRevenue || 0);
          break;
        case 'margin':
          comparison = (b.margin || 0) - (a.margin || 0);
          break;
        case 'margin_pct':
          comparison = (b.margin_pct || 0) - (a.margin_pct || 0);
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }
      return arrangementSortOrder === 'asc' ? -comparison : comparison;
    });
  };

  const fetchSocialData = async () => {
    try {
      const [channelsRes, postsRes, analyticsRes] = await Promise.all([
        api.get('/social-channels'),
        api.get('/social-posts' + (selectedChannel ? `?channel_id=${selectedChannel}` : '')),
        api.get('/social-analytics' + (selectedChannel ? `?channel_id=${selectedChannel}` : ''))
      ]);
      setSocialChannels(channelsRes.data || []);
      setSocialPosts(postsRes.data || []);
      setSocialAnalytics(analyticsRes.data || null);
    } catch (error) {
      console.error('Error fetching social data:', error);
    }
  };

  const handleAnalyzeUrl = async () => {
    if (!socialUrl.trim()) return;
    setAnalyzingUrl(true);
    try {
      const res = await api.post(`/social-channels/analyze-url?url=${encodeURIComponent(socialUrl)}`);
      toast.success(res.data.message || 'Profiel geanalyseerd');
      setSocialUrl('');
      await fetchSocialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij analyseren URL');
    } finally {
      setAnalyzingUrl(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Kanaal en alle posts verwijderen?')) return;
    try {
      await api.delete(`/social-channels/${channelId}`);
      toast.success('Kanaal verwijderd');
      setSelectedChannel(null);
      await fetchSocialData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleAddPost = async (e) => {
    e.preventDefault();
    if (!selectedChannel) {
      toast.error('Selecteer eerst een kanaal');
      return;
    }
    try {
      await api.post('/social-posts', {
        channel_id: selectedChannel,
        ...postForm,
        likes: parseInt(postForm.likes) || 0,
        comments: parseInt(postForm.comments) || 0,
        shares: parseInt(postForm.shares) || 0,
        reach: parseInt(postForm.reach) || 0,
        saves: 0,
        impressions: parseInt(postForm.reach) || 0,
        clicks: 0
      });
      toast.success('Post toegevoegd');
      setPostDialogOpen(false);
      setPostForm({
        post_date: new Date().toISOString().split('T')[0],
        post_time: '12:00',
        content_type: 'image',
        description: '',
        likes: '',
        comments: '',
        shares: '',
        reach: ''
      });
      await fetchSocialData();
    } catch (error) {
      toast.error('Fout bij toevoegen post');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.delete(`/social-posts/${postId}`);
      toast.success('Post verwijderd');
      await fetchSocialData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Social Media Strategy functions
  const fetchSocialStrategy = async () => {
    try {
      const res = await api.get('/social-strategy');
      setSocialStrategy(res.data || null);
    } catch (error) {
      console.error('Error fetching strategy:', error);
    }
  };

  const handleSaveStrategy = async (updates) => {
    try {
      const res = await api.post('/social-strategy', updates);
      setSocialStrategy(res.data);
      toast.success('Strategie opgeslagen');
    } catch (error) {
      toast.error('Fout bij opslaan strategie');
    }
  };

  const handleAddContentPillar = () => {
    if (!newPillarName.trim()) return;
    const newPillar = {
      id: Date.now().toString(),
      name: newPillarName,
      description: '',
      percentage: 0,
      color: COLORS[socialStrategy?.content_pillars?.length % COLORS.length || 0],
      examples: []
    };
    const updatedPillars = [...(socialStrategy?.content_pillars || []), newPillar];
    handleSaveStrategy({ content_pillars: updatedPillars });
    setNewPillarName('');
  };

  const handleUpdatePillar = (pillarId, updates) => {
    const updatedPillars = (socialStrategy?.content_pillars || []).map(p => 
      p.id === pillarId ? { ...p, ...updates } : p
    );
    handleSaveStrategy({ content_pillars: updatedPillars });
  };

  const handleDeletePillar = (pillarId) => {
    const updatedPillars = (socialStrategy?.content_pillars || []).filter(p => p.id !== pillarId);
    handleSaveStrategy({ content_pillars: updatedPillars });
  };

  const handleAddHashtagGroup = (groupName) => {
    const newGroup = {
      id: Date.now().toString(),
      name: groupName,
      hashtags: [],
      platform: null
    };
    const updatedGroups = [...(socialStrategy?.hashtag_groups || []), newGroup];
    handleSaveStrategy({ hashtag_groups: updatedGroups });
  };

  const handleAddHashtagToGroup = (groupId, hashtag) => {
    if (!hashtag.trim()) return;
    const formattedHashtag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    const updatedGroups = (socialStrategy?.hashtag_groups || []).map(g => 
      g.id === groupId ? { ...g, hashtags: [...g.hashtags, formattedHashtag] } : g
    );
    handleSaveStrategy({ hashtag_groups: updatedGroups });
    setNewHashtag('');
  };

  const handleRemoveHashtag = (groupId, hashtag) => {
    const updatedGroups = (socialStrategy?.hashtag_groups || []).map(g => 
      g.id === groupId ? { ...g, hashtags: g.hashtags.filter(h => h !== hashtag) } : g
    );
    handleSaveStrategy({ hashtag_groups: updatedGroups });
  };

  const handleDeleteHashtagGroup = (groupId) => {
    const updatedGroups = (socialStrategy?.hashtag_groups || []).filter(g => g.id !== groupId);
    handleSaveStrategy({ hashtag_groups: updatedGroups });
  };

  const handleUpdatePostingSchedule = (platform, updates) => {
    const existingSchedules = socialStrategy?.posting_schedules || [];
    const existingIndex = existingSchedules.findIndex(s => s.platform === platform);
    
    let updatedSchedules;
    if (existingIndex >= 0) {
      updatedSchedules = existingSchedules.map((s, i) => 
        i === existingIndex ? { ...s, ...updates } : s
      );
    } else {
      updatedSchedules = [...existingSchedules, { platform, posts_per_week: 3, best_times: [], best_days: [], ...updates }];
    }
    handleSaveStrategy({ posting_schedules: updatedSchedules });
  };

  const handleAddKPIGoal = (goal) => {
    const updatedGoals = [...(socialStrategy?.kpi_goals || []), goal];
    handleSaveStrategy({ kpi_goals: updatedGoals });
  };

  const handleRemoveKPIGoal = (index) => {
    const updatedGoals = (socialStrategy?.kpi_goals || []).filter((_, i) => i !== index);
    handleSaveStrategy({ kpi_goals: updatedGoals });
  };

  const handleGenerateContentIdeas = async () => {
    setGeneratingIdeas(true);
    try {
      const res = await api.post('/social-strategy/generate-ideas');
      if (res.data.success) {
        setContentIdeas(res.data.ideas || []);
        setWeeklySchedule(res.data.weekly_schedule || []);
        setAiTips(res.data.tips || []);
        toast.success(`${res.data.ideas?.length || 0} content ideeën gegenereerd!`);
      }
    } catch (error) {
      toast.error('Fout bij genereren content ideeën');
    } finally {
      setGeneratingIdeas(false);
    }
  };

  // Marketing Plan functions
  const fetchMarketingPlan = async (year) => {
    try {
      const res = await api.get(`/marketing-plan/${year}`);
      setMarketingPlan(res.data || null);
    } catch (error) {
      console.error('Error fetching marketing plan:', error);
    }
  };

  const handleSavePlan = async (updates) => {
    try {
      const res = await api.post(`/marketing-plan/${planYear}`, updates);
      setMarketingPlan(res.data);
      toast.success('Marketing plan opgeslagen');
    } catch (error) {
      toast.error('Fout bij opslaan plan');
    }
  };

  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await api.post(`/marketing-plan/${planYear}/generate-plan`);
      if (res.data.success) {
        setMarketingPlan(res.data.plan);
        setPlanRecommendations(res.data.recommendations || []);
        toast.success('Marketing plan gegenereerd!');
      }
    } catch (error) {
      if (error.response?.status === 412 || error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error('Fout bij genereren plan');
      }
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleGenerateSwot = async () => {
    setGeneratingSwot(true);
    try {
      const res = await api.post(`/marketing-plan/${planYear}/generate-swot`);
      if (res.data.success) {
        setMarketingPlan(prev => ({ ...prev, swot_items: res.data.swot_items }));
        toast.success('SWOT analyse gegenereerd!');
      }
    } catch (error) {
      if (error.response?.status === 412 || error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error('Fout bij genereren SWOT');
      }
    } finally {
      setGeneratingSwot(false);
    }
  };

  const [syncingGoals, setSyncingGoals] = useState(false);

  const handleSyncGoalsToStructuur = async () => {
    setSyncingGoals(true);
    try {
      const res = await api.post(`/marketing-plan/${planYear}/sync-goals`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Synchronisatie mislukt');
      }
    } catch (error) {
      toast.error('Fout bij synchroniseren naar Doelen & Structuur');
    } finally {
      setSyncingGoals(false);
    }
  };

  const handleAddGoal = () => {
    if (!newGoalTitle.trim()) return;
    const newGoal = {
      id: Date.now().toString(),
      title: newGoalTitle,
      description: '',
      target_value: null,
      current_value: 0,
      unit: '',
      deadline: null,
      status: 'active'
    };
    const updatedGoals = [...(marketingPlan?.goals || []), newGoal];
    handleSavePlan({ goals: updatedGoals });
    setNewGoalTitle('');
  };

  const handleUpdateGoal = (goalId, updates) => {
    const updatedGoals = (marketingPlan?.goals || []).map(g =>
      g.id === goalId ? { ...g, ...updates } : g
    );
    handleSavePlan({ goals: updatedGoals });
  };

  const handleDeleteGoal = (goalId) => {
    const updatedGoals = (marketingPlan?.goals || []).filter(g => g.id !== goalId);
    handleSavePlan({ goals: updatedGoals });
  };

  const handleUpdateQuarter = (quarter, updates) => {
    const quarters = marketingPlan?.quarters || [];
    const existingIndex = quarters.findIndex(q => q.quarter === quarter);
    let updatedQuarters;
    if (existingIndex >= 0) {
      updatedQuarters = quarters.map((q, i) =>
        i === existingIndex ? { ...q, ...updates } : q
      );
    } else {
      updatedQuarters = [...quarters, { quarter, year: planYear, ...updates }];
    }
    handleSavePlan({ quarters: updatedQuarters });
  };

  const handleAddSwotItem = (category) => {
    const newItem = {
      id: Date.now().toString(),
      category,
      text: '',
      priority: 'normal'
    };
    const updatedItems = [...(marketingPlan?.swot_items || []), newItem];
    handleSavePlan({ swot_items: updatedItems });
  };

  const handleUpdateSwotItem = (itemId, updates) => {
    const updatedItems = (marketingPlan?.swot_items || []).map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    );
    handleSavePlan({ swot_items: updatedItems });
  };

  const handleDeleteSwotItem = (itemId) => {
    const updatedItems = (marketingPlan?.swot_items || []).filter(item => item.id !== itemId);
    handleSavePlan({ swot_items: updatedItems });
  };

  const handleToggleSeasonalCampaign = (seasonId, isPlanned) => {
    const updatedSeasons = (marketingPlan?.seasonal_campaigns || []).map(s =>
      s.id === seasonId ? { ...s, is_planned: isPlanned, planned_year: isPlanned ? planYear : null } : s
    );
    handleSavePlan({ seasonal_campaigns: updatedSeasons });
  };

  const handleAddSeasonalCampaign = () => {
    if (!newSeasonForm.name.trim()) {
      toast.error('Vul een naam in');
      return;
    }
    const newSeason = {
      id: Date.now().toString(),
      season: newSeasonForm.season || 'custom',
      name: newSeasonForm.name,
      suggested_start: newSeasonForm.suggested_start,
      suggested_end: newSeasonForm.suggested_end,
      description: newSeasonForm.description,
      suggested_actions: newSeasonForm.suggested_actions.split(',').map(a => a.trim()).filter(Boolean),
      suggested_posts: newSeasonForm.suggested_posts.split(',').map(p => p.trim()).filter(Boolean),
      suggested_decorations: newSeasonForm.suggested_decorations.split(',').map(d => d.trim()).filter(Boolean),
      is_planned: false,
      planned_year: null
    };
    const updatedSeasons = [...(marketingPlan?.seasonal_campaigns || []), newSeason];
    handleSavePlan({ seasonal_campaigns: updatedSeasons });
    setNewSeasonDialogOpen(false);
    setNewSeasonForm({
      name: '',
      season: 'custom',
      suggested_start: '',
      suggested_end: '',
      description: '',
      suggested_actions: '',
      suggested_posts: '',
      suggested_decorations: ''
    });
    toast.success('Seizoen toegevoegd!');
  };

  const handleDeleteSeasonalCampaign = (seasonId) => {
    const updatedSeasons = (marketingPlan?.seasonal_campaigns || []).filter(s => s.id !== seasonId);
    handleSavePlan({ seasonal_campaigns: updatedSeasons });
    toast.success('Seizoen verwijderd');
  };

  const handleUpdateBudgetAllocation = (channel, amount) => {
    const allocations = marketingPlan?.budget_allocations || [];
    const totalBudget = marketingPlan?.total_budget || 0;
    const existingIndex = allocations.findIndex(a => a.channel === channel);
    
    let updatedAllocations;
    if (existingIndex >= 0) {
      updatedAllocations = allocations.map((a, i) =>
        i === existingIndex ? { ...a, amount, percentage: totalBudget > 0 ? (amount / totalBudget) * 100 : 0 } : a
      );
    } else {
      updatedAllocations = [...allocations, { channel, amount, percentage: totalBudget > 0 ? (amount / totalBudget) * 100 : 0 }];
    }
    handleSavePlan({ budget_allocations: updatedAllocations });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/campaigns', {
        ...formData,
        revenue_generated: parseFloat(formData.revenue_generated) || 0,
        cost: parseFloat(formData.cost) || 0,
        discount_percentage: parseFloat(formData.discount_percentage) || null
      });
      toast.success('Campagne toegevoegd');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'actie',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      revenue_generated: '',
      cost: '',
      description: '',
      channel: '',
      target_audience: '',
      discount_percentage: '',
      discount_code: ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Campagne verwijderen?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/campaigns/${id}`, { status });
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  // Calculate local metrics
  const activeCampaigns = campaigns.filter(c => c.status === 'actief');
  const totalRevenue = campaigns.reduce((sum, c) => sum + (c.revenue_generated || 0), 0);
  const totalCost = campaigns.reduce((sum, c) => sum + (c.cost || 0), 0);
  const roi = totalCost > 0 ? ((totalRevenue - totalCost) / totalCost) * 100 : 0;

  // Group by type for pie chart
  const typeData = Object.entries(
    campaigns.reduce((acc, c) => {
      const t = c.type || 'overig';
      acc[t] = (acc[t] || 0) + (c.revenue_generated || 0);
      return acc;
    }, {})
  ).map(([name, value]) => ({ name: typeLabels[name] || name, value }));

  if (loading) {
    return <div className="p-8 text-center">Laden...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="marketing-acties-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Marketing & Acties
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer campagnes, acties en meet de effectiviteit
          </p>
        </div>
        
        {/* Budget Dialog */}
        <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-600" />
                Marketing Budget {budgetYear}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label>Jaarbudget (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={budgetForm.annual_budget}
                  onChange={(e) => setBudgetForm({ ...budgetForm, annual_budget: e.target.value })}
                  placeholder="bijv. 12000"
                />
                <p className="text-xs text-[#57534E]">Het totale marketing budget voor {budgetYear}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Verdeling</Label>
                <Select 
                  value={budgetForm.distribution_type} 
                  onValueChange={(v) => setBudgetForm({ ...budgetForm, distribution_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equal">Gelijk (elke maand hetzelfde)</SelectItem>
                    <SelectItem value="seasonal">Seizoensgebonden (meer in drukke periodes)</SelectItem>
                    <SelectItem value="custom">Handmatig per maand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {budgetForm.distribution_type === 'equal' && budgetForm.annual_budget && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    <strong>Maandelijks budget:</strong> {formatCurrency(parseFloat(budgetForm.annual_budget) / 12)} per maand
                  </p>
                </div>
              )}
              
              {budgetForm.distribution_type === 'seasonal' && budgetForm.annual_budget && (
                <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                  <p className="text-sm font-medium text-amber-800">Seizoensgebonden verdeling:</p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div><span className="font-semibold">Jan-Feb:</span> 6%</div>
                    <div><span className="font-semibold">Mrt-Mei:</span> 9-10%</div>
                    <div><span className="font-semibold">Jun-Aug:</span> 7%</div>
                    <div><span className="font-semibold">Sep-Dec:</span> 9-10%</div>
                  </div>
                  <p className="text-xs text-amber-600">Meer budget in drukke horeca periodes (voorjaar & herfst/winter)</p>
                </div>
              )}
              
              {budgetForm.distribution_type === 'custom' && (
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-[#57534E]">
                    Na opslaan kun je het budget per maand handmatig aanpassen in het overzicht.
                  </p>
                </div>
              )}
              
              <Button onClick={handleSaveBudget} className="w-full btn-primary">
                <Check className="w-4 h-4 mr-2" />
                Budget Opslaan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe campagne
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nieuwe marketing campagne</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Naam *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="bijv. Zomer Actie"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actie">Actie</SelectItem>
                      <SelectItem value="korting">Korting</SelectItem>
                      <SelectItem value="upsell">Upsell/Cross-sell</SelectItem>
                      <SelectItem value="campagne">Campagne</SelectItem>
                      <SelectItem value="evenement">Evenement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kanaal</Label>
                  <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecteer kanaal" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="google_ads">Google Ads</SelectItem>
                      <SelectItem value="flyer">Flyers/Print</SelectItem>
                      <SelectItem value="social_media">Social Media (Overig)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Doelgroep</Label>
                  <Input
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    placeholder="bijv. Stamgasten, Nieuwe klanten"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Startdatum</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Einddatum</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Korting %</Label>
                  <Input type="number" value={formData.discount_percentage} onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })} placeholder="bijv. 15" />
                </div>
                <div className="space-y-2">
                  <Label>Kortingscode</Label>
                  <Input value={formData.discount_code} onChange={(e) => setFormData({ ...formData, discount_code: e.target.value })} placeholder="bijv. ZOMER2024" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Budget/Kosten (€)</Label>
                  <Input type="number" step="0.01" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>Gegenereerde omzet (€)</Label>
                  <Input type="number" step="0.01" value={formData.revenue_generated} onChange={(e) => setFormData({ ...formData, revenue_generated: e.target.value })} placeholder="0.00" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Beschrijving</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>

              <Button type="submit" className="w-full btn-primary">Opslaan</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Actieve Campagnes</p>
          <p className="kpi-value text-[#C2410C]">{activeCampaigns.length}</p>
          <p className="text-sm text-[#57534E]">van {campaigns.length} totaal</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Totale Omzet</p>
          <p className="kpi-value">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-[#57534E]">uit marketing</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Marketing Budget</p>
          <p className="kpi-value text-[#D97706]">{formatCurrency(totalCost)}</p>
          <p className="text-sm text-[#57534E]">uitgegeven</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">ROI</p>
          <p className={`kpi-value ${roi >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
          </p>
          <p className="text-sm text-[#57534E]">return on investment</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Beste Kanaal</p>
          <p className="kpi-value text-[#1A2E05] text-lg">{channelLabels[stats?.bestChannel] || '-'}</p>
          <p className="text-sm text-[#57534E]">hoogste omzet</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="plan" data-testid="plan-tab">
            <FileText className="w-4 h-4 mr-1" />
            Marketing Plan
          </TabsTrigger>
          <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
          <TabsTrigger value="acties">Acties</TabsTrigger>
          <TabsTrigger value="events" data-testid="events-tab">
            <Calendar className="w-4 h-4 mr-1" />
            Events
            {events.filter(e => e.status === 'gepland' || e.status === 'bevestigd').length > 0 && (
              <span className="ml-1 bg-[#C2410C] text-white text-xs px-1.5 rounded-full">
                {events.filter(e => e.status === 'gepland' || e.status === 'bevestigd').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="arrangements" data-testid="arrangements-tab">
            <Utensils className="w-4 h-4 mr-1" />
            Arrangementen
            {arrangements.filter(a => !a.is_template && a.status === 'geboekt').length > 0 && (
              <span className="ml-1 bg-[#C2410C] text-white text-xs px-1.5 rounded-full">
                {arrangements.filter(a => !a.is_template && a.status === 'geboekt').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="channels">Kanalen</TabsTrigger>
          <TabsTrigger value="social" data-testid="social-media-tab">
            <Instagram className="w-4 h-4 mr-1" />
            Social Media
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Marketing Budget Section */}
          <Card className="card-base border-2 border-emerald-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope' }}>Marketing Budget {budgetYear}</CardTitle>
                    <p className="text-sm text-[#57534E]">Jaarbudget en maandelijkse verdeling</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={budgetYear.toString()} onValueChange={(v) => { setBudgetYear(parseInt(v)); fetchBudgetData(parseInt(v)); }}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => setBudgetDialogOpen(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Budget Instellen
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Budget Alerts */}
              {budgetSpending && budgetYear === new Date().getFullYear() && (() => {
                const currentMonth = new Date().getMonth() + 1;
                const currentMonthData = budgetSpending.monthly?.find(m => m.month === currentMonth);
                const monthPercentage = currentMonthData?.percentage || 0;
                const yearPercentage = budgetSpending.total_budget > 0 
                  ? (budgetSpending.total_spent / budgetSpending.total_budget) * 100 
                  : 0;
                
                const alerts = [];
                
                // Check monthly budget
                if (monthPercentage >= 100) {
                  alerts.push({
                    type: 'error',
                    message: `Budget deze maand is overschreden met ${formatCurrency(currentMonthData.spent - currentMonthData.budget)}`,
                    detail: `Besteed: ${formatCurrency(currentMonthData.spent)} van ${formatCurrency(currentMonthData.budget)} (${monthPercentage.toFixed(0)}%)`
                  });
                } else if (monthPercentage >= 80) {
                  alerts.push({
                    type: 'warning',
                    message: `Budget deze maand is voor ${monthPercentage.toFixed(0)}% verbruikt`,
                    detail: `Nog ${formatCurrency(currentMonthData.remaining)} beschikbaar`
                  });
                }
                
                // Check yearly budget
                if (yearPercentage >= 100) {
                  alerts.push({
                    type: 'error',
                    message: `Jaarbudget is overschreden met ${formatCurrency(budgetSpending.total_spent - budgetSpending.total_budget)}`,
                    detail: `Besteed: ${formatCurrency(budgetSpending.total_spent)} van ${formatCurrency(budgetSpending.total_budget)} (${yearPercentage.toFixed(0)}%)`
                  });
                } else if (yearPercentage >= 80) {
                  alerts.push({
                    type: 'warning',
                    message: `Jaarbudget is voor ${yearPercentage.toFixed(0)}% verbruikt`,
                    detail: `Nog ${formatCurrency(budgetSpending.total_remaining)} beschikbaar voor ${12 - currentMonth} maanden`
                  });
                }
                
                if (alerts.length === 0) return null;
                
                return (
                  <div className="space-y-2">
                    {alerts.map((alert, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 ${
                          alert.type === 'error' 
                            ? 'bg-rose-50 border-rose-300 text-rose-800' 
                            : 'bg-amber-50 border-amber-300 text-amber-800'
                        }`}
                      >
                        <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          alert.type === 'error' ? 'text-rose-600' : 'text-amber-600'
                        }`} />
                        <div>
                          <p className="font-semibold">{alert.message}</p>
                          <p className="text-sm opacity-80">{alert.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Budget Summary */}
              <div className="grid grid-cols-5 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xs text-emerald-600 mb-1">Jaarbudget</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(budgetSpending?.total_budget || 0)}</p>
                </div>
                <div className="p-4 bg-rose-50 rounded-lg text-center">
                  <p className="text-xs text-rose-600 mb-1">Besteed</p>
                  <p className="text-2xl font-bold text-rose-700">{formatCurrency(budgetSpending?.total_spent || 0)}</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${(budgetSpending?.total_remaining || 0) >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                  <p className="text-xs mb-1">Resterend</p>
                  <p className={`text-2xl font-bold ${(budgetSpending?.total_remaining || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatCurrency(budgetSpending?.total_remaining || 0)}
                  </p>
                </div>
                <div className={`p-4 rounded-lg text-center ${(budgetSpending?.adjusted_monthly_budget || 0) > 0 ? 'bg-blue-50' : 'bg-stone-100'}`}>
                  <p className="text-xs text-blue-600 mb-1">Nieuw p/m</p>
                  <p className={`text-2xl font-bold ${(budgetSpending?.adjusted_monthly_budget || 0) > 0 ? 'text-blue-700' : 'text-stone-400'}`}>
                    {budgetSpending?.adjusted_monthly_budget > 0 
                      ? formatCurrency(budgetSpending.adjusted_monthly_budget)
                      : '-'}
                  </p>
                  {budgetSpending?.remaining_months > 0 && (
                    <p className="text-xs text-blue-500">{budgetSpending.remaining_months} maanden</p>
                  )}
                </div>
                <div className="p-4 bg-stone-100 rounded-lg text-center">
                  <p className="text-xs text-stone-600 mb-1">Verbruikt</p>
                  <p className="text-2xl font-bold text-stone-700">
                    {budgetSpending?.total_budget > 0 
                      ? `${Math.round((budgetSpending?.total_spent / budgetSpending?.total_budget) * 100)}%`
                      : '0%'}
                  </p>
                </div>
              </div>

              {/* Adjusted Budget Info */}
              {budgetSpending?.adjusted_monthly_budget > 0 && budgetSpending?.remaining_months > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <TrendingDown className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-800">Aangepast budget voor resterende maanden</p>
                      <p className="text-sm text-blue-700">
                        Op basis van je huidige besteding heb je nog <strong>{formatCurrency(budgetSpending.remaining_for_future || 0)}</strong> over 
                        voor de resterende <strong>{budgetSpending.remaining_months} maanden</strong>. 
                        Dit betekent <strong>{formatCurrency(budgetSpending.adjusted_monthly_budget)}</strong> per maand 
                        {budgetSpending.adjusted_monthly_budget < (budgetSpending.total_budget / 12) && (
                          <span className="text-blue-600"> (was {formatCurrency(budgetSpending.total_budget / 12)})</span>
                        )}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Monthly Budget Overview */}
              {budgetSpending?.monthly?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1C1917] mb-3">Budget per Maand</h4>
                  <div className="grid grid-cols-12 gap-2">
                    {budgetSpending.monthly.map((m) => {
                      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
                      const pct = m.budget > 0 ? (m.spent / m.budget) * 100 : 0;
                      const isOverBudget = m.is_over_budget || pct > 100;
                      const isFuture = !m.is_past && !m.is_current;
                      const hasAdjustedBudget = isFuture && m.adjusted_budget !== m.budget && m.adjusted_budget > 0;
                      
                      return (
                        <div 
                          key={m.month} 
                          className={`p-2 rounded-lg text-center border-2 ${
                            m.is_current ? 'border-[#C2410C] bg-orange-50' : 
                            isOverBudget ? 'border-rose-300 bg-rose-50' : 
                            m.is_past && pct > 80 ? 'border-amber-200 bg-amber-50' :
                            isFuture && hasAdjustedBudget ? 'border-blue-200 bg-blue-50' :
                            'border-stone-200 bg-stone-50'
                          }`}
                        >
                          <p className={`text-xs font-semibold ${m.is_current ? 'text-[#C2410C]' : 'text-[#57534E]'}`}>
                            {monthNames[m.month - 1]}
                          </p>
                          {/* Show adjusted budget for future months */}
                          {isFuture && hasAdjustedBudget ? (
                            <>
                              <p className="text-xs text-stone-400 line-through">{formatCurrency(m.budget)}</p>
                              <p className="text-sm font-bold text-blue-700">{formatCurrency(m.adjusted_budget)}</p>
                            </>
                          ) : (
                            <p className="text-sm font-bold text-[#1C1917]">{formatCurrency(m.budget)}</p>
                          )}
                          <div className="w-full h-1.5 bg-stone-200 rounded-full mt-1 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                isOverBudget ? 'bg-rose-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <p className={`text-xs mt-1 ${isOverBudget ? 'text-rose-600' : 'text-[#57534E]'}`}>
                            {formatCurrency(m.spent)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!marketingBudget?.annual_budget && (
                <div className="text-center py-8">
                  <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-[#57534E] mb-4">Nog geen marketing budget ingesteld voor {budgetYear}</p>
                  <Button onClick={() => setBudgetDialogOpen(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Budget Instellen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Type */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet per Type</CardTitle>
              </CardHeader>
              <CardContent>
                {typeData.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-[#57534E]">Nog geen data</div>
                )}
              </CardContent>
            </Card>

            {/* Channel Performance */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Kanaal Performance</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.channelStats?.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.channelStats.slice(0, 6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="channel" width={80} tickFormatter={(v) => channelLabels[v] || v} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="revenue" fill="#C2410C" name="Omzet" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-[#57534E]">Nog geen kanaal data</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Campaigns */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Actieve Campagnes & Acties</CardTitle>
            </CardHeader>
            <CardContent>
              {activeCampaigns.length === 0 ? (
                <p className="text-center py-8 text-[#57534E]">Geen actieve campagnes of acties</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeCampaigns.slice(0, 6).map(c => (
                    <div 
                      key={c.id} 
                      className="p-4 bg-stone-50 rounded-lg border border-stone-200 hover:border-[#C2410C] hover:shadow-md cursor-pointer transition-all"
                      onClick={() => { setSelectedCampaign(c); setCampaignDetailOpen(true); }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={c.type === 'korting' ? 'bg-amber-100 text-amber-700' : c.type === 'actie' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                          {typeLabels[c.type] || c.type}
                        </Badge>
                        {c.channel && <span className="text-xs text-[#57534E]">{channelLabels[c.channel]}</span>}
                      </div>
                      <h4 className="font-semibold text-[#1C1917]">{c.name}</h4>
                      <p className="text-sm text-[#57534E] mt-1">{c.start_date} - {c.end_date || 'lopend'}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-bold text-[#C2410C]">{formatCurrency(c.revenue_generated)}</span>
                        {c.discount_percentage && <Badge variant="outline">{c.discount_percentage}% korting</Badge>}
                      </div>
                      <p className="text-xs text-[#C2410C] mt-2">Klik voor details →</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Marketing Plan Tab */}
        <TabsContent value="plan" className="mt-6 space-y-6" data-testid="plan-content">
          {/* Plan Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                Marketing Plan {planYear}
              </h2>
              <p className="text-[#57534E] text-sm">
                Jaarlijks marketingplan met doelen, kalender en strategie
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={planYear.toString()} onValueChange={(v) => setPlanYear(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleSyncGoalsToStructuur}
                disabled={syncingGoals}
                variant="outline"
                className="border-purple-400 text-purple-700 hover:bg-purple-50"
                data-testid="sync-goals-btn"
              >
                {syncingGoals ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Synchroniseren...</>
                ) : (
                  <><Link2 className="w-4 h-4 mr-2" />Sync naar Doelen</>
                )}
              </Button>
              <Button 
                onClick={handleGeneratePlan}
                disabled={generatingPlan}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                data-testid="generate-plan-btn"
              >
                {generatingPlan ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Genereren...</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" />AI Plan Genereren</>
                )}
              </Button>
            </div>
          </div>

          {/* Plan Section Navigation */}
          <div className="flex flex-wrap gap-2 border-b pb-4">
            {[
              { key: 'overview', label: 'Jaaroverzicht', icon: Eye },
              { key: 'goals', label: 'Doelen', icon: Target },
              { key: 'quarters', label: 'Kwartalen', icon: Calendar },
              { key: 'budget', label: 'Budget', icon: Euro },
              { key: 'seasons', label: 'Seizoenen', icon: PartyPopper },
              { key: 'swot', label: 'SWOT', icon: BarChart3 },
              { key: 'calendar', label: 'Kalender', icon: Calendar }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activePlanSection === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActivePlanSection(tab.key)}
                className={activePlanSection === tab.key ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <tab.icon className="w-4 h-4 mr-1" />
                {tab.label}
              </Button>
            ))}
          </div>

          {/* Plan Overview Section */}
          {activePlanSection === 'overview' && (
            <div className="space-y-6">
              {/* Main Goal */}
              <Card className="card-base border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm text-emerald-600 font-medium">Hoofddoel {planYear}</Label>
                      <Textarea
                        placeholder="Wat is je belangrijkste marketingdoel dit jaar? (bijv. '20% meer omzet uit nieuwe klanten')"
                        value={marketingPlan?.main_goal || ''}
                        onChange={(e) => handleSavePlan({ main_goal: e.target.value })}
                        className="mt-2 text-lg font-medium border-emerald-200 focus:border-emerald-400"
                        rows={2}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* KPI Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="kpi-tile">
                  <p className="kpi-label">Doelen</p>
                  <p className="kpi-value text-emerald-600">{(marketingPlan?.goals || []).length}</p>
                  <p className="text-sm text-[#57534E]">{(marketingPlan?.goals || []).filter(g => g.status === 'completed').length} behaald</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Totaal Budget</p>
                  <p className="kpi-value">{formatCurrency(marketingPlan?.total_budget || 0)}</p>
                  <p className="text-sm text-[#57534E]">voor {planYear}</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Seizoenscampagnes</p>
                  <p className="kpi-value text-amber-600">
                    {(marketingPlan?.seasonal_campaigns || []).filter(s => s.is_planned).length}
                  </p>
                  <p className="text-sm text-[#57534E]">van {(marketingPlan?.seasonal_campaigns || []).length} gepland</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">SWOT Items</p>
                  <p className="kpi-value text-blue-600">{(marketingPlan?.swot_items || []).length}</p>
                  <p className="text-sm text-[#57534E]">geanalyseerd</p>
                </div>
              </div>

              {/* AI Recommendations */}
              {planRecommendations.length > 0 && (
                <Card className="card-base border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      AI Aanbevelingen
                    </h4>
                    <ul className="space-y-1">
                      {planRecommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                          <span className="text-amber-500">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Goals Section */}
          {activePlanSection === 'goals' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#1C1917]">Marketing Doelen {planYear}</h3>
                  <p className="text-sm text-[#57534E]">Stel meetbare doelen voor dit jaar</p>
                </div>
              </div>

              {/* Add Goal */}
              <div className="flex gap-2">
                <Input
                  placeholder="Nieuw doel toevoegen (bijv. '1000 nieuwe Instagram volgers')"
                  value={newGoalTitle}
                  onChange={(e) => setNewGoalTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddGoal()}
                  className="flex-1"
                />
                <Button onClick={handleAddGoal} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-1" />Toevoegen
                </Button>
              </div>

              {/* Goals Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(marketingPlan?.goals || []).map(goal => (
                  <div 
                    key={goal.id} 
                    className={`p-4 rounded-xl border-2 bg-white ${
                      goal.status === 'completed' ? 'border-emerald-300 bg-emerald-50' : 'border-stone-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <Input
                          value={goal.title}
                          onChange={(e) => handleUpdateGoal(goal.id, { title: e.target.value })}
                          className="font-semibold border-0 p-0 h-auto text-lg focus-visible:ring-0"
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={goal.status === 'completed' ? 'text-emerald-600' : 'text-stone-400'}
                          onClick={() => handleUpdateGoal(goal.id, { status: goal.status === 'completed' ? 'active' : 'completed' })}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-500"
                          onClick={() => handleDeleteGoal(goal.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      placeholder="Beschrijving..."
                      value={goal.description || ''}
                      onChange={(e) => handleUpdateGoal(goal.id, { description: e.target.value })}
                      className="mb-3 text-sm"
                      rows={2}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Doelwaarde</Label>
                        <Input
                          type="number"
                          value={goal.target_value || ''}
                          onChange={(e) => handleUpdateGoal(goal.id, { target_value: parseFloat(e.target.value) || null })}
                          placeholder="100"
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Eenheid</Label>
                        <Select 
                          value={goal.unit || ''} 
                          onValueChange={(v) => handleUpdateGoal(goal.id, { unit: v })}
                        >
                          <SelectTrigger className="h-8"><SelectValue placeholder="..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="%">%</SelectItem>
                            <SelectItem value="€">€</SelectItem>
                            <SelectItem value="aantal">aantal</SelectItem>
                            <SelectItem value="volgers">volgers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Huidig</Label>
                        <Input
                          type="number"
                          value={goal.current_value || ''}
                          onChange={(e) => handleUpdateGoal(goal.id, { current_value: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                          className="h-8"
                        />
                      </div>
                    </div>
                    {goal.target_value > 0 && (
                      <div className="mt-3">
                        <Progress value={(goal.current_value / goal.target_value) * 100} className="h-2" />
                        <p className="text-xs text-[#57534E] mt-1">
                          {goal.current_value} / {goal.target_value} {goal.unit} ({Math.round((goal.current_value / goal.target_value) * 100)}%)
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {(marketingPlan?.goals || []).length === 0 && (
                <div className="p-8 text-center bg-stone-50 rounded-xl">
                  <Target className="w-12 h-12 text-[#57534E] mx-auto mb-3" />
                  <p className="text-[#57534E]">Nog geen doelen. Voeg hierboven je eerste doel toe of laat AI een plan genereren.</p>
                </div>
              )}
            </div>
          )}

          {/* Quarters Section */}
          {activePlanSection === 'quarters' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-[#1C1917]">Kwartaalplanning {planYear}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(q => {
                  const quarter = (marketingPlan?.quarters || []).find(qtr => qtr.quarter === q) || { quarter: q };
                  const quarterMonths = {
                    1: 'Jan - Mrt',
                    2: 'Apr - Jun',
                    3: 'Jul - Sep',
                    4: 'Okt - Dec'
                  };
                  const quarterColors = {
                    1: 'border-blue-300 bg-blue-50',
                    2: 'border-emerald-300 bg-emerald-50',
                    3: 'border-amber-300 bg-amber-50',
                    4: 'border-purple-300 bg-purple-50'
                  };
                  return (
                    <div key={q} className={`p-4 rounded-xl border-2 ${quarterColors[q]}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-lg">Q{q} {planYear}</h4>
                          <p className="text-sm text-[#57534E]">{quarterMonths[q]}</p>
                        </div>
                        <Badge variant="outline">{formatCurrency(quarter.budget || 0)}</Badge>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Thema</Label>
                          <Input
                            placeholder="Kwartaal thema..."
                            value={quarter.theme || ''}
                            onChange={(e) => handleUpdateQuarter(q, { theme: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Budget (€)</Label>
                          <Input
                            type="number"
                            value={quarter.budget || ''}
                            onChange={(e) => handleUpdateQuarter(q, { budget: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Focus gebieden (komma gescheiden)</Label>
                          <Input
                            placeholder="Social media, Events, Email..."
                            value={(quarter.focus_areas || []).join(', ')}
                            onChange={(e) => handleUpdateQuarter(q, { focus_areas: e.target.value.split(',').map(f => f.trim()).filter(Boolean) })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Notities</Label>
                          <Textarea
                            placeholder="Aantekeningen voor dit kwartaal..."
                            value={quarter.notes || ''}
                            onChange={(e) => handleUpdateQuarter(q, { notes: e.target.value })}
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Budget Section */}
          {activePlanSection === 'budget' && (
            <div className="space-y-4">
              {/* Link to Marketing Budget */}
              {marketingBudget?.annual_budget && planYear === budgetYear && (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-emerald-800">Marketing Budget {budgetYear}</p>
                        <p className="text-sm text-emerald-600">
                          Jaarbudget: {formatCurrency(marketingBudget.annual_budget)} | 
                          Uitgegeven: {formatCurrency(budgetSpending?.total_spent || 0)} | 
                          Resterend: {formatCurrency((marketingBudget.annual_budget || 0) - (budgetSpending?.total_spent || 0))}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        handleSavePlan({ total_budget: marketingBudget.annual_budget });
                        toast.success('Budget gesynchroniseerd!');
                      }}
                      variant="outline"
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Synchroniseer naar Plan
                    </Button>
                  </div>
                </div>
              )}

              {/* Budget not set warning */}
              {!marketingBudget?.annual_budget && planYear === budgetYear && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Geen Marketing Budget ingesteld</p>
                      <p className="text-sm text-amber-600">
                        Ga naar het <button onClick={() => setActiveTab('overview')} className="underline font-medium">Overzicht tab</button> om eerst je jaarlijks marketing budget in te stellen.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#1C1917]">Budget Verdeling {planYear}</h3>
                  <p className="text-sm text-[#57534E]">Verdeel je marketingbudget over kanalen</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Totaal Budget:</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={marketingPlan?.total_budget || ''}
                      onChange={(e) => handleSavePlan({ total_budget: parseFloat(e.target.value) || 0 })}
                      className="w-32"
                      placeholder="€"
                    />
                    {marketingBudget?.annual_budget && marketingPlan?.total_budget !== marketingBudget.annual_budget && planYear === budgetYear && (
                      <span className="absolute -top-2 -right-2 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">!</span>
                      </span>
                    )}
                  </div>
                  {marketingBudget?.annual_budget && marketingPlan?.total_budget !== marketingBudget.annual_budget && planYear === budgetYear && (
                    <span className="text-xs text-amber-600">(Marketing budget: {formatCurrency(marketingBudget.annual_budget)})</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Budget Inputs */}
                <div className="space-y-3">
                  {[
                    { channel: 'social', label: 'Social Media', icon: Instagram, color: 'text-pink-600' },
                    { channel: 'events', label: 'Events & Acties', icon: PartyPopper, color: 'text-purple-600' },
                    { channel: 'print', label: 'Print & Flyers', icon: FileText, color: 'text-amber-600' },
                    { channel: 'google_ads', label: 'Google Ads', icon: Globe, color: 'text-blue-600' },
                    { channel: 'email', label: 'Email Marketing', icon: Mail, color: 'text-emerald-600' },
                    { channel: 'influencer', label: 'Influencer', icon: Users, color: 'text-rose-600' },
                    { channel: 'other', label: 'Overig', icon: Settings, color: 'text-stone-600' }
                  ].map(({ channel, label, icon: Icon, color }) => {
                    const allocation = (marketingPlan?.budget_allocations || []).find(a => a.channel === channel) || { amount: 0, percentage: 0 };
                    return (
                      <div key={channel} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                        <Icon className={`w-5 h-5 ${color}`} />
                        <span className="flex-1 font-medium">{label}</span>
                        <Input
                          type="number"
                          value={allocation.amount || ''}
                          onChange={(e) => handleUpdateBudgetAllocation(channel, parseFloat(e.target.value) || 0)}
                          className="w-28"
                          placeholder="€"
                        />
                        <span className="w-16 text-right text-sm text-[#57534E]">
                          {allocation.percentage?.toFixed(0) || 0}%
                        </span>
                      </div>
                    );
                  })}

                  {/* Remaining Budget Summary */}
                  {(() => {
                    const totalBudget = marketingPlan?.total_budget || 0;
                    const allocatedAmount = (marketingPlan?.budget_allocations || []).reduce((sum, a) => sum + (a.amount || 0), 0);
                    const remainingAmount = totalBudget - allocatedAmount;
                    const allocatedPercentage = totalBudget > 0 ? (allocatedAmount / totalBudget) * 100 : 0;
                    const remainingPercentage = 100 - allocatedPercentage;
                    const isOverBudget = remainingAmount < 0;
                    const isFullyAllocated = Math.abs(remainingAmount) < 0.01;
                    
                    return (
                      <div className={`p-4 rounded-lg border-2 ${
                        isOverBudget 
                          ? 'bg-rose-50 border-rose-300' 
                          : isFullyAllocated 
                            ? 'bg-emerald-50 border-emerald-300'
                            : 'bg-blue-50 border-blue-300'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-semibold ${
                            isOverBudget ? 'text-rose-700' : isFullyAllocated ? 'text-emerald-700' : 'text-blue-700'
                          }`}>
                            {isOverBudget ? '⚠️ Budget overschreden' : isFullyAllocated ? '✓ Volledig verdeeld' : '💰 Nog te verdelen'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-[#57534E]">Bedrag</p>
                            <p className={`text-xl font-bold ${
                              isOverBudget ? 'text-rose-600' : isFullyAllocated ? 'text-emerald-600' : 'text-blue-600'
                            }`}>
                              {isOverBudget ? '-' : ''}{formatCurrency(Math.abs(remainingAmount))}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-[#57534E]">Percentage</p>
                            <p className={`text-xl font-bold ${
                              isOverBudget ? 'text-rose-600' : isFullyAllocated ? 'text-emerald-600' : 'text-blue-600'
                            }`}>
                              {isOverBudget ? '-' : ''}{Math.abs(remainingPercentage).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-[#57534E] mb-1">
                            <span>Verdeeld: {formatCurrency(allocatedAmount)}</span>
                            <span>Totaal: {formatCurrency(totalBudget)}</span>
                          </div>
                          <Progress 
                            value={Math.min(allocatedPercentage, 100)} 
                            className={`h-2 ${isOverBudget ? '[&>div]:bg-rose-500' : '[&>div]:bg-emerald-500'}`}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Budget Chart */}
                <div className="p-4 bg-white rounded-xl border">
                  <h4 className="font-semibold mb-4">Budget Verdeling</h4>
                  {(marketingPlan?.budget_allocations || []).filter(a => a.amount > 0).length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={(marketingPlan?.budget_allocations || []).filter(a => a.amount > 0)}
                            dataKey="amount"
                            nameKey="channel"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ channel, percentage }) => `${channel}: ${percentage?.toFixed(0)}%`}
                          >
                            {(marketingPlan?.budget_allocations || []).map((entry, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-[#57534E]">
                      Vul bedragen in om de verdeling te zien
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Seasonal Campaigns Section */}
          {activePlanSection === 'seasons' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#1C1917]">Seizoenscampagnes {planYear}</h3>
                  <p className="text-sm text-[#57534E]">Plan je campagnes voor feestdagen en seizoenen</p>
                </div>
                <Button 
                  onClick={() => setNewSeasonDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-1" />Seizoen Toevoegen
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(marketingPlan?.seasonal_campaigns || []).map(season => {
                  const seasonIcons = {
                    nieuwjaar: '🎆',
                    valentijn: '❤️',
                    pasen: '🐰',
                    koningsdag: '🧡',
                    zomer: '☀️',
                    halloween: '🎃',
                    sintmaarten: '🏮',
                    sinterklaas: '🎁',
                    kerst: '🎄',
                    oudnieuw: '🥂',
                    custom: '📅'
                  };
                  return (
                    <div 
                      key={season.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        season.is_planned 
                          ? 'border-emerald-400 bg-emerald-50 shadow-md' 
                          : 'border-stone-200 bg-white hover:border-stone-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl">{seasonIcons[season.season] || '📅'}</span>
                        <div className="flex items-center gap-1">
                          {season.is_planned && (
                            <Badge className="bg-emerald-100 text-emerald-700">Gepland</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-rose-500 hover:bg-rose-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSeasonalCampaign(season.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div 
                        className="cursor-pointer"
                        onClick={() => handleToggleSeasonalCampaign(season.id, !season.is_planned)}
                      >
                        <h4 className="font-semibold text-[#1C1917]">{season.name}</h4>
                        <p className="text-xs text-[#57534E] mb-2">{season.suggested_start} - {season.suggested_end}</p>
                        <p className="text-sm text-[#57534E] mb-3">{season.description}</p>
                        
                        {/* Suggestions */}
                        <div className="space-y-2">
                          {season.suggested_actions?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-emerald-600">Acties:</p>
                              <div className="flex flex-wrap gap-1">
                                {season.suggested_actions.slice(0, 3).map((action, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{action}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add New Season Card */}
                <div 
                  onClick={() => setNewSeasonDialogOpen(true)}
                  className="p-4 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 hover:border-emerald-400 hover:bg-emerald-50 cursor-pointer transition-all flex flex-col items-center justify-center min-h-[200px]"
                >
                  <Plus className="w-10 h-10 text-stone-400 mb-2" />
                  <p className="text-[#57534E] font-medium">Nieuw seizoen</p>
                  <p className="text-xs text-[#78716C]">Voeg eigen campagne toe</p>
                </div>
              </div>

              {/* New Season Dialog */}
              <Dialog open={newSeasonDialogOpen} onOpenChange={setNewSeasonDialogOpen}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Nieuw Seizoen Toevoegen</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Naam *</Label>
                      <Input
                        placeholder="bijv. Moederdag, Terras Opening, Jubileum"
                        value={newSeasonForm.name}
                        onChange={(e) => setNewSeasonForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start datum</Label>
                        <Input
                          placeholder="DD-MM (bijv. 01-05)"
                          value={newSeasonForm.suggested_start}
                          onChange={(e) => setNewSeasonForm(prev => ({ ...prev, suggested_start: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Eind datum</Label>
                        <Input
                          placeholder="DD-MM (bijv. 15-05)"
                          value={newSeasonForm.suggested_end}
                          onChange={(e) => setNewSeasonForm(prev => ({ ...prev, suggested_end: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Beschrijving</Label>
                      <Textarea
                        placeholder="Korte beschrijving van dit seizoen/campagne..."
                        value={newSeasonForm.description}
                        onChange={(e) => setNewSeasonForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Actie suggesties (komma gescheiden)</Label>
                      <Input
                        placeholder="bijv. Speciaal menu, Korting, Cadeautje"
                        value={newSeasonForm.suggested_actions}
                        onChange={(e) => setNewSeasonForm(prev => ({ ...prev, suggested_actions: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Post suggesties (komma gescheiden)</Label>
                      <Input
                        placeholder="bijv. Aankondiging post, Behind the scenes"
                        value={newSeasonForm.suggested_posts}
                        onChange={(e) => setNewSeasonForm(prev => ({ ...prev, suggested_posts: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Decoratie suggesties (komma gescheiden)</Label>
                      <Input
                        placeholder="bijv. Bloemen, Speciale verlichting"
                        value={newSeasonForm.suggested_decorations}
                        onChange={(e) => setNewSeasonForm(prev => ({ ...prev, suggested_decorations: e.target.value }))}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setNewSeasonDialogOpen(false)}>
                        Annuleren
                      </Button>
                      <Button onClick={handleAddSeasonalCampaign} className="bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="w-4 h-4 mr-1" />Toevoegen
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* SWOT Section */}
          {activePlanSection === 'swot' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#1C1917]">SWOT Analyse</h3>
                  <p className="text-sm text-[#57534E]">Sterktes, Zwaktes, Kansen en Bedreigingen</p>
                </div>
                <Button 
                  onClick={handleGenerateSwot}
                  disabled={generatingSwot}
                  variant="outline"
                >
                  {generatingSwot ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyseren...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />AI SWOT Genereren</>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Strengths */}
                <div className="p-4 rounded-xl border-2 border-emerald-300 bg-emerald-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Sterktes (S)
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => handleAddSwotItem('strength')} className="text-emerald-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(marketingPlan?.swot_items || []).filter(i => i.category === 'strength').map(item => (
                      <div key={item.id} className="flex items-start gap-2 bg-white p-2 rounded-lg">
                        <Input
                          value={item.text}
                          onChange={(e) => handleUpdateSwotItem(item.id, { text: e.target.value })}
                          className="flex-1 border-0"
                          placeholder="Sterkte punt..."
                        />
                        <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeleteSwotItem(item.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weaknesses */}
                <div className="p-4 rounded-xl border-2 border-rose-300 bg-rose-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-rose-800 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Zwaktes (W)
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => handleAddSwotItem('weakness')} className="text-rose-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(marketingPlan?.swot_items || []).filter(i => i.category === 'weakness').map(item => (
                      <div key={item.id} className="flex items-start gap-2 bg-white p-2 rounded-lg">
                        <Input
                          value={item.text}
                          onChange={(e) => handleUpdateSwotItem(item.id, { text: e.target.value })}
                          className="flex-1 border-0"
                          placeholder="Zwakte punt..."
                        />
                        <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeleteSwotItem(item.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Opportunities */}
                <div className="p-4 rounded-xl border-2 border-blue-300 bg-blue-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Kansen (O)
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => handleAddSwotItem('opportunity')} className="text-blue-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(marketingPlan?.swot_items || []).filter(i => i.category === 'opportunity').map(item => (
                      <div key={item.id} className="flex items-start gap-2 bg-white p-2 rounded-lg">
                        <Input
                          value={item.text}
                          onChange={(e) => handleUpdateSwotItem(item.id, { text: e.target.value })}
                          className="flex-1 border-0"
                          placeholder="Kans..."
                        />
                        <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeleteSwotItem(item.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Threats */}
                <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Bedreigingen (T)
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => handleAddSwotItem('threat')} className="text-amber-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(marketingPlan?.swot_items || []).filter(i => i.category === 'threat').map(item => (
                      <div key={item.id} className="flex items-start gap-2 bg-white p-2 rounded-lg">
                        <Input
                          value={item.text}
                          onChange={(e) => handleUpdateSwotItem(item.id, { text: e.target.value })}
                          className="flex-1 border-0"
                          placeholder="Bedreiging..."
                        />
                        <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeleteSwotItem(item.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Section */}
          {activePlanSection === 'calendar' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#1C1917]">Marketing Kalender {planYear}</h3>
                  <p className="text-sm text-[#57534E]">Overzicht van alle geplande activiteiten</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCalendarMonth(prev => prev > 0 ? prev - 1 : 11)}
                  >
                    ←
                  </Button>
                  <span className="font-medium w-24 text-center">
                    {['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'][calendarMonth]}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCalendarMonth(prev => prev < 11 ? prev + 1 : 0)}
                  >
                    →
                  </Button>
                </div>
              </div>

              {/* Planned Seasons for this month */}
              {(() => {
                const plannedSeasons = (marketingPlan?.seasonal_campaigns || []).filter(s => {
                  if (!s.is_planned || !s.suggested_start) return false;
                  // Parse DD-MM format
                  const [startDay, startMonth] = s.suggested_start.split('-').map(Number);
                  const [endDay, endMonth] = (s.suggested_end || s.suggested_start).split('-').map(Number);
                  // Check if this season spans the current month
                  const currentMonth = calendarMonth + 1;
                  if (startMonth <= endMonth) {
                    return currentMonth >= startMonth && currentMonth <= endMonth;
                  } else {
                    // Season spans year end (e.g., Dec-Jan)
                    return currentMonth >= startMonth || currentMonth <= endMonth;
                  }
                });
                
                if (plannedSeasons.length === 0) return null;
                
                return (
                  <div className="flex flex-wrap gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="text-sm font-medium text-amber-800">Geplande seizoenen deze maand:</span>
                    {plannedSeasons.map(season => (
                      <Badge key={season.id} className="bg-amber-100 text-amber-800">
                        {season.name} ({season.suggested_start} - {season.suggested_end})
                      </Badge>
                    ))}
                  </div>
                );
              })()}

              {/* Month Grid View */}
              <div className="grid grid-cols-7 gap-1">
                {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
                  <div key={day} className="p-2 text-center font-semibold text-sm text-[#57534E] bg-stone-100 rounded">
                    {day}
                  </div>
                ))}
                {Array.from({ length: 35 }, (_, i) => {
                  const firstDay = new Date(planYear, calendarMonth, 1).getDay();
                  const daysInMonth = new Date(planYear, calendarMonth + 1, 0).getDate();
                  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
                  const dayNum = i - adjustedFirstDay + 1;
                  const isValidDay = dayNum > 0 && dayNum <= daysInMonth;
                  const dateStr = `${planYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const currentMonthNum = calendarMonth + 1;
                  
                  // Find events/campaigns on this day
                  const dayEvents = events.filter(e => e.event_date === dateStr);
                  const dayCampaigns = campaigns.filter(c => c.start_date <= dateStr && (!c.end_date || c.end_date >= dateStr));
                  
                  // Find seasonal campaigns that include this day
                  const daySeasons = (marketingPlan?.seasonal_campaigns || []).filter(s => {
                    if (!s.is_planned || !s.suggested_start) return false;
                    const [startDay, startMonth] = s.suggested_start.split('-').map(Number);
                    const [endDay, endMonth] = (s.suggested_end || s.suggested_start).split('-').map(Number);
                    
                    // Create date ranges for comparison
                    const isInRange = () => {
                      if (startMonth === endMonth) {
                        // Same month
                        return currentMonthNum === startMonth && dayNum >= startDay && dayNum <= endDay;
                      } else if (startMonth < endMonth) {
                        // Normal range within same year
                        if (currentMonthNum === startMonth) return dayNum >= startDay;
                        if (currentMonthNum === endMonth) return dayNum <= endDay;
                        return currentMonthNum > startMonth && currentMonthNum < endMonth;
                      } else {
                        // Range spans year end
                        if (currentMonthNum === startMonth) return dayNum >= startDay;
                        if (currentMonthNum === endMonth) return dayNum <= endDay;
                        return currentMonthNum > startMonth || currentMonthNum < endMonth;
                      }
                    };
                    return isInRange();
                  });
                  
                  return (
                    <div 
                      key={i} 
                      className={`p-2 min-h-[80px] rounded border ${
                        isValidDay 
                          ? daySeasons.length > 0 
                            ? 'bg-amber-50 border-amber-300' 
                            : 'bg-white border-stone-200 hover:border-[#C2410C]'
                          : 'bg-stone-50 border-transparent'
                      }`}
                    >
                      {isValidDay && (
                        <>
                          <p className="text-sm font-medium text-[#1C1917]">{dayNum}</p>
                          <div className="space-y-1 mt-1">
                            {daySeasons.slice(0, 1).map(season => (
                              <div key={season.id} className="text-xs p-1 rounded bg-amber-200 text-amber-800 truncate" title={season.name}>
                                {season.name}
                              </div>
                            ))}
                            {dayEvents.slice(0, 1).map(event => (
                              <div key={event.id} className="text-xs p-1 rounded bg-purple-100 text-purple-700 truncate">
                                {event.name}
                              </div>
                            ))}
                            {dayCampaigns.slice(0, 1).map(camp => (
                              <div key={camp.id} className="text-xs p-1 rounded bg-emerald-100 text-emerald-700 truncate">
                                {camp.name}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-200 border border-amber-400" />
                  <span>Seizoenen</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-purple-100 border border-purple-300" />
                  <span>Events</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-300" />
                  <span>Campagnes</span>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Alle Campagnes</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Naam</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Kanaal</TableHead>
                    <TableHead>Periode</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Omzet</TableHead>
                    <TableHead>ROI</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.filter(c => c.type === 'campagne' || c.type === 'korting').length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-[#57534E]">
                        Nog geen campagnes. Maak uw eerste campagne aan.
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.filter(c => c.type === 'campagne' || c.type === 'korting').sort((a, b) => new Date(b.start_date) - new Date(a.start_date)).map(c => {
                      const cRoi = c.cost > 0 ? ((c.revenue_generated - c.cost) / c.cost) * 100 : 0;
                      return (
                        <TableRow 
                          key={c.id} 
                          className="table-row cursor-pointer hover:bg-stone-50"
                          onClick={() => { setSelectedCampaign(c); setCampaignDetailOpen(true); }}
                        >
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{typeLabels[c.type] || c.type}</Badge>
                          </TableCell>
                          <TableCell>{channelLabels[c.channel] || '-'}</TableCell>
                          <TableCell className="text-sm">
                            {c.start_date}
                            {c.end_date && <span className="text-[#57534E]"> → {c.end_date}</span>}
                          </TableCell>
                          <TableCell>{formatCurrency(c.cost)}</TableCell>
                          <TableCell className="font-bold text-[#C2410C]">{formatCurrency(c.revenue_generated)}</TableCell>
                          <TableCell>
                            <span className={cRoi >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                              {cRoi >= 0 ? '+' : ''}{cRoi.toFixed(0)}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              c.status === 'actief' ? 'bg-emerald-100 text-emerald-700' :
                              c.status === 'concept' ? 'bg-stone-100 text-stone-700' :
                              c.status === 'afgelopen' ? 'bg-blue-100 text-blue-700' :
                              'bg-rose-100 text-rose-700'
                            }>{c.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedCampaign(c); setCampaignDetailOpen(true); }}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-rose-600" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Acties Tab */}
        <TabsContent value="acties" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Bedrijfsacties</CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuwe Actie
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Nieuwe Actie Toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const actieData = {
                      name: formData.get('name'),
                      type: 'actie',
                      description: formData.get('description'),
                      start_date: formData.get('start_date'),
                      end_date: formData.get('end_date'),
                      cost: parseFloat(formData.get('cost')) || 0,
                      revenue_generated: parseFloat(formData.get('revenue')) || 0,
                      channel: formData.get('channel'),
                      discount_percentage: parseFloat(formData.get('discount')) || null,
                      discount_code: formData.get('discount_code') || null,
                      status: 'actief'
                    };
                    api.post('/campaigns', actieData).then(() => {
                      toast.success('Actie toegevoegd');
                      fetchData();
                    }).catch(() => toast.error('Fout bij toevoegen'));
                  }} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam actie *</Label>
                      <Input name="name" placeholder="Bijv. Zondagse Brunch Deal" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Beschrijving</Label>
                      <Textarea name="description" placeholder="Wat houdt deze actie in?" rows={2} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Startdatum *</Label>
                        <Input type="date" name="start_date" required />
                      </div>
                      <div className="space-y-2">
                        <Label>Einddatum</Label>
                        <Input type="date" name="end_date" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Kosten (€)</Label>
                        <Input type="number" step="0.01" name="cost" placeholder="0.00" />
                      </div>
                      <div className="space-y-2">
                        <Label>Opbrengst (€)</Label>
                        <Input type="number" step="0.01" name="revenue" placeholder="0.00" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Kanaal</Label>
                      <Select name="channel" defaultValue="">
                        <SelectTrigger><SelectValue placeholder="Selecteer kanaal" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instagram">Instagram</SelectItem>
                          <SelectItem value="facebook">Facebook</SelectItem>
                          <SelectItem value="linkedin">LinkedIn</SelectItem>
                          <SelectItem value="google_ads">Google Ads</SelectItem>
                          <SelectItem value="email">E-mail</SelectItem>
                          <SelectItem value="flyer">Flyer/Print</SelectItem>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="lokaal">Lokale actie</SelectItem>
                          <SelectItem value="overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Korting %</Label>
                        <Input type="number" name="discount" placeholder="Bijv. 15" />
                      </div>
                      <div className="space-y-2">
                        <Label>Kortingscode</Label>
                        <Input name="discount_code" placeholder="Bijv. ACTIE20" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-primary">Actie Opslaan</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {campaigns.filter(c => c.type === 'actie').length === 0 ? (
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 mx-auto text-[#57534E]/30 mb-4" />
                  <p className="text-[#57534E] mb-2">Nog geen acties toegevoegd</p>
                  <p className="text-sm text-[#78716C]">Voeg een actie toe om te zien hoe deze presteert</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.filter(c => c.type === 'actie').sort((a, b) => new Date(b.start_date) - new Date(a.start_date)).map(actie => {
                    const actieRoi = actie.cost > 0 ? ((actie.revenue_generated - actie.cost) / actie.cost) * 100 : 0;
                    const isActive = actie.status === 'actief';
                    return (
                      <div 
                        key={actie.id}
                        className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                          isActive ? 'border-emerald-200 bg-emerald-50/30 hover:border-emerald-400' : 'border-stone-200 bg-stone-50 hover:border-stone-400'
                        }`}
                        onClick={() => { setSelectedCampaign(actie); setCampaignDetailOpen(true); }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg text-[#1C1917]">{actie.name}</h4>
                              <Badge className={isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-700'}>
                                {isActive ? 'Actief' : actie.status}
                              </Badge>
                              {actie.channel && (
                                <Badge variant="outline" className="text-xs">
                                  {channelLabels[actie.channel] || actie.channel}
                                </Badge>
                              )}
                            </div>
                            {actie.description && (
                              <p className="text-sm text-[#57534E] mb-3">{actie.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-[#78716C]">
                                <Calendar className="w-4 h-4 inline mr-1" />
                                {actie.start_date} {actie.end_date && `→ ${actie.end_date}`}
                              </span>
                              {actie.discount_percentage && (
                                <span className="text-amber-600 font-medium">
                                  <Percent className="w-4 h-4 inline mr-1" />
                                  {actie.discount_percentage}% korting
                                </span>
                              )}
                              {actie.discount_code && (
                                <span className="bg-stone-100 px-2 py-0.5 rounded text-xs font-mono">
                                  {actie.discount_code}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div>
                                <p className="text-xs text-[#78716C]">Kosten</p>
                                <p className="font-semibold text-rose-600">{formatCurrency(actie.cost)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#78716C]">Opbrengst</p>
                                <p className="font-semibold text-emerald-600">{formatCurrency(actie.revenue_generated)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#78716C]">ROI</p>
                                <p className={`font-semibold ${actieRoi >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                                  {actieRoi >= 0 ? '+' : ''}{actieRoi.toFixed(0)}%
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end mt-3 pt-3 border-t border-stone-200/50">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-rose-600"
                            onClick={(e) => { e.stopPropagation(); handleDelete(actie.id); }}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Arrangements Tab */}
        <TabsContent value="arrangements" className="mt-6 space-y-6" data-testid="arrangements-content">
          {/* Arrangements Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                Arrangementen
              </h2>
              <p className="text-[#57534E] text-sm">
                Beheer je catering pakketten, menu's en groepsdeals
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowArrangementAnalytics(!showArrangementAnalytics)}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {showArrangementAnalytics ? 'Verberg' : 'Toon'} Analytics
              </Button>
              <Dialog open={arrangementDialogOpen} onOpenChange={setArrangementDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" onClick={resetArrangementForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuw Arrangement
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nieuw arrangement aanmaken</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateArrangement} className="space-y-6 pt-4">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Naam arrangement *</Label>
                      <Input
                        value={arrangementForm.name}
                        onChange={(e) => setArrangementForm({ ...arrangementForm, name: e.target.value })}
                        placeholder="bijv. Luxe BBQ Pakket"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Type arrangement</Label>
                        <Select value={arrangementForm.arrangement_type} onValueChange={(v) => setArrangementForm({ ...arrangementForm, arrangement_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="feestmenu">🎉 Feestmenu</SelectItem>
                            <SelectItem value="catering">🍽️ Catering</SelectItem>
                            <SelectItem value="vergaderpakket">💼 Vergaderpakket</SelectItem>
                            <SelectItem value="groepsdeal">👥 Groepsdeal</SelectItem>
                            <SelectItem value="high_tea">🫖 High Tea</SelectItem>
                            <SelectItem value="bbq">🔥 BBQ Arrangement</SelectItem>
                            <SelectItem value="buffet">🍖 Buffet</SelectItem>
                            <SelectItem value="walking_dinner">🚶 Walking Dinner</SelectItem>
                            <SelectItem value="borrel">🍺 Borrel Pakket</SelectItem>
                            <SelectItem value="lunch">🥪 Lunch Arrangement</SelectItem>
                            <SelectItem value="diner">🍷 Diner Arrangement</SelectItem>
                            <SelectItem value="overig">📋 Overig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Prijstype</Label>
                        <Select value={arrangementForm.price_type} onValueChange={(v) => setArrangementForm({ ...arrangementForm, price_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="per_persoon">Per persoon</SelectItem>
                            <SelectItem value="vast_bedrag">Vast bedrag</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {arrangementForm.price_type === 'per_persoon' ? (
                        <div className="space-y-2">
                          <Label>Prijs per persoon (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={arrangementForm.price_per_person}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, price_per_person: e.target.value })}
                            placeholder="35.00"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Vast bedrag (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={arrangementForm.fixed_price}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, fixed_price: e.target.value })}
                            placeholder="500.00"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Min - Max gasten</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="number"
                            value={arrangementForm.min_guests}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, min_guests: e.target.value })}
                            placeholder="Min"
                            className="w-24"
                          />
                          <span className="text-[#57534E]">-</span>
                          <Input
                            type="number"
                            value={arrangementForm.max_guests}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, max_guests: e.target.value })}
                            placeholder="Max"
                            className="w-24"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Inkoopprijs */}
                    <div className="grid grid-cols-2 gap-4">
                      {arrangementForm.price_type === 'per_persoon' ? (
                        <div className="space-y-2">
                          <Label>Inkoopprijs per persoon (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={arrangementForm.cost_per_person}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, cost_per_person: e.target.value })}
                            placeholder="15.00"
                          />
                          <p className="text-xs text-[#57534E]">Jouw kosten per persoon</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Vaste inkoopprijs (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={arrangementForm.cost_fixed}
                            onChange={(e) => setArrangementForm({ ...arrangementForm, cost_fixed: e.target.value })}
                            placeholder="200.00"
                          />
                          <p className="text-xs text-[#57534E]">Jouw totale kosten</p>
                        </div>
                      )}
                      {arrangementForm.price_type === 'per_persoon' && arrangementForm.price_per_person && arrangementForm.cost_per_person && (
                        <div className="space-y-2">
                          <Label>Marge per persoon</Label>
                          <div className="p-3 bg-emerald-50 rounded-lg">
                            <p className="text-lg font-semibold text-emerald-700">
                              €{(parseFloat(arrangementForm.price_per_person) - parseFloat(arrangementForm.cost_per_person)).toFixed(2)}
                            </p>
                            <p className="text-xs text-emerald-600">
                              {((1 - parseFloat(arrangementForm.cost_per_person) / parseFloat(arrangementForm.price_per_person)) * 100).toFixed(0)}% marge
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Included Items */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1C1917] flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Wat is inbegrepen?
                    </h3>
                    <div className="flex gap-2">
                      <Input
                        value={newIncludedItem}
                        onChange={(e) => setNewIncludedItem(e.target.value)}
                        placeholder="bijv. 3-gangen menu"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIncludedItem())}
                      />
                      <Button type="button" variant="outline" onClick={addIncludedItem}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {arrangementForm.included_items.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {arrangementForm.included_items.map((item, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1">
                            {item}
                            <X className="w-3 h-3 cursor-pointer" onClick={() => removeIncludedItem(index)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label>Beschrijving</Label>
                    <Textarea
                      value={arrangementForm.description}
                      onChange={(e) => setArrangementForm({ ...arrangementForm, description: e.target.value })}
                      placeholder="Uitgebreide omschrijving van het arrangement..."
                      rows={3}
                    />
                  </div>

                  <Button type="submit" className="w-full btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Arrangement Aanmaken
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Arrangements Stats */}
          {arrangementStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#57534E]">Templates</p>
                      <p className="text-2xl font-bold text-[#1C1917]">{arrangementStats.total_templates}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-[#C2410C]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#57534E]">Boekingen</p>
                      <p className="text-2xl font-bold text-blue-600">{arrangementStats.total_bookings}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#57534E]">Aankomend</p>
                      <p className="text-2xl font-bold text-amber-600">{arrangementStats.upcoming_bookings}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#57534E]">Totale Omzet</p>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(arrangementStats.total_revenue)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#57534E]">Winst</p>
                      <p className={`text-2xl font-bold ${arrangementStats.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(arrangementStats.profit)}
                      </p>
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${arrangementStats.profit >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                      <Euro className={`w-5 h-5 ${arrangementStats.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Analytics Dashboard */}
          {showArrangementAnalytics && (() => {
            const analytics = getArrangementAnalytics();
            return (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <Card className="card-base bg-gradient-to-br from-[#C2410C]/5 to-[#C2410C]/10">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Templates</p>
                      <p className="text-2xl font-bold text-[#C2410C]">{analytics.stats.totalTemplates}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Boekingen</p>
                      <p className="text-2xl font-bold text-blue-600">{analytics.stats.totalBookings}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-emerald-50 to-emerald-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Totale Omzet</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(analytics.stats.totalRevenue)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-rose-50 to-rose-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Totale Kosten</p>
                      <p className="text-xl font-bold text-rose-600">{formatCurrency(analytics.stats.totalCosts)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-purple-50 to-purple-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Winst</p>
                      <p className="text-xl font-bold text-purple-600">{formatCurrency(analytics.stats.totalProfit)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-amber-50 to-amber-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Gem. Marge</p>
                      <p className="text-2xl font-bold text-amber-600">{analytics.stats.avgMarginPct.toFixed(0)}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts & Rankings */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Bar Chart */}
                  <Card className="card-base lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#C2410C]" />
                        Prestaties per Arrangement
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={analytics.chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                            <Tooltip 
                              formatter={(value, name) => {
                                if (name === 'omzet') return [formatCurrency(value), 'Omzet'];
                                if (name === 'marge') return [`${value.toFixed(0)}%`, 'Marge'];
                                return [value, 'Boekingen'];
                              }}
                            />
                            <Legend />
                            <Bar yAxisId="left" dataKey="boekingen" fill="#C2410C" name="Boekingen" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="right" dataKey="marge" fill="#10B981" name="Marge %" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-12 text-[#57534E]">
                          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                          <p>Nog geen data beschikbaar</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top Performers */}
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Top Performers
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Most Booked */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Meest Geboekt</p>
                        {analytics.byBookings.slice(0, 3).map((arr, i) => (
                          <div key={arr.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{arr.name}</span>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700">{arr.bookings}x</Badge>
                          </div>
                        ))}
                      </div>
                      
                      {/* Highest Margin */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Hoogste Marge</p>
                        {analytics.byMarginPct.slice(0, 3).map((arr, i) => (
                          <div key={arr.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{arr.name}</span>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">{arr.margin_pct.toFixed(0)}%</Badge>
                          </div>
                        ))}
                      </div>

                      {/* Most Revenue */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Meeste Omzet</p>
                        {analytics.byRevenue.filter(a => a.totalRevenue > 0).slice(0, 3).map((arr, i) => (
                          <div key={arr.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-purple-500 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{arr.name}</span>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">{formatCurrency(arr.totalRevenue)}</Badge>
                          </div>
                        ))}
                        {analytics.byRevenue.filter(a => a.totalRevenue > 0).length === 0 && (
                          <p className="text-sm text-[#57534E] italic">Nog geen omzet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Low Performers Alert */}
                {analytics.byMarginPct.filter(a => a.margin_pct < 30 && a.margin_pct > 0).length > 0 && (
                  <Card className="card-base border-amber-300 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <TrendingDown className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800">Let op: Lage marges</p>
                          <p className="text-sm text-amber-700 mt-1">
                            De volgende arrangementen hebben een marge onder 30%:
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {analytics.byMarginPct.filter(a => a.margin_pct < 30 && a.margin_pct > 0).map(arr => (
                              <Badge key={arr.id} className="bg-amber-200 text-amber-800">
                                {arr.name}: {arr.margin_pct.toFixed(0)}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* Sorting & Filtering */}
          <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
            <span className="text-sm font-medium text-[#57534E]">Sorteer op:</span>
            <Select value={arrangementSortBy} onValueChange={setArrangementSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Naam</SelectItem>
                <SelectItem value="bookings">Meest geboekt</SelectItem>
                <SelectItem value="revenue">Hoogste omzet</SelectItem>
                <SelectItem value="margin">Hoogste marge (€)</SelectItem>
                <SelectItem value="margin_pct">Hoogste marge (%)</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setArrangementSortOrder(arrangementSortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {arrangementSortOrder === 'desc' ? (
                <><TrendingDown className="w-4 h-4 mr-1" /> Aflopend</>
              ) : (
                <><TrendingUp className="w-4 h-4 mr-1" /> Oplopend</>
              )}
            </Button>
          </div>

          {/* Two columns: Templates and Bookings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Templates */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Utensils className="w-5 h-5" />
                  Arrangement Templates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {arrangements.filter(a => a.is_template).length === 0 ? (
                  <div className="text-center py-8 text-[#57534E]">
                    <Utensils className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>Nog geen arrangementen</p>
                    <p className="text-sm mt-1">Maak je eerste arrangement template aan</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getSortedArrangements().map((arr, index) => {
                      const analytics = getArrangementAnalytics();
                      const stats = analytics.byBookings.find(b => b.id === arr.id) || {};
                      return (
                        <div 
                          key={arr.id} 
                          className="p-4 border border-stone-200 rounded-lg hover:border-[#C2410C]/50 transition-all cursor-pointer"
                          onClick={() => openArrangementDetail(arr)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {arrangementSortBy === 'bookings' && stats.bookings > 0 && (
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-400 text-white' : index === 1 ? 'bg-stone-400 text-white' : index === 2 ? 'bg-amber-700 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                    {index + 1}
                                  </span>
                                )}
                                <p className="font-semibold text-[#1C1917]">{arr.name}</p>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {arrangementTypeLabels[arr.arrangement_type] || arr.arrangement_type}
                                </Badge>
                                {stats.bookings > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{stats.bookings} boekingen</Badge>
                                )}
                                {stats.margin_pct > 0 && (
                                  <Badge className={`text-xs ${stats.margin_pct >= 50 ? 'bg-emerald-100 text-emerald-700' : stats.margin_pct >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {stats.margin_pct.toFixed(0)}% marge
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#C2410C]">
                                {arr.price_type === 'per_persoon' 
                                  ? `${formatCurrency(arr.price_per_person)} p.p.` 
                                  : formatCurrency(arr.fixed_price)}
                            </p>
                            {arr.min_guests > 0 && (
                              <p className="text-xs text-[#57534E]">{arr.min_guests} - {arr.max_guests} pers.</p>
                            )}
                          </div>
                        </div>
                        {arr.included_items?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {arr.included_items.slice(0, 3).map((item, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{item}</Badge>
                            ))}
                            {arr.included_items.length > 3 && (
                              <Badge variant="secondary" className="text-xs">+{arr.included_items.length - 3}</Badge>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <Button size="sm" className="btn-primary flex-1" onClick={(e) => { 
                            e.stopPropagation(); 
                            setArrangementToBook(arr); 
                            setBookDialogOpen(true); 
                          }}>
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            Boeken
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openArrangementDetail(arr); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bookings */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Boekingen
                    {arrangements.filter(a => !a.is_template && !a.archived).length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 ml-2">
                        {arrangements.filter(a => !a.is_template && !a.archived).length}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {arrangements.filter(a => !a.is_template && a.archived).length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowArchivedBookings(!showArchivedBookings)}
                        className="text-xs"
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        {showArchivedBookings ? 'Verberg' : 'Toon'} archief ({arrangements.filter(a => !a.is_template && a.archived).length})
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {arrangements.filter(a => !a.is_template && (showArchivedBookings || !a.archived)).length === 0 ? (
                  <div className="text-center py-8 text-[#57534E]">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>Nog geen boekingen</p>
                    <p className="text-sm mt-1">Boek een arrangement om te beginnen</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {arrangements
                      .filter(a => !a.is_template && (showArchivedBookings || !a.archived))
                      .sort((a, b) => {
                        // Sort: non-archived first, then by date
                        if (a.archived !== b.archived) return a.archived ? 1 : -1;
                        return new Date(b.booking_date) - new Date(a.booking_date);
                      })
                      .map(booking => (
                      <div 
                        key={booking.id} 
                        className={`p-4 border rounded-lg hover:border-[#C2410C]/50 transition-all cursor-pointer ${
                          booking.archived ? 'border-stone-200 bg-stone-50 opacity-75' : 'border-stone-200'
                        }`}
                        onClick={() => openArrangementDetail(booking)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-[#1C1917]">{booking.name}</p>
                              {booking.archived && (
                                <Badge variant="outline" className="text-xs bg-stone-100">
                                  <Archive className="w-3 h-3 mr-1" />
                                  Gearchiveerd
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-[#57534E]">{booking.customer_name}</p>
                            {booking.customer_company && (
                              <p className="text-xs text-[#57534E]">{booking.customer_company}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={arrangementStatusColors[booking.status]}>{booking.status}</Badge>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveArrangement(booking.id, booking.archived);
                              }}
                              title={booking.archived ? 'Uit archief halen' : 'Archiveren'}
                            >
                              {booking.archived ? <ArchiveRestore className="w-4 h-4 text-stone-500" /> : <Archive className="w-4 h-4 text-stone-400" />}
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-[#57534E]">
                          {booking.booking_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(booking.booking_date)}
                            </span>
                          )}
                          {(booking.actual_guests > 0 || booking.guests > 0) && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {booking.actual_guests || booking.guests} pers.
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-rose-600">Kosten: {formatCurrency(booking.total_costs || 0)}</span>
                            <span className="text-emerald-600">Omzet: {formatCurrency(booking.total_revenue || 0)}</span>
                          </div>
                          <span className={`font-bold ${(booking.total_revenue - booking.total_costs) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency((booking.total_revenue || 0) - (booking.total_costs || 0))}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Arrangement Detail Dialog */}
          <Dialog open={arrangementDetailOpen} onOpenChange={setArrangementDetailOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedArrangement?.name}</span>
                  <div className="flex items-center gap-2">
                    {!editingArrangement && (
                      <>
                        <Button size="sm" variant="outline" onClick={startEditArrangement}>
                          <Edit className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                        {!selectedArrangement?.is_template && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleArchiveArrangement(selectedArrangement?.id, selectedArrangement?.archived)}
                          >
                            {selectedArrangement?.archived ? (
                              <><ArchiveRestore className="w-4 h-4 mr-1" />Uit archief</>
                            ) : (
                              <><Archive className="w-4 h-4 mr-1" />Archiveren</>
                            )}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => handleDeleteArrangement(selectedArrangement?.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              {selectedArrangement && (
                <div className="space-y-6 pt-4">
                  {editingArrangement ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Naam</Label>
                          <Input value={arrangementForm.name} onChange={(e) => setArrangementForm({ ...arrangementForm, name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={arrangementForm.arrangement_type} onValueChange={(v) => setArrangementForm({ ...arrangementForm, arrangement_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(arrangementTypeLabels).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Prijstype</Label>
                          <Select value={arrangementForm.price_type} onValueChange={(v) => setArrangementForm({ ...arrangementForm, price_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="per_persoon">Per persoon</SelectItem>
                              <SelectItem value="vast_bedrag">Vast bedrag</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{arrangementForm.price_type === 'per_persoon' ? 'Verkoopprijs p.p.' : 'Vast bedrag'}</Label>
                          <Input 
                            type="number" 
                            step="0.01"
                            value={arrangementForm.price_type === 'per_persoon' ? arrangementForm.price_per_person : arrangementForm.fixed_price}
                            onChange={(e) => setArrangementForm({ 
                              ...arrangementForm, 
                              [arrangementForm.price_type === 'per_persoon' ? 'price_per_person' : 'fixed_price']: e.target.value 
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{arrangementForm.price_type === 'per_persoon' ? 'Inkoopprijs p.p.' : 'Vaste inkoop'}</Label>
                          <Input 
                            type="number" 
                            step="0.01"
                            value={arrangementForm.price_type === 'per_persoon' ? arrangementForm.cost_per_person : arrangementForm.cost_fixed}
                            onChange={(e) => setArrangementForm({ 
                              ...arrangementForm, 
                              [arrangementForm.price_type === 'per_persoon' ? 'cost_per_person' : 'cost_fixed']: e.target.value 
                            })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select value={arrangementForm.status} onValueChange={(v) => setArrangementForm({ ...arrangementForm, status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beschikbaar">Beschikbaar</SelectItem>
                              <SelectItem value="geboekt">Geboekt</SelectItem>
                              <SelectItem value="afgerond">Afgerond</SelectItem>
                              <SelectItem value="geannuleerd">Geannuleerd</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {arrangementForm.price_per_person && arrangementForm.cost_per_person && arrangementForm.price_type === 'per_persoon' && (
                          <div className="p-3 bg-emerald-50 rounded-lg">
                            <p className="text-sm text-[#57534E]">Berekende marge</p>
                            <p className="text-lg font-bold text-emerald-600">
                              {formatCurrency(parseFloat(arrangementForm.price_per_person) - parseFloat(arrangementForm.cost_per_person))} p.p.
                              <span className="text-sm font-normal ml-2">
                                ({(((parseFloat(arrangementForm.price_per_person) - parseFloat(arrangementForm.cost_per_person)) / parseFloat(arrangementForm.price_per_person)) * 100).toFixed(0)}%)
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Beschrijving</Label>
                        <Textarea value={arrangementForm.description} onChange={(e) => setArrangementForm({ ...arrangementForm, description: e.target.value })} rows={3} />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleUpdateArrangement} className="btn-primary">Opslaan</Button>
                        <Button variant="outline" onClick={() => setEditingArrangement(false)}>Annuleren</Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <>
                      {/* Type & Status */}
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{arrangementTypeLabels[selectedArrangement.arrangement_type] || selectedArrangement.arrangement_type}</Badge>
                        <Badge className={arrangementStatusColors[selectedArrangement.status]}>{selectedArrangement.status}</Badge>
                        {selectedArrangement.is_template && <Badge className="bg-purple-100 text-purple-700">Template</Badge>}
                      </div>

                      {/* Price Info */}
                      <div className="p-4 bg-[#C2410C]/10 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-[#57534E]">Verkoopprijs</p>
                            <p className="text-2xl font-bold text-[#C2410C]">
                              {selectedArrangement.price_type === 'per_persoon' 
                                ? `${formatCurrency(selectedArrangement.price_per_person)} per persoon` 
                                : formatCurrency(selectedArrangement.fixed_price)}
                            </p>
                          </div>
                          {selectedArrangement.min_guests > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-[#57534E]">Aantal personen</p>
                              <p className="font-semibold">{selectedArrangement.min_guests} - {selectedArrangement.max_guests}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Cost & Margin Info */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-stone-100 rounded-lg">
                          <p className="text-sm text-[#57534E]">Inkoopprijs</p>
                          <p className="text-xl font-bold text-[#1C1917]">
                            {selectedArrangement.price_type === 'per_persoon' 
                              ? (selectedArrangement.cost_per_person > 0 
                                  ? `${formatCurrency(selectedArrangement.cost_per_person)} p.p.` 
                                  : 'Niet ingesteld')
                              : (selectedArrangement.cost_fixed > 0 
                                  ? formatCurrency(selectedArrangement.cost_fixed) 
                                  : 'Niet ingesteld')}
                          </p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-lg">
                          <p className="text-sm text-[#57534E]">Marge per {selectedArrangement.price_type === 'per_persoon' ? 'persoon' : 'arrangement'}</p>
                          <p className="text-xl font-bold text-emerald-600">
                            {selectedArrangement.price_type === 'per_persoon' 
                              ? (selectedArrangement.cost_per_person > 0 
                                  ? formatCurrency(selectedArrangement.price_per_person - selectedArrangement.cost_per_person)
                                  : formatCurrency(selectedArrangement.price_per_person))
                              : (selectedArrangement.cost_fixed > 0 
                                  ? formatCurrency(selectedArrangement.fixed_price - selectedArrangement.cost_fixed)
                                  : formatCurrency(selectedArrangement.fixed_price))}
                          </p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-[#57534E]">Marge %</p>
                          <p className="text-xl font-bold text-blue-600">
                            {selectedArrangement.price_type === 'per_persoon' 
                              ? (selectedArrangement.price_per_person > 0 
                                  ? `${(((selectedArrangement.price_per_person - (selectedArrangement.cost_per_person || 0)) / selectedArrangement.price_per_person) * 100).toFixed(0)}%`
                                  : '-')
                              : (selectedArrangement.fixed_price > 0 
                                  ? `${(((selectedArrangement.fixed_price - (selectedArrangement.cost_fixed || 0)) / selectedArrangement.fixed_price) * 100).toFixed(0)}%`
                                  : '-')}
                          </p>
                        </div>
                      </div>

                      {/* Included Items */}
                      {selectedArrangement.included_items?.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-[#1C1917] flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-600" />
                            Inbegrepen
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedArrangement.included_items.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 bg-emerald-50 rounded">
                                <Check className="w-4 h-4 text-emerald-600" />
                                <span className="text-sm">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Customer Info (for bookings) */}
                      {!selectedArrangement.is_template && selectedArrangement.customer_name && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4" />
                            Klant
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-600" />
                              <span>{selectedArrangement.customer_name}</span>
                            </div>
                            {selectedArrangement.customer_company && (
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                <span>{selectedArrangement.customer_company}</span>
                              </div>
                            )}
                            {selectedArrangement.customer_email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-600" />
                                <span>{selectedArrangement.customer_email}</span>
                              </div>
                            )}
                            {selectedArrangement.customer_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600" />
                                <span>{selectedArrangement.customer_phone}</span>
                              </div>
                            )}
                            {selectedArrangement.booking_date && (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-blue-600" />
                                <span>{formatDate(selectedArrangement.booking_date)} {selectedArrangement.booking_time}</span>
                              </div>
                            )}
                            {selectedArrangement.actual_guests > 0 && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-600" />
                                <span>{selectedArrangement.actual_guests} personen</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Financial Summary (for bookings) */}
                      {!selectedArrangement.is_template && (
                        <>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 bg-rose-50 rounded-lg text-center">
                              <p className="text-xs text-rose-600 mb-1">Kosten</p>
                              <p className="text-xl font-bold text-rose-700">{formatCurrency(selectedArrangement.total_costs || 0)}</p>
                            </div>
                            <div className="p-4 bg-emerald-50 rounded-lg text-center">
                              <p className="text-xs text-emerald-600 mb-1">Omzet</p>
                              <p className="text-xl font-bold text-emerald-700">{formatCurrency(selectedArrangement.total_revenue || 0)}</p>
                            </div>
                            <div className={`p-4 rounded-lg text-center ${((selectedArrangement.total_revenue || 0) - (selectedArrangement.total_costs || 0)) >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                              <p className="text-xs mb-1">Resultaat</p>
                              <p className={`text-xl font-bold ${((selectedArrangement.total_revenue || 0) - (selectedArrangement.total_costs || 0)) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                {formatCurrency((selectedArrangement.total_revenue || 0) - (selectedArrangement.total_costs || 0))}
                              </p>
                            </div>
                          </div>

                          {/* Linked Costs */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-[#1C1917] flex items-center gap-2">
                                <Receipt className="w-4 h-4" />
                                Kosten
                              </h4>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setArrManualCostDialogOpen(true)}>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Toevoegen
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { fetchAvailableCosts(); setLinkCostArrDialogOpen(true); }}>
                                  <Link className="w-4 h-4 mr-1" />
                                  Koppelen
                                </Button>
                              </div>
                            </div>
                            
                            {/* Manual costs */}
                            {selectedArrangement.manual_costs?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Handmatig toegevoegd</p>
                                {selectedArrangement.manual_costs.map(cost => (
                                  <div key={cost.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-200">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded bg-rose-100 flex items-center justify-center">
                                        <Euro className="w-4 h-4 text-rose-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{cost.description}</p>
                                        <p className="text-xs text-[#57534E]">{cost.category} • {cost.date}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-rose-700">{formatCurrency(cost.amount)}</span>
                                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteArrManualCost(cost.id)}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Linked costs */}
                            {selectedArrangement.linked_costs_details?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-rose-500 uppercase tracking-wide">Gekoppeld</p>
                                {selectedArrangement.linked_costs_details.map(cost => (
                                  <div key={cost.id} className="flex items-center justify-between p-3 bg-rose-50/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded bg-rose-100 flex items-center justify-center">
                                        <Receipt className="w-4 h-4 text-rose-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{cost.supplier || cost.description}</p>
                                        <p className="text-xs text-[#57534E]">{cost.type} • {cost.date}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-rose-700">{formatCurrency(cost.amount)}</span>
                                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleUnlinkCostFromArrangement(cost.id)}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {(!selectedArrangement.manual_costs?.length && !selectedArrangement.linked_costs_details?.length) && (
                              <p className="text-sm text-[#57534E] text-center py-4">Geen kosten toegevoegd</p>
                            )}
                          </div>

                          {/* Linked Revenue */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-[#1C1917] flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Omzet
                              </h4>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setArrManualRevenueDialogOpen(true)}>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Toevoegen
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { fetchAvailableRevenue(); setLinkRevenueArrDialogOpen(true); }}>
                                  <Link className="w-4 h-4 mr-1" />
                                  Koppelen
                                </Button>
                              </div>
                            </div>
                            
                            {/* Manual revenue */}
                            {selectedArrangement.manual_revenue?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Handmatig toegevoegd</p>
                                {selectedArrangement.manual_revenue.map(rev => (
                                  <div key={rev.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center">
                                        <Euro className="w-4 h-4 text-emerald-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{rev.description}</p>
                                        <p className="text-xs text-[#57534E]">{rev.category} • {rev.date} {rev.guests > 0 && `• ${rev.guests} gasten`}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-emerald-700">{formatCurrency(rev.amount)}</span>
                                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteArrManualRevenue(rev.id)}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Linked revenue */}
                            {selectedArrangement.linked_revenue_details?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide">Gekoppeld</p>
                                {selectedArrangement.linked_revenue_details.map(rev => (
                                  <div key={rev.id} className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center">
                                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{rev.date}</p>
                                        <p className="text-xs text-[#57534E]">{rev.channel} • {rev.guests} gasten</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-emerald-700">{formatCurrency(rev.amount)}</span>
                                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleUnlinkRevenueFromArrangement(rev.id)}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {(!selectedArrangement.manual_revenue?.length && !selectedArrangement.linked_revenue_details?.length) && (
                              <p className="text-sm text-[#57534E] text-center py-4">Geen omzet toegevoegd</p>
                            )}
                          </div>
                        </>
                      )}

                      {/* Description */}
                      {selectedArrangement.description && (
                        <div className="p-4 bg-stone-50 rounded-lg">
                          <h4 className="font-semibold text-[#1C1917] mb-2">Beschrijving</h4>
                          <p className="text-sm text-[#57534E]">{selectedArrangement.description}</p>
                        </div>
                      )}

                      {/* Book Button for templates */}
                      {selectedArrangement.is_template && (
                        <Button className="w-full btn-primary" onClick={() => { setArrangementToBook(selectedArrangement); setBookDialogOpen(true); setArrangementDetailOpen(false); }}>
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Dit arrangement boeken
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Book Arrangement Dialog */}
          <Dialog open={bookDialogOpen} onOpenChange={setBookDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Arrangement boeken: {arrangementToBook?.name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleBookArrangement} className="space-y-4 pt-4">
                <div className="p-3 bg-[#C2410C]/10 rounded-lg text-center">
                  <p className="font-bold text-[#C2410C]">
                    {arrangementToBook?.price_type === 'per_persoon' 
                      ? `${formatCurrency(arrangementToBook?.price_per_person)} per persoon` 
                      : formatCurrency(arrangementToBook?.fixed_price)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Naam klant *</Label>
                    <Input
                      value={bookingForm.customer_name}
                      onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })}
                      placeholder="Jan Jansen"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>E-mail</Label>
                    <Input
                      type="email"
                      value={bookingForm.customer_email}
                      onChange={(e) => setBookingForm({ ...bookingForm, customer_email: e.target.value })}
                      placeholder="jan@email.nl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefoon</Label>
                    <Input
                      value={bookingForm.customer_phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, customer_phone: e.target.value })}
                      placeholder="06-12345678"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Bedrijf</Label>
                    <Input
                      value={bookingForm.customer_company}
                      onChange={(e) => setBookingForm({ ...bookingForm, customer_company: e.target.value })}
                      placeholder="ABC BV"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Datum *</Label>
                    <Input
                      type="date"
                      value={bookingForm.booking_date}
                      onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tijd</Label>
                    <Input
                      type="time"
                      value={bookingForm.booking_time}
                      onChange={(e) => setBookingForm({ ...bookingForm, booking_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aantal personen</Label>
                    <Input
                      type="number"
                      value={bookingForm.actual_guests}
                      onChange={(e) => setBookingForm({ ...bookingForm, actual_guests: e.target.value })}
                      placeholder={arrangementToBook?.min_guests?.toString() || "10"}
                    />
                  </div>
                </div>

                {/* Automatische berekening totalen */}
                {bookingForm.actual_guests && (
                  <div className="grid grid-cols-3 gap-4 p-4 bg-stone-50 rounded-lg border">
                    <div className="text-center">
                      <p className="text-sm text-[#57534E]">Totaal Omzet</p>
                      <p className="text-xl font-bold text-emerald-600">
                        {formatCurrency(calculateBookingTotals().revenue)}
                      </p>
                      {arrangementToBook?.price_type === 'per_persoon' && (
                        <p className="text-xs text-[#57534E]">{bookingForm.actual_guests} × {formatCurrency(arrangementToBook?.price_per_person || 0)}</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#57534E]">Totaal Kosten</p>
                      <p className="text-xl font-bold text-rose-600">
                        {formatCurrency(calculateBookingTotals().costs)}
                      </p>
                      {arrangementToBook?.price_type === 'per_persoon' && arrangementToBook?.cost_per_person > 0 && (
                        <p className="text-xs text-[#57534E]">{bookingForm.actual_guests} × {formatCurrency(arrangementToBook?.cost_per_person || 0)}</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#57534E]">Winst</p>
                      <p className={`text-xl font-bold ${calculateBookingTotals().profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(calculateBookingTotals().profit)}
                      </p>
                      {calculateBookingTotals().revenue > 0 && (
                        <p className="text-xs text-[#57534E]">
                          {((calculateBookingTotals().profit / calculateBookingTotals().revenue) * 100).toFixed(0)}% marge
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Aanbetaling (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={bookingForm.deposit_paid}
                    onChange={(e) => setBookingForm({ ...bookingForm, deposit_paid: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Opmerkingen</Label>
                  <Textarea
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    placeholder="Speciale wensen, dieetwensen, etc."
                    rows={2}
                  />
                </div>

                <Button type="submit" className="w-full btn-primary">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Boeken
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Link Cost to Arrangement Dialog */}
          <Dialog open={linkCostArrDialogOpen} onOpenChange={setLinkCostArrDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kosten koppelen aan arrangement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
                {availableCosts.length === 0 ? (
                  <p className="text-center text-[#57534E] py-8">Geen facturen of bestellingen beschikbaar</p>
                ) : (
                  availableCosts.filter(c => !selectedArrangement?.linked_costs?.includes(c.id)).map(cost => (
                    <div key={cost.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer" onClick={() => { handleLinkCostToArrangement(cost.id); setLinkCostArrDialogOpen(false); }}>
                      <div>
                        <p className="font-medium">{cost.displayName}</p>
                        <p className="text-xs text-[#57534E]">{cost.costType} • {cost.invoice_date || cost.order_date}</p>
                      </div>
                      <Plus className="w-4 h-4 text-[#C2410C]" />
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Link Revenue to Arrangement Dialog */}
          <Dialog open={linkRevenueArrDialogOpen} onOpenChange={setLinkRevenueArrDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Omzet koppelen aan arrangement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
                {availableRevenue.length === 0 ? (
                  <p className="text-center text-[#57534E] py-8">Geen omzet entries beschikbaar</p>
                ) : (
                  availableRevenue.filter(r => !selectedArrangement?.linked_revenue?.includes(r.id)).map(rev => (
                    <div key={rev.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer" onClick={() => { handleLinkRevenueToArrangement(rev.id); setLinkRevenueArrDialogOpen(false); }}>
                      <div>
                        <p className="font-medium">{formatCurrency(rev.amount)}</p>
                        <p className="text-xs text-[#57534E]">{rev.date} • {rev.channel} • {rev.guests} gasten</p>
                      </div>
                      <Plus className="w-4 h-4 text-[#C2410C]" />
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual Cost Entry Dialog for Arrangements */}
          <Dialog open={arrManualCostDialogOpen} onOpenChange={setArrManualCostDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-rose-600" />
                  Kosten toevoegen aan "{selectedArrangement?.name}"
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddArrManualCost} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Omschrijving *</Label>
                  <Input
                    value={arrManualCostForm.description}
                    onChange={(e) => setArrManualCostForm({ ...arrManualCostForm, description: e.target.value })}
                    placeholder="Bijv. Ingrediënten, Decoratie, Personeel..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bedrag (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={arrManualCostForm.amount}
                      onChange={(e) => setArrManualCostForm({ ...arrManualCostForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select value={arrManualCostForm.category} onValueChange={(v) => setArrManualCostForm({ ...arrManualCostForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="food">Food</SelectItem>
                        <SelectItem value="beverage">Dranken</SelectItem>
                        <SelectItem value="personeel">Personeel</SelectItem>
                        <SelectItem value="materiaal">Materiaal</SelectItem>
                        <SelectItem value="locatie">Locatie</SelectItem>
                        <SelectItem value="overig">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={arrManualCostForm.date}
                    onChange={(e) => setArrManualCostForm({ ...arrManualCostForm, date: e.target.value })}
                  />
                </div>
                <p className="text-xs text-[#57534E] bg-amber-50 p-3 rounded-lg">
                  💡 Deze kosten worden automatisch meegenomen in de <strong>Kosten & Marges</strong> module en zijn daar traceerbaar naar dit arrangement.
                </p>
                <Button type="submit" className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Kosten Toevoegen
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Manual Revenue Entry Dialog for Arrangements */}
          <Dialog open={arrManualRevenueDialogOpen} onOpenChange={setArrManualRevenueDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Omzet toevoegen aan "{selectedArrangement?.name}"
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddArrManualRevenue} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Omschrijving *</Label>
                  <Input
                    value={arrManualRevenueForm.description}
                    onChange={(e) => setArrManualRevenueForm({ ...arrManualRevenueForm, description: e.target.value })}
                    placeholder="Bijv. Boeking, Extra consumpties, Aanvulling..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bedrag (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={arrManualRevenueForm.amount}
                      onChange={(e) => setArrManualRevenueForm({ ...arrManualRevenueForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select value={arrManualRevenueForm.category} onValueChange={(v) => setArrManualRevenueForm({ ...arrManualRevenueForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boeking">Boeking</SelectItem>
                        <SelectItem value="extra">Extra's</SelectItem>
                        <SelectItem value="overig">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={arrManualRevenueForm.date}
                      onChange={(e) => setArrManualRevenueForm({ ...arrManualRevenueForm, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aantal gasten</Label>
                    <Input
                      type="number"
                      value={arrManualRevenueForm.guests}
                      onChange={(e) => setArrManualRevenueForm({ ...arrManualRevenueForm, guests: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#57534E] bg-emerald-50 p-3 rounded-lg">
                  💡 Deze omzet wordt automatisch meegenomen in de <strong>Omzet & Verkoop</strong> module en is daar traceerbaar naar dit arrangement.
                </p>
                <Button type="submit" className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Omzet Toevoegen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="mt-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Kanaal Analyse</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.channelStats?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stats.channelStats.map((ch, idx) => {
                    const Icon = channelIcons[ch.channel] || Globe;
                    const channelCampaigns = campaigns.filter(c => c.channel === ch.channel);
                    return (
                      <div 
                        key={ch.channel} 
                        className="p-5 bg-stone-50 rounded-xl border border-stone-200 hover:border-[#C2410C] hover:shadow-md transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedChannelDetail({ ...ch, campaigns: channelCampaigns });
                          setChannelDetailOpen(true);
                        }}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS[idx % COLORS.length] + '20' }}>
                            <Icon className="w-5 h-5" style={{ color: COLORS[idx % COLORS.length] }} />
                          </div>
                          <div>
                            <h4 className="font-semibold">{channelLabels[ch.channel] || ch.channel}</h4>
                            <p className="text-xs text-[#57534E]">{ch.campaigns} campagne(s)</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-[#78716C] ml-auto" />
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-[#57534E]">Omzet</span>
                            <span className="font-bold">{formatCurrency(ch.revenue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-[#57534E]">Kosten</span>
                            <span>{formatCurrency(ch.cost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-[#57534E]">ROI</span>
                            <span className={ch.roi >= 0 ? 'text-emerald-600 font-bold' : 'text-rose-600 font-bold'}>
                              {ch.roi >= 0 ? '+' : ''}{ch.roi}%
                            </span>
                          </div>
                          {ch.reach > 0 && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-sm text-[#57534E]">Bereik</span>
                                <span>{ch.reach.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-[#57534E]">Conversie</span>
                                <span>{ch.conversionRate}%</span>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="mt-4 pt-3 border-t border-stone-200">
                          <p className="text-xs text-[#C2410C] font-medium">Klik voor details →</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-8 text-[#57534E]">
                  Nog geen kanaal data. Voeg campagnes toe met een kanaal om analyses te zien.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channel Detail Dialog */}
        <Dialog open={channelDetailOpen} onOpenChange={setChannelDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedChannelDetail && (
                  <>
                    {(() => {
                      const Icon = channelIcons[selectedChannelDetail.channel] || Globe;
                      return <Icon className="w-6 h-6 text-[#C2410C]" />;
                    })()}
                    <span>{channelLabels[selectedChannelDetail?.channel] || selectedChannelDetail?.channel} - Overzicht</span>
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedChannelDetail && (
              <div className="space-y-6 pt-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(selectedChannelDetail.revenue)}</p>
                    <p className="text-xs text-emerald-600">Totale Omzet</p>
                  </div>
                  <div className="p-4 bg-rose-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-rose-700">{formatCurrency(selectedChannelDetail.cost)}</p>
                    <p className="text-xs text-rose-600">Totale Kosten</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className={`text-2xl font-bold ${selectedChannelDetail.roi >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>
                      {selectedChannelDetail.roi >= 0 ? '+' : ''}{selectedChannelDetail.roi}%
                    </p>
                    <p className="text-xs text-blue-600">ROI</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{selectedChannelDetail.campaigns?.length || 0}</p>
                    <p className="text-xs text-amber-600">Campagnes</p>
                  </div>
                </div>

                {/* Campaigns List */}
                <div>
                  <h4 className="font-semibold text-[#1C1917] mb-3">Campagnes via dit kanaal</h4>
                  {selectedChannelDetail.campaigns?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedChannelDetail.campaigns.map(campaign => {
                        const campaignRoi = campaign.cost > 0 ? ((campaign.revenue_generated - campaign.cost) / campaign.cost) * 100 : 0;
                        return (
                          <div key={campaign.id} className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h5 className="font-semibold text-[#1C1917]">{campaign.name}</h5>
                                <p className="text-xs text-[#78716C]">
                                  {campaign.start_date} {campaign.end_date && `→ ${campaign.end_date}`}
                                </p>
                              </div>
                              <Badge className={
                                campaign.status === 'actief' ? 'bg-emerald-100 text-emerald-700' :
                                campaign.status === 'concept' ? 'bg-stone-100 text-stone-700' :
                                campaign.status === 'afgelopen' ? 'bg-blue-100 text-blue-700' :
                                'bg-rose-100 text-rose-700'
                              }>{campaign.status}</Badge>
                            </div>
                            {campaign.description && (
                              <p className="text-sm text-[#57534E] mb-3">{campaign.description}</p>
                            )}
                            <div className="grid grid-cols-3 gap-4 pt-2 border-t border-stone-200">
                              <div>
                                <p className="text-xs text-[#78716C]">Kosten</p>
                                <p className="font-semibold text-rose-600">{formatCurrency(campaign.cost)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#78716C]">Omzet</p>
                                <p className="font-semibold text-emerald-600">{formatCurrency(campaign.revenue_generated)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#78716C]">ROI</p>
                                <p className={`font-semibold ${campaignRoi >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                                  {campaignRoi >= 0 ? '+' : ''}{campaignRoi.toFixed(0)}%
                                </p>
                              </div>
                            </div>
                            {(campaign.discount_percentage || campaign.discount_code) && (
                              <div className="mt-3 flex gap-2">
                                {campaign.discount_percentage && (
                                  <Badge variant="outline" className="text-[#C2410C]">
                                    <Percent className="w-3 h-3 mr-1" />
                                    {campaign.discount_percentage}% korting
                                  </Badge>
                                )}
                                {campaign.discount_code && (
                                  <Badge variant="outline">
                                    Code: {campaign.discount_code}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center py-6 text-[#78716C] bg-stone-50 rounded-lg">
                      Geen campagnes gevonden voor dit kanaal
                    </p>
                  )}
                </div>

                {/* Close Button */}
                <Button variant="outline" className="w-full" onClick={() => setChannelDetailOpen(false)}>
                  Sluiten
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Campaign/Actie Detail Dialog */}
        <Dialog open={campaignDetailOpen} onOpenChange={setCampaignDetailOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedCampaign?.type === 'actie' ? (
                  <Zap className="w-6 h-6 text-purple-600" />
                ) : (
                  <Megaphone className="w-6 h-6 text-[#C2410C]" />
                )}
                <span>{selectedCampaign?.type === 'actie' ? 'Actie Details' : 'Campagne Details'}</span>
              </DialogTitle>
            </DialogHeader>
            {selectedCampaign && (
              <div className="space-y-5 pt-4">
                {/* Header */}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-[#1C1917]">{selectedCampaign.name}</h3>
                    <Badge className={
                      selectedCampaign.status === 'actief' ? 'bg-emerald-100 text-emerald-700' :
                      selectedCampaign.status === 'concept' ? 'bg-stone-100 text-stone-700' :
                      selectedCampaign.status === 'afgelopen' ? 'bg-blue-100 text-blue-700' :
                      'bg-rose-100 text-rose-700'
                    }>{selectedCampaign.status}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{typeLabels[selectedCampaign.type] || selectedCampaign.type}</Badge>
                    {selectedCampaign.channel && (
                      <Badge variant="outline">
                        {(() => {
                          const Icon = channelIcons[selectedCampaign.channel] || Globe;
                          return <Icon className="w-3 h-3 mr-1 inline" />;
                        })()}
                        {channelLabels[selectedCampaign.channel]}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedCampaign.description && (
                  <div className="p-4 bg-stone-50 rounded-lg">
                    <p className="text-sm text-[#57534E]">{selectedCampaign.description}</p>
                  </div>
                )}

                {/* Period */}
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-5 h-5 text-[#C2410C]" />
                  <span className="font-medium">{selectedCampaign.start_date}</span>
                  {selectedCampaign.end_date && (
                    <>
                      <span className="text-[#78716C]">→</span>
                      <span className="font-medium">{selectedCampaign.end_date}</span>
                    </>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-rose-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-rose-700">{formatCurrency(selectedCampaign.cost)}</p>
                    <p className="text-xs text-rose-600">Kosten</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(selectedCampaign.revenue_generated)}</p>
                    <p className="text-xs text-emerald-600">Omzet</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    {(() => {
                      const roi = selectedCampaign.cost > 0 ? ((selectedCampaign.revenue_generated - selectedCampaign.cost) / selectedCampaign.cost) * 100 : 0;
                      return (
                        <>
                          <p className={`text-xl font-bold ${roi >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>
                            {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                          </p>
                          <p className="text-xs text-blue-600">ROI</p>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Additional Info */}
                {(selectedCampaign.reach || selectedCampaign.conversions || selectedCampaign.target_audience) && (
                  <div className="p-4 bg-stone-50 rounded-lg space-y-2">
                    {selectedCampaign.reach > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-[#78716C]">Bereik</span>
                        <span className="font-medium">{selectedCampaign.reach.toLocaleString()}</span>
                      </div>
                    )}
                    {selectedCampaign.conversions > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-[#78716C]">Conversies</span>
                        <span className="font-medium">{selectedCampaign.conversions}</span>
                      </div>
                    )}
                    {selectedCampaign.target_audience && (
                      <div className="flex justify-between">
                        <span className="text-sm text-[#78716C]">Doelgroep</span>
                        <span className="font-medium">{selectedCampaign.target_audience}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Discount Info */}
                {(selectedCampaign.discount_percentage || selectedCampaign.discount_code) && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-amber-800 mb-2">Kortingsactie</h4>
                    <div className="flex flex-wrap gap-3">
                      {selectedCampaign.discount_percentage && (
                        <div className="flex items-center gap-2">
                          <Percent className="w-4 h-4 text-amber-600" />
                          <span className="font-bold text-amber-800">{selectedCampaign.discount_percentage}% korting</span>
                        </div>
                      )}
                      {selectedCampaign.discount_code && (
                        <div className="bg-white px-3 py-1 rounded border border-amber-300">
                          <span className="text-xs text-amber-600">Code:</span>
                          <span className="font-mono font-bold ml-2 text-amber-800">{selectedCampaign.discount_code}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2 border-t">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      const newStatus = selectedCampaign.status === 'actief' ? 'afgelopen' : 'actief';
                      api.put(`/campaigns/${selectedCampaign.id}`, { ...selectedCampaign, status: newStatus })
                        .then(() => {
                          toast.success(`Status gewijzigd naar ${newStatus}`);
                          fetchData();
                          setCampaignDetailOpen(false);
                        })
                        .catch(() => toast.error('Fout bij wijzigen status'));
                    }}
                  >
                    {selectedCampaign.status === 'actief' ? 'Markeer als Afgelopen' : 'Activeren'}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-rose-600"
                    onClick={() => {
                      handleDelete(selectedCampaign.id);
                      setCampaignDetailOpen(false);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Social Media Tab */}
        <TabsContent value="social" className="mt-6 space-y-6" data-testid="social-media-content">
          {/* URL Input Section */}
          <Card className="card-base border-2 border-dashed border-[#C2410C]/30 bg-gradient-to-br from-orange-50/50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#C2410C]/10 flex items-center justify-center">
                  <Search className="w-6 h-6 text-[#C2410C]" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                    Social Media Profiel Analyseren
                  </h3>
                  <p className="text-sm text-[#57534E]">
                    Plak je Instagram, Facebook, LinkedIn of TikTok profiel URL om posts en prestaties te analyseren
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#57534E]" />
                  <Input
                    value={socialUrl}
                    onChange={(e) => setSocialUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/in/jouw_profiel/"
                    className="pl-11"
                    data-testid="social-url-input"
                  />
                </div>
                <Button 
                  onClick={handleAnalyzeUrl} 
                  disabled={analyzingUrl || !socialUrl.trim()}
                  className="btn-primary min-w-[160px]"
                  data-testid="analyze-url-btn"
                >
                  {analyzingUrl ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyseren...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyseer Profiel
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Social Channels Grid */}
          {socialChannels.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {socialChannels.map(channel => {
                const Icon = channelIcons[channel.platform] || Globe;
                const isSelected = selectedChannel === channel.id;
                return (
                  <div 
                    key={channel.id}
                    onClick={() => setSelectedChannel(isSelected ? null : channel.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-[#C2410C] bg-[#C2410C]/5 shadow-lg' 
                        : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow'
                    }`}
                    data-testid={`channel-card-${channel.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-[#C2410C]' : 'text-[#57534E]'}`} />
                        <span className="font-semibold capitalize">{channel.platform}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-rose-500 h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); handleDeleteChannel(channel.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="font-bold text-[#1C1917]">@{channel.account_name}</p>
                    {channel.followers > 0 && (
                      <p className="text-sm text-[#57534E] mt-1">{channel.followers.toLocaleString()} volgers</p>
                    )}
                    {channel.account_url && (
                      <a 
                        href={channel.account_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#C2410C] hover:underline flex items-center gap-1 mt-2"
                      >
                        <ExternalLink className="w-3 h-3" /> Bekijk profiel
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Analytics Section - Only show when channel selected or has data */}
          {socialAnalytics && socialAnalytics.summary?.totalPosts > 0 && (
            <>
              {/* KPI Tiles */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="kpi-tile">
                  <p className="kpi-label">Totaal Posts</p>
                  <p className="kpi-value text-[#C2410C]">{socialAnalytics.summary.totalPosts}</p>
                  <p className="text-sm text-[#57534E]">geanalyseerd</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Totaal Bereik</p>
                  <p className="kpi-value">{(socialAnalytics.summary.totalReach || 0).toLocaleString()}</p>
                  <p className="text-sm text-[#57534E]">mensen bereikt</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Engagement Rate</p>
                  <p className="kpi-value text-emerald-600">{socialAnalytics.summary.avgEngagementRate || 0}%</p>
                  <p className="text-sm text-[#57534E]">gemiddeld</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Beste Tijd</p>
                  <p className="kpi-value text-[#1A2E05] text-xl">{socialAnalytics.summary.bestTimeToPost || '-'}</p>
                  <p className="text-sm text-[#57534E]">om te posten</p>
                </div>
                <div className="kpi-tile">
                  <p className="kpi-label">Beste Dag</p>
                  <p className="kpi-value text-[#D97706] text-xl">{socialAnalytics.summary.bestDayToPost || '-'}</p>
                  <p className="text-sm text-[#57534E]">om te posten</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Best Times Chart */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Clock className="w-5 h-5 text-[#C2410C]" />
                      Beste Tijden om te Posten
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {socialAnalytics.bestTimes?.length > 0 ? (
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={socialAnalytics.bestTimes}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="avgEngagement" fill="#C2410C" name="Gem. Engagement" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-[#57534E]">Nog geen tijdsdata</p>
                    )}
                  </CardContent>
                </Card>

                {/* Day Performance Chart */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Calendar className="w-5 h-5 text-[#C2410C]" />
                      Performance per Dag
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {socialAnalytics.dayStats?.length > 0 ? (
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={socialAnalytics.dayStats}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="avgEngagement" fill="#1A2E05" name="Gem. Engagement" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-center py-8 text-[#57534E]">Nog geen dag data</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Content Type Performance */}
              {socialAnalytics.contentTypeStats?.length > 0 && (
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Zap className="w-5 h-5 text-[#C2410C]" />
                      Content Type Prestaties
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {socialAnalytics.contentTypeStats.map((ct, idx) => (
                        <div key={ct.type} className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="capitalize bg-[#C2410C]/10 text-[#C2410C]">{ct.type}</Badge>
                            <span className="text-xs text-[#57534E]">{ct.posts} posts</span>
                          </div>
                          <div className="space-y-2 mt-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-[#57534E]">Gem. Engagement</span>
                              <span className="font-bold">{ct.avgEngagement}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-[#57534E]">Gem. Bereik</span>
                              <span className="font-medium">{ct.avgReach.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-[#57534E]">Totaal Likes</span>
                              <span className="font-medium text-rose-500">{ct.totalLikes.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Best Performing Posts */}
              {socialAnalytics.bestPosts?.length > 0 && (
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Best Presterende Posts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {socialAnalytics.bestPosts.slice(0, 5).map((post, idx) => (
                        <div key={post.id} className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                            #{idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-[#1C1917] line-clamp-1">
                              {post.description || 'Geen beschrijving'}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-[#57534E]">
                              <span>{post.post_date}</span>
                              <span className="capitalize">{post.content_type}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-1 text-rose-500">
                              <Heart className="w-4 h-4" />
                              <span>{post.likes?.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-1 text-blue-500">
                              <MessageCircle className="w-4 h-4" />
                              <span>{post.comments?.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-1 text-green-500">
                              <Share2 className="w-4 h-4" />
                              <span>{post.shares?.toLocaleString()}</span>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {post.engagement_rate}% engagement
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Posts Table with Add Button */}
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>
                {selectedChannel ? 'Posts van geselecteerd kanaal' : 'Alle Posts'}
              </CardTitle>
              <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" disabled={!selectedChannel} data-testid="add-post-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Post Toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe Post Toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPost} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input 
                          type="date" 
                          value={postForm.post_date}
                          onChange={(e) => setPostForm({...postForm, post_date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tijd</Label>
                        <Input 
                          type="time" 
                          value={postForm.post_time}
                          onChange={(e) => setPostForm({...postForm, post_time: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Type Content</Label>
                      <Select value={postForm.content_type} onValueChange={(v) => setPostForm({...postForm, content_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="image">Afbeelding</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="reel">Reel</SelectItem>
                          <SelectItem value="carousel">Carousel</SelectItem>
                          <SelectItem value="story">Story</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Beschrijving</Label>
                      <Textarea 
                        value={postForm.description}
                        onChange={(e) => setPostForm({...postForm, description: e.target.value})}
                        placeholder="Korte beschrijving van de post"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Likes</Label>
                        <Input 
                          type="number" 
                          value={postForm.likes}
                          onChange={(e) => setPostForm({...postForm, likes: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Comments</Label>
                        <Input 
                          type="number" 
                          value={postForm.comments}
                          onChange={(e) => setPostForm({...postForm, comments: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Shares</Label>
                        <Input 
                          type="number" 
                          value={postForm.shares}
                          onChange={(e) => setPostForm({...postForm, shares: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bereik</Label>
                        <Input 
                          type="number" 
                          value={postForm.reach}
                          onChange={(e) => setPostForm({...postForm, reach: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-primary">Post Opslaan</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {socialPosts.length === 0 ? (
                <div className="text-center py-8">
                  <Instagram className="w-12 h-12 mx-auto text-[#57534E]/30 mb-4" />
                  {socialChannels.length === 0 ? (
                    <p className="text-[#57534E]">
                      Voeg een social media profiel toe via de URL hierboven om te beginnen.
                    </p>
                  ) : selectedChannel ? (
                    <div className="space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-lg mx-auto">
                        <p className="text-amber-800 text-sm mb-2">
                          <strong>Let op:</strong> Instagram, Facebook en TikTok blokkeren automatisch scrapen van posts.
                        </p>
                        <p className="text-amber-700 text-sm">
                          Je kunt posts handmatig toevoegen via de "Post Toevoegen" knop, of exporteer je Instagram Insights en voer de data hier in.
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-3">
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            const channel = socialChannels.find(c => c.id === selectedChannel);
                            if (channel?.account_url) {
                              setSocialUrl(channel.account_url);
                              await handleAnalyzeUrl();
                            }
                          }}
                          disabled={analyzingUrl}
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${analyzingUrl ? 'animate-spin' : ''}`} />
                          Opnieuw analyseren
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button className="btn-primary">
                              <Plus className="w-4 h-4 mr-2" />
                              Post Toevoegen
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Post handmatig toevoegen</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddPost} className="space-y-4 pt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Datum</Label>
                                  <Input 
                                    type="date" 
                                    value={postForm.post_date}
                                    onChange={(e) => setPostForm({...postForm, post_date: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Tijd</Label>
                                  <Input 
                                    type="time" 
                                    value={postForm.post_time}
                                    onChange={(e) => setPostForm({...postForm, post_time: e.target.value})}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>Type content</Label>
                                <Select value={postForm.content_type} onValueChange={(v) => setPostForm({...postForm, content_type: v})}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="image">Foto</SelectItem>
                                    <SelectItem value="video">Video</SelectItem>
                                    <SelectItem value="reel">Reel</SelectItem>
                                    <SelectItem value="carousel">Carousel</SelectItem>
                                    <SelectItem value="story">Story</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Beschrijving</Label>
                                <Textarea 
                                  value={postForm.description}
                                  onChange={(e) => setPostForm({...postForm, description: e.target.value})}
                                  placeholder="Korte beschrijving van de post"
                                  rows={2}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Likes</Label>
                                  <Input 
                                    type="number" 
                                    value={postForm.likes}
                                    onChange={(e) => setPostForm({...postForm, likes: e.target.value})}
                                    placeholder="0"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Comments</Label>
                                  <Input 
                                    type="number" 
                                    value={postForm.comments}
                                    onChange={(e) => setPostForm({...postForm, comments: e.target.value})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Bereik</Label>
                                  <Input 
                                    type="number" 
                                    value={postForm.reach}
                                    onChange={(e) => setPostForm({...postForm, reach: e.target.value})}
                                    placeholder="0"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Shares</Label>
                                  <Input 
                                    type="number" 
                                    value={postForm.shares}
                                    onChange={(e) => setPostForm({...postForm, shares: e.target.value})}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              <Button type="submit" className="w-full btn-primary">Post Opslaan</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#57534E]">
                      Selecteer een kanaal om posts te bekijken of voeg een nieuw profiel toe.
                    </p>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Datum</TableHead>
                      <TableHead>Tijd</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Beschrijving</TableHead>
                      <TableHead>Likes</TableHead>
                      <TableHead>Comments</TableHead>
                      <TableHead>Bereik</TableHead>
                      <TableHead>Engagement</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {socialPosts.sort((a, b) => new Date(b.post_date) - new Date(a.post_date)).map(post => (
                      <TableRow key={post.id} className="table-row">
                        <TableCell>{post.post_date}</TableCell>
                        <TableCell>{post.post_time}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{post.content_type}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{post.description || '-'}</TableCell>
                        <TableCell className="text-rose-500 font-medium">{post.likes?.toLocaleString()}</TableCell>
                        <TableCell className="text-blue-500 font-medium">{post.comments?.toLocaleString()}</TableCell>
                        <TableCell>{post.reach?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={post.engagement_rate >= 3 ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-700'}>
                            {post.engagement_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeletePost(post.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* No Channels Empty State */}
          {socialChannels.length === 0 && (
            <Card className="card-base">
              <CardContent className="py-16 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#C2410C]/10 flex items-center justify-center mb-6">
                  <Globe className="w-10 h-10 text-[#C2410C]" />
                </div>
                <h3 className="text-xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Manrope' }}>
                  Verbind je Social Media
                </h3>
                <p className="text-[#57534E] max-w-md mx-auto mb-6">
                  Plak de URL van je Instagram, Facebook, LinkedIn of TikTok profiel hierboven. 
                  We analyseren automatisch je posts en tonen welke content het beste presteert.
                </p>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 text-white">
                    <Instagram className="w-6 h-6" />
                  </div>
                  <div className="p-3 rounded-lg bg-blue-600 text-white">
                    <Facebook className="w-6 h-6" />
                  </div>
                  <div className="p-3 rounded-lg bg-[#0077B5] text-white">
                    <Linkedin className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-6 text-sm text-[#57534E]">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    <span>Beste tijden ontdekken</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <span>Top posts analyseren</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    <span>Engagement vergelijken</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ==================== SOCIAL MEDIA STRATEGY SECTION ==================== */}
          <Card className="card-base border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Social Media Strategie</CardTitle>
                    <p className="text-sm text-[#57534E]">Definieer je content pijlers, tone of voice en posting schema</p>
                  </div>
                </div>
                <Button 
                  onClick={handleGenerateContentIdeas}
                  disabled={generatingIdeas}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="generate-ideas-btn"
                >
                  {generatingIdeas ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Genereren...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />AI Content Ideeën</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Strategy Navigation Tabs */}
              <div className="flex flex-wrap gap-2 border-b pb-4">
                {[
                  { key: 'pillars', label: 'Content Pijlers', icon: Palette },
                  { key: 'audience', label: 'Doelgroep', icon: Users },
                  { key: 'tone', label: 'Tone of Voice', icon: MessageSquare },
                  { key: 'schedule', label: 'Posting Schema', icon: Calendar },
                  { key: 'hashtags', label: 'Hashtags', icon: Hash },
                  { key: 'kpis', label: 'KPI Doelen', icon: Target },
                  { key: 'ideas', label: 'AI Ideeën', icon: Lightbulb }
                ].map(tab => (
                  <Button
                    key={tab.key}
                    variant={activeStrategySection === tab.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveStrategySection(tab.key)}
                    className={activeStrategySection === tab.key ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    <tab.icon className="w-4 h-4 mr-1" />
                    {tab.label}
                  </Button>
                ))}
              </div>

              {/* Content Pillars Section */}
              {activeStrategySection === 'pillars' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">Content Pijlers & Thema's</h3>
                      <p className="text-sm text-[#57534E]">Definieer 4-6 thema's waarover je posts gaat maken</p>
                    </div>
                  </div>
                  
                  {/* Add New Pillar */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nieuwe content pijler (bijv. Behind the scenes, Menu highlights)"
                      value={newPillarName}
                      onChange={(e) => setNewPillarName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddContentPillar()}
                      className="flex-1"
                    />
                    <Button onClick={handleAddContentPillar} className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-1" />Toevoegen
                    </Button>
                  </div>

                  {/* Pillars Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(socialStrategy?.content_pillars || []).map((pillar, index) => (
                      <div 
                        key={pillar.id} 
                        className="p-4 rounded-xl border-2 bg-white"
                        style={{ borderColor: pillar.color }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: pillar.color }}
                            />
                            <span className="font-semibold">{pillar.name}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-rose-500 h-6 w-6 p-0"
                            onClick={() => handleDeletePillar(pillar.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <Textarea
                          placeholder="Beschrijving van deze pijler..."
                          value={pillar.description}
                          onChange={(e) => handleUpdatePillar(pillar.id, { description: e.target.value })}
                          className="mb-3 text-sm"
                          rows={2}
                        />
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#57534E]">Percentage:</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={pillar.percentage}
                            onChange={(e) => handleUpdatePillar(pillar.id, { percentage: parseInt(e.target.value) || 0 })}
                            className="w-20"
                          />
                          <span className="text-sm text-[#57534E]">%</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pillar Distribution Chart */}
                  {(socialStrategy?.content_pillars || []).length > 0 && (
                    <div className="p-4 bg-stone-50 rounded-xl">
                      <h4 className="font-semibold mb-3">Content Verdeling</h4>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={(socialStrategy?.content_pillars || []).filter(p => p.percentage > 0)}
                              dataKey="percentage"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              label={({ name, percentage }) => `${name}: ${percentage}%`}
                            >
                              {(socialStrategy?.content_pillars || []).map((pillar, index) => (
                                <Cell key={index} fill={pillar.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {(socialStrategy?.content_pillars || []).reduce((sum, p) => sum + (p.percentage || 0), 0) !== 100 && (
                        <p className="text-amber-600 text-sm mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Totaal: {(socialStrategy?.content_pillars || []).reduce((sum, p) => sum + (p.percentage || 0), 0)}% (moet 100% zijn)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Target Audience Section */}
              {activeStrategySection === 'audience' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1C1917]">Doelgroep Definitie</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Doelgroep beschrijving</Label>
                      <Textarea
                        placeholder="Wie wil je bereiken? (bijv. Jonge professionals uit de stad, foodie's, stamgasten...)"
                        value={socialStrategy?.target_audience || ''}
                        onChange={(e) => handleSaveStrategy({ target_audience: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Leeftijdsgroep</Label>
                      <Select 
                        value={socialStrategy?.audience_age_range || ''}
                        onValueChange={(v) => handleSaveStrategy({ audience_age_range: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecteer leeftijdsgroep" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="18-24">18-24 jaar</SelectItem>
                          <SelectItem value="25-34">25-34 jaar</SelectItem>
                          <SelectItem value="35-44">35-44 jaar</SelectItem>
                          <SelectItem value="45-54">45-54 jaar</SelectItem>
                          <SelectItem value="55+">55+ jaar</SelectItem>
                          <SelectItem value="25-45">25-45 jaar (breed)</SelectItem>
                          <SelectItem value="all">Alle leeftijden</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Locatie</Label>
                      <Input
                        placeholder="bijv. Amsterdam en omgeving, Nederland"
                        value={socialStrategy?.audience_location || ''}
                        onChange={(e) => handleSaveStrategy({ audience_location: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Interesses (komma gescheiden)</Label>
                      <Input
                        placeholder="bijv. food, wijn, uitgaan, lifestyle"
                        value={(socialStrategy?.audience_interests || []).join(', ')}
                        onChange={(e) => handleSaveStrategy({ audience_interests: e.target.value.split(',').map(i => i.trim()).filter(Boolean) })}
                      />
                    </div>
                  </div>
                  
                  {/* Preview Card */}
                  {socialStrategy?.target_audience && (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Doelgroep Samenvatting
                      </h4>
                      <p className="text-blue-700">{socialStrategy?.target_audience}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {socialStrategy?.audience_age_range && (
                          <Badge className="bg-blue-100 text-blue-700">{socialStrategy.audience_age_range}</Badge>
                        )}
                        {socialStrategy?.audience_location && (
                          <Badge className="bg-blue-100 text-blue-700">{socialStrategy.audience_location}</Badge>
                        )}
                        {(socialStrategy?.audience_interests || []).map((interest, i) => (
                          <Badge key={i} variant="outline" className="text-blue-600">{interest}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tone of Voice Section */}
              {activeStrategySection === 'tone' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1C1917]">Tone of Voice & Merkpersoonlijkheid</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Merkpersoonlijkheid</Label>
                      <Textarea
                        placeholder="Hoe zou je je merk als persoon beschrijven? (bijv. vriendelijk, gastvrij, authentiek, avontuurlijk)"
                        value={socialStrategy?.brand_personality || ''}
                        onChange={(e) => handleSaveStrategy({ brand_personality: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tone of Voice</Label>
                      <Select 
                        value={socialStrategy?.tone_of_voice || ''}
                        onValueChange={(v) => handleSaveStrategy({ tone_of_voice: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Kies je communicatiestijl" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professioneel">Professioneel & zakelijk</SelectItem>
                          <SelectItem value="casual">Casual & vriendelijk</SelectItem>
                          <SelectItem value="humoristisch">Humoristisch & speels</SelectItem>
                          <SelectItem value="inspirerend">Inspirerend & motiverend</SelectItem>
                          <SelectItem value="luxe">Luxe & exclusief</SelectItem>
                          <SelectItem value="warm">Warm & persoonlijk</SelectItem>
                          <SelectItem value="informatief">Informatief & educatief</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Do's and Don'ts */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Do's - Wel doen
                      </h4>
                      <Textarea
                        placeholder="Wat mag wel in je communicatie? (per regel)"
                        value={(socialStrategy?.communication_dos || []).join('\n')}
                        onChange={(e) => handleSaveStrategy({ communication_dos: e.target.value.split('\n').filter(Boolean) })}
                        rows={5}
                        className="bg-white"
                      />
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(socialStrategy?.communication_dos || []).map((item, i) => (
                          <Badge key={i} className="bg-emerald-100 text-emerald-700">{item}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                      <h4 className="font-semibold text-rose-800 mb-3 flex items-center gap-2">
                        <X className="w-4 h-4" />
                        Don'ts - Niet doen
                      </h4>
                      <Textarea
                        placeholder="Wat mag niet in je communicatie? (per regel)"
                        value={(socialStrategy?.communication_donts || []).join('\n')}
                        onChange={(e) => handleSaveStrategy({ communication_donts: e.target.value.split('\n').filter(Boolean) })}
                        rows={5}
                        className="bg-white"
                      />
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(socialStrategy?.communication_donts || []).map((item, i) => (
                          <Badge key={i} className="bg-rose-100 text-rose-700">{item}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Posting Schedule Section */}
              {activeStrategySection === 'schedule' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1C1917]">Posting Schema per Platform</h3>
                  <p className="text-sm text-[#57534E]">Stel in hoe vaak je per platform wilt posten</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['instagram', 'facebook', 'linkedin', 'tiktok'].map(platform => {
                      const schedule = (socialStrategy?.posting_schedules || []).find(s => s.platform === platform) || { posts_per_week: 3, best_times: [], best_days: [] };
                      const Icon = channelIcons[platform] || Globe;
                      return (
                        <div key={platform} className="p-4 rounded-xl border bg-white">
                          <div className="flex items-center gap-2 mb-3">
                            <Icon className="w-5 h-5 text-[#C2410C]" />
                            <span className="font-semibold capitalize">{platform}</span>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm">Posts per week</Label>
                              <Select 
                                value={schedule.posts_per_week?.toString() || '3'}
                                onValueChange={(v) => handleUpdatePostingSchedule(platform, { posts_per_week: parseInt(v) })}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[1,2,3,4,5,6,7,10,14].map(n => (
                                    <SelectItem key={n} value={n.toString()}>{n}x per week</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm">Beste tijden</Label>
                              <Input
                                placeholder="bijv. 09:00, 12:00, 18:00"
                                value={(schedule.best_times || []).join(', ')}
                                onChange={(e) => handleUpdatePostingSchedule(platform, { best_times: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Beste dagen</Label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day, i) => (
                                  <Button
                                    key={i}
                                    size="sm"
                                    variant={(schedule.best_days || []).includes(i) ? 'default' : 'outline'}
                                    className={`w-10 h-8 ${(schedule.best_days || []).includes(i) ? 'bg-purple-600' : ''}`}
                                    onClick={() => {
                                      const current = schedule.best_days || [];
                                      const updated = current.includes(i) 
                                        ? current.filter(d => d !== i)
                                        : [...current, i];
                                      handleUpdatePostingSchedule(platform, { best_days: updated });
                                    }}
                                  >
                                    {day}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Best Times from Analytics */}
                  {socialAnalytics?.summary?.totalPosts > 0 && (
                    <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Aanbevolen op basis van je analytics
                      </h4>
                      <div className="flex flex-wrap gap-4 text-sm">
                        {socialAnalytics?.recommendations?.bestTimeToPost && (
                          <div>
                            <span className="text-purple-600">Beste tijd:</span>
                            <span className="font-semibold ml-1">{socialAnalytics.recommendations.bestTimeToPost}:00</span>
                          </div>
                        )}
                        {socialAnalytics?.recommendations?.bestDayToPost !== undefined && (
                          <div>
                            <span className="text-purple-600">Beste dag:</span>
                            <span className="font-semibold ml-1">{['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'][socialAnalytics.recommendations.bestDayToPost]}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hashtag Strategy Section */}
              {activeStrategySection === 'hashtags' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">Hashtag Strategie</h3>
                      <p className="text-sm text-[#57534E]">Organiseer je hashtags in groepen voor makkelijk kopiëren</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddHashtagGroup('Vaste hashtags')}
                      >
                        <Plus className="w-4 h-4 mr-1" />Vaste
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddHashtagGroup('Seizoen/Actie')}
                      >
                        <Plus className="w-4 h-4 mr-1" />Seizoen
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddHashtagGroup('Lokaal')}
                      >
                        <Plus className="w-4 h-4 mr-1" />Lokaal
                      </Button>
                    </div>
                  </div>

                  {(socialStrategy?.hashtag_groups || []).length === 0 && (
                    <div className="p-8 text-center bg-stone-50 rounded-xl">
                      <Hash className="w-12 h-12 text-[#57534E] mx-auto mb-3" />
                      <p className="text-[#57534E]">Nog geen hashtag groepen. Klik op een knop hierboven om te starten.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(socialStrategy?.hashtag_groups || []).map(group => (
                      <div key={group.id} className="p-4 rounded-xl border bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold flex items-center gap-2">
                            <Hash className="w-4 h-4 text-purple-600" />
                            {group.name}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-purple-600"
                              onClick={() => {
                                navigator.clipboard.writeText(group.hashtags.join(' '));
                                toast.success('Hashtags gekopieerd!');
                              }}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-rose-500"
                              onClick={() => handleDeleteHashtagGroup(group.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Add hashtag input */}
                        <div className="flex gap-1 mb-3">
                          <Input
                            placeholder="#hashtag"
                            className="text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddHashtagToGroup(group.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                        
                        {/* Hashtag badges */}
                        <div className="flex flex-wrap gap-1">
                          {group.hashtags.map((hashtag, i) => (
                            <Badge 
                              key={i} 
                              variant="outline" 
                              className="text-purple-600 cursor-pointer hover:bg-rose-50 hover:text-rose-600"
                              onClick={() => handleRemoveHashtag(group.id, hashtag)}
                            >
                              {hashtag} <X className="w-3 h-3 ml-1" />
                            </Badge>
                          ))}
                        </div>
                        {group.hashtags.length === 0 && (
                          <p className="text-xs text-[#78716C]">Voeg hashtags toe met Enter</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KPI Goals Section */}
              {activeStrategySection === 'kpis' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1C1917]">Social Media KPI Doelen</h3>
                  <p className="text-sm text-[#57534E]">Stel meetbare doelen voor je social media groei</p>

                  {/* Add KPI Goal */}
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Select defaultValue="followers_growth" id="kpi-metric">
                        <SelectTrigger><SelectValue placeholder="KPI Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="followers_growth">Volgers groei</SelectItem>
                          <SelectItem value="engagement_rate">Engagement rate %</SelectItem>
                          <SelectItem value="reach">Bereik</SelectItem>
                          <SelectItem value="posts_count">Aantal posts</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" placeholder="Doelwaarde" id="kpi-value" />
                      <Select defaultValue="month" id="kpi-period">
                        <SelectTrigger><SelectValue placeholder="Periode" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="week">Per week</SelectItem>
                          <SelectItem value="month">Per maand</SelectItem>
                          <SelectItem value="quarter">Per kwartaal</SelectItem>
                          <SelectItem value="year">Per jaar</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          const metric = document.getElementById('kpi-metric')?.textContent || 'followers_growth';
                          const value = parseFloat(document.getElementById('kpi-value')?.value) || 0;
                          const period = document.getElementById('kpi-period')?.textContent || 'month';
                          if (value > 0) {
                            handleAddKPIGoal({ metric, target_value: value, period, platform: null });
                            document.getElementById('kpi-value').value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />Toevoegen
                      </Button>
                    </div>
                  </div>

                  {/* KPI Goals List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(socialStrategy?.kpi_goals || []).map((goal, index) => {
                      const metricLabels = {
                        followers_growth: 'Volgers groei',
                        engagement_rate: 'Engagement rate',
                        reach: 'Bereik',
                        posts_count: 'Aantal posts'
                      };
                      const periodLabels = {
                        week: 'per week',
                        month: 'per maand',
                        quarter: 'per kwartaal',
                        year: 'per jaar'
                      };
                      return (
                        <div key={index} className="p-4 rounded-xl border bg-white">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-[#1C1917]">{metricLabels[goal.metric] || goal.metric}</p>
                              <p className="text-2xl font-bold text-purple-600">
                                {goal.target_value.toLocaleString()}
                                {goal.metric === 'engagement_rate' && '%'}
                              </p>
                              <p className="text-sm text-[#57534E]">{periodLabels[goal.period] || goal.period}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-rose-500"
                              onClick={() => handleRemoveKPIGoal(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {(socialStrategy?.kpi_goals || []).length === 0 && (
                    <div className="p-8 text-center bg-stone-50 rounded-xl">
                      <Target className="w-12 h-12 text-[#57534E] mx-auto mb-3" />
                      <p className="text-[#57534E]">Nog geen KPI doelen ingesteld. Voeg hierboven je eerste doel toe.</p>
                    </div>
                  )}
                </div>
              )}

              {/* AI Content Ideas Section */}
              {activeStrategySection === 'ideas' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">AI Content Ideeën</h3>
                      <p className="text-sm text-[#57534E]">Gegenereerd op basis van je strategie, doelgroep en seizoen</p>
                    </div>
                    <Button 
                      onClick={handleGenerateContentIdeas}
                      disabled={generatingIdeas}
                      className="bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      {generatingIdeas ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Genereren...</>
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-2" />Nieuwe ideeën</>
                      )}
                    </Button>
                  </div>

                  {/* AI Tips */}
                  {aiTips.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        AI Tips
                      </h4>
                      <ul className="space-y-1">
                        {aiTips.map((tip, i) => (
                          <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                            <span className="text-amber-500">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Weekly Schedule */}
                  {weeklySchedule.length > 0 && (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Weekschema
                      </h4>
                      <div className="grid grid-cols-7 gap-2">
                        {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day, i) => {
                          const dayItems = weeklySchedule.filter(s => 
                            s.day?.toLowerCase() === ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'][i]
                          );
                          return (
                            <div key={i} className="text-center">
                              <p className="font-semibold text-blue-800 text-sm mb-1">{day}</p>
                              {dayItems.map((item, j) => (
                                <div key={j} className="text-xs bg-white rounded p-1 mb-1">
                                  <p className="font-medium capitalize">{item.platform}</p>
                                  <p className="text-blue-600">{item.content_type}</p>
                                  <p className="text-[#78716C]">{item.suggested_time}</p>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Content Ideas Grid */}
                  {contentIdeas.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {contentIdeas.map((idea, index) => (
                        <div 
                          key={index} 
                          className={`p-4 rounded-xl border-2 bg-white ${
                            idea.priority === 'hoog' ? 'border-rose-300' : 
                            idea.priority === 'normaal' ? 'border-blue-300' : 'border-stone-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-[#1C1917]">{idea.title}</h4>
                            <div className="flex gap-1">
                              <Badge variant="outline" className="capitalize text-xs">{idea.content_type}</Badge>
                              {idea.priority === 'hoog' && <Badge className="bg-rose-100 text-rose-700 text-xs">Hoog</Badge>}
                            </div>
                          </div>
                          <p className="text-sm text-[#57534E] mb-3">{idea.description}</p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <Badge variant="outline" className="capitalize">
                              {channelIcons[idea.platform] ? 
                                <span className="flex items-center gap-1">
                                  {React.createElement(channelIcons[idea.platform], { className: "w-3 h-3" })}
                                  {idea.platform}
                                </span> : idea.platform
                              }
                            </Badge>
                            {idea.pillar && <Badge className="bg-purple-100 text-purple-700">{idea.pillar}</Badge>}
                            {idea.best_time && <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />{idea.best_time}</Badge>}
                            {idea.best_day && <Badge variant="outline">{idea.best_day}</Badge>}
                          </div>
                          {idea.hashtags && idea.hashtags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {idea.hashtags.slice(0, 5).map((tag, i) => (
                                <span key={i} className="text-xs text-purple-600">{tag}</span>
                              ))}
                            </div>
                          )}
                          {idea.call_to_action && (
                            <p className="text-xs text-emerald-600 mt-2 italic">CTA: {idea.call_to_action}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-12 text-center bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                      <Wand2 className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-[#1C1917] mb-2">Genereer AI Content Ideeën</h4>
                      <p className="text-[#57534E] max-w-md mx-auto mb-4">
                        Vul eerst je strategie in (content pijlers, doelgroep, tone of voice) voor de beste resultaten.
                        De AI genereert dan relevante post ideeën en een weekschema.
                      </p>
                      <Button 
                        onClick={handleGenerateContentIdeas}
                        disabled={generatingIdeas}
                        className="bg-gradient-to-r from-purple-600 to-pink-600"
                      >
                        {generatingIdeas ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Genereren...</>
                        ) : (
                          <><Wand2 className="w-4 h-4 mr-2" />Genereer Ideeën</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-6 space-y-6" data-testid="events-content">
          {/* Events Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                Evenementen
              </h2>
              <p className="text-[#57534E] text-sm">
                Beheer je events en koppel kosten en omzet
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowEventAnalytics(!showEventAnalytics)}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {showEventAnalytics ? 'Verberg' : 'Toon'} Analytics
              </Button>
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" onClick={resetEventForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuw Event
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nieuw evenement aanmaken</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEvent} className="space-y-6 pt-4">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Naam evenement *</Label>
                      <Input
                        value={eventForm.name}
                        onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                        placeholder="bijv. Live Jazz Avond"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Type event</Label>
                        <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="feest">🎉 Feest/Party</SelectItem>
                            <SelectItem value="live_muziek">🎵 Live Muziek</SelectItem>
                            <SelectItem value="thema_avond">🌙 Thema Avond</SelectItem>
                            <SelectItem value="workshop">📚 Workshop</SelectItem>
                            <SelectItem value="proeverij">🍷 Proeverij</SelectItem>
                            <SelectItem value="seizoen">🎄 Seizoensevenement</SelectItem>
                            <SelectItem value="corporate">💼 Zakelijk/Corporate</SelectItem>
                            <SelectItem value="prive">🎂 Privé Feest</SelectItem>
                            <SelectItem value="overig">📋 Overig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={eventForm.status} onValueChange={(v) => setEventForm({ ...eventForm, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gepland">📅 Gepland</SelectItem>
                            <SelectItem value="bevestigd">✅ Bevestigd</SelectItem>
                            <SelectItem value="afgelopen">✔️ Afgelopen</SelectItem>
                            <SelectItem value="geannuleerd">❌ Geannuleerd</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Datum *</Label>
                        <Input
                          type="date"
                          value={eventForm.event_date}
                          onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Starttijd</Label>
                        <Input
                          type="time"
                          value={eventForm.start_time}
                          onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Eindtijd</Label>
                        <Input
                          type="time"
                          value={eventForm.end_time}
                          onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Locatie/Zaal</Label>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                          placeholder="bijv. Grote zaal, Terras"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Verwacht aantal gasten</Label>
                        <Input
                          type="number"
                          value={eventForm.expected_guests}
                          onChange={(e) => setEventForm({ ...eventForm, expected_guests: e.target.value })}
                          placeholder="50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Organizer Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1C1917] flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organisator gegevens
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Naam contactpersoon</Label>
                        <Input
                          value={eventForm.organizer_name}
                          onChange={(e) => setEventForm({ ...eventForm, organizer_name: e.target.value })}
                          placeholder="Jan Jansen"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bedrijf/Organisatie</Label>
                        <Input
                          value={eventForm.organizer_company}
                          onChange={(e) => setEventForm({ ...eventForm, organizer_company: e.target.value })}
                          placeholder="ABC BV"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>E-mail</Label>
                        <Input
                          type="email"
                          value={eventForm.organizer_email}
                          onChange={(e) => setEventForm({ ...eventForm, organizer_email: e.target.value })}
                          placeholder="contact@bedrijf.nl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefoon</Label>
                        <Input
                          value={eventForm.organizer_phone}
                          onChange={(e) => setEventForm({ ...eventForm, organizer_phone: e.target.value })}
                          placeholder="06-12345678"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#1C1917] flex items-center gap-2">
                      <Euro className="w-4 h-4" />
                      Financieel
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Ticketprijs per persoon (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={eventForm.ticket_price}
                          onChange={(e) => setEventForm({ ...eventForm, ticket_price: e.target.value })}
                          placeholder="25.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Inkoopprijs per persoon (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={eventForm.cost_per_person}
                          onChange={(e) => setEventForm({ ...eventForm, cost_per_person: e.target.value })}
                          placeholder="15.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Aanbetaling ontvangen (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={eventForm.deposit_paid}
                          onChange={(e) => setEventForm({ ...eventForm, deposit_paid: e.target.value })}
                          placeholder="500.00"
                        />
                      </div>
                    </div>
                    
                    {/* Automatische berekening voor Events */}
                    {(eventForm.expected_guests || eventForm.actual_guests) && (eventForm.ticket_price || eventForm.cost_per_person) && (() => {
                      const guests = parseInt(eventForm.actual_guests) || parseInt(eventForm.expected_guests) || 0;
                      const ticketPrice = parseFloat(eventForm.ticket_price) || 0;
                      const costPerPerson = parseFloat(eventForm.cost_per_person) || 0;
                      const totalRevenue = guests * ticketPrice;
                      const totalCosts = guests * costPerPerson;
                      const profit = totalRevenue - totalCosts;
                      const marginPct = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;
                      
                      return (
                        <div className="grid grid-cols-4 gap-3 p-4 bg-stone-50 rounded-lg border mt-4">
                          <div className="text-center">
                            <p className="text-xs text-[#57534E]">Totaal Omzet</p>
                            <p className="text-lg font-bold text-emerald-600">{formatCurrency(totalRevenue)}</p>
                            <p className="text-xs text-[#57534E]">{guests} × {formatCurrency(ticketPrice)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-[#57534E]">Totaal Kosten</p>
                            <p className="text-lg font-bold text-rose-600">{formatCurrency(totalCosts)}</p>
                            {costPerPerson > 0 && <p className="text-xs text-[#57534E]">{guests} × {formatCurrency(costPerPerson)}</p>}
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-[#57534E]">Winst</p>
                            <p className={`text-lg font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {formatCurrency(profit)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-[#57534E]">Marge</p>
                            <p className={`text-lg font-bold ${marginPct >= 20 ? 'text-emerald-600' : marginPct > 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                              {marginPct.toFixed(0)}%
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Beschrijving / Notities</Label>
                    <Textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      placeholder="Bijzonderheden, afspraken, menu wensen..."
                      rows={3}
                    />
                  </div>

                  <Button type="submit" className="w-full btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Event Aanmaken
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Event Analytics Dashboard */}
          {showEventAnalytics && (() => {
            const analytics = getEventAnalytics();
            return (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <Card className="card-base bg-gradient-to-br from-[#C2410C]/5 to-[#C2410C]/10">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Totaal Events</p>
                      <p className="text-2xl font-bold text-[#C2410C]">{analytics.stats.total}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Aankomend</p>
                      <p className="text-2xl font-bold text-blue-600">{analytics.stats.upcoming}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-emerald-50 to-emerald-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Totale Omzet</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(analytics.stats.totalRevenue)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-rose-50 to-rose-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Totale Kosten</p>
                      <p className="text-xl font-bold text-rose-600">{formatCurrency(analytics.stats.totalCosts)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-purple-50 to-purple-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Winst</p>
                      <p className="text-xl font-bold text-purple-600">{formatCurrency(analytics.stats.totalProfit)}</p>
                    </CardContent>
                  </Card>
                  <Card className="card-base bg-gradient-to-br from-amber-50 to-amber-100">
                    <CardContent className="p-4">
                      <p className="text-sm text-[#57534E]">Gem. Marge</p>
                      <p className="text-2xl font-bold text-amber-600">{analytics.stats.avgMarginPct.toFixed(0)}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts & Rankings */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Chart - Events per Type */}
                  <Card className="card-base lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#C2410C]" />
                        Prestaties per Event Type
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.typeChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={analytics.typeChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                            <Tooltip 
                              formatter={(value, name) => {
                                if (name === 'omzet' || name === 'winst') return [formatCurrency(value), name === 'omzet' ? 'Omzet' : 'Winst'];
                                return [value, 'Events'];
                              }}
                            />
                            <Legend />
                            <Bar yAxisId="left" dataKey="events" fill="#C2410C" name="Events" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="right" dataKey="winst" fill="#10B981" name="Winst" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-12 text-[#57534E]">
                          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                          <p>Nog geen data beschikbaar</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top Performers */}
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Top Events
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Highest Revenue */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Hoogste Omzet</p>
                        {analytics.byRevenue.filter(e => e.total_revenue > 0).slice(0, 3).map((event, i) => (
                          <div key={event.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{event.name}</span>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">{formatCurrency(event.total_revenue)}</Badge>
                          </div>
                        ))}
                        {analytics.byRevenue.filter(e => e.total_revenue > 0).length === 0 && (
                          <p className="text-sm text-[#57534E] italic">Nog geen omzet</p>
                        )}
                      </div>
                      
                      {/* Highest Profit */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Hoogste Winst</p>
                        {analytics.byProfit.filter(e => e.profit > 0).slice(0, 3).map((event, i) => (
                          <div key={event.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-purple-500 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{event.name}</span>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">{formatCurrency(event.profit)}</Badge>
                          </div>
                        ))}
                        {analytics.byProfit.filter(e => e.profit > 0).length === 0 && (
                          <p className="text-sm text-[#57534E] italic">Nog geen winst</p>
                        )}
                      </div>

                      {/* Most Guests */}
                      <div>
                        <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wide mb-2">Meeste Gasten</p>
                        {analytics.byGuests.filter(e => e.guests > 0).slice(0, 3).map((event, i) => (
                          <div key={event.id} className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-blue-500 text-white' : 'bg-stone-200 text-stone-600'}`}>
                                {i + 1}
                              </span>
                              <span className="text-sm truncate max-w-[120px]">{event.name}</span>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700">{event.guests} gasten</Badge>
                          </div>
                        ))}
                        {analytics.byGuests.filter(e => e.guests > 0).length === 0 && (
                          <p className="text-sm text-[#57534E] italic">Nog geen gasten</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Low Margin Warning */}
                {analytics.events.filter(e => e.margin_pct < 20 && e.margin_pct > 0 && e.total_revenue > 0).length > 0 && (
                  <Card className="card-base border-amber-300 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <TrendingDown className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800">Let op: Lage marges</p>
                          <p className="text-sm text-amber-700 mt-1">
                            De volgende events hebben een marge onder 20%:
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {analytics.events.filter(e => e.margin_pct < 20 && e.margin_pct > 0 && e.total_revenue > 0).map(event => (
                              <Badge key={event.id} className="bg-amber-200 text-amber-800">
                                {event.name}: {event.margin_pct.toFixed(0)}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* Sorting & Filtering */}
          <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
            <span className="text-sm font-medium text-[#57534E]">Sorteer op:</span>
            <Select value={eventSortBy} onValueChange={setEventSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Datum</SelectItem>
                <SelectItem value="revenue">Hoogste omzet</SelectItem>
                <SelectItem value="profit">Hoogste winst</SelectItem>
                <SelectItem value="guests">Meeste gasten</SelectItem>
                <SelectItem value="margin">Hoogste marge (%)</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setEventSortOrder(eventSortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {eventSortOrder === 'desc' ? (
                <><TrendingDown className="w-4 h-4 mr-1" /> Aflopend</>
              ) : (
                <><TrendingUp className="w-4 h-4 mr-1" /> Oplopend</>
              )}
            </Button>
          </div>

          {/* Events List */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Alle Evenementen</CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-12 text-[#57534E]">
                  <PartyPopper className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p className="font-medium">Nog geen evenementen</p>
                  <p className="text-sm mt-1">Maak je eerste event aan</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Event</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead>Gasten</TableHead>
                      <TableHead>Organisator</TableHead>
                      <TableHead>Kosten</TableHead>
                      <TableHead>Omzet</TableHead>
                      <TableHead>Winst</TableHead>
                      <TableHead>Marge</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedEvents().map((event, index) => {
                      const profit = (event.total_revenue || 0) - (event.total_costs || 0);
                      const marginPct = event.total_revenue > 0 ? (profit / event.total_revenue) * 100 : 0;
                      return (
                        <TableRow key={event.id} className="table-row cursor-pointer hover:bg-stone-50" onClick={() => openEventDetail(event)}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {eventSortBy !== 'date' && index < 3 && (
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-400 text-white' : index === 1 ? 'bg-stone-400 text-white' : 'bg-amber-700 text-white'}`}>
                                  {index + 1}
                                </span>
                              )}
                              <div>
                                <p className="font-medium text-[#1C1917]">{event.name}</p>
                                {event.location && (
                                  <p className="text-xs text-[#57534E] flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {event.location}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {eventTypeLabels[event.event_type] || event.event_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{formatDate(event.event_date)}</p>
                              {event.start_time && (
                                <p className="text-xs text-[#57534E]">{event.start_time} - {event.end_time}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4 text-[#57534E]" />
                              <span>{event.actual_guests || event.expected_guests || '-'}</span>
                              {event.expected_guests > 0 && event.actual_guests > 0 && (
                                <span className="text-xs text-[#57534E]">/{event.expected_guests}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {event.organizer_name || event.organizer_company ? (
                              <div>
                                <p className="text-sm">{event.organizer_name}</p>
                                {event.organizer_company && (
                                  <p className="text-xs text-[#57534E]">{event.organizer_company}</p>
                                )}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-rose-600 font-medium">
                            {event.total_costs > 0 ? formatCurrency(event.total_costs) : '-'}
                          </TableCell>
                          <TableCell className="text-emerald-600 font-medium">
                            {event.total_revenue > 0 ? formatCurrency(event.total_revenue) : '-'}
                          </TableCell>
                          <TableCell>
                            <span className={`font-medium ${profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {event.total_revenue > 0 || event.total_costs > 0 ? formatCurrency(profit) : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {event.total_revenue > 0 ? (
                              <Badge className={`${marginPct >= 50 ? 'bg-emerald-100 text-emerald-700' : marginPct >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                                {marginPct.toFixed(0)}%
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={eventStatusColors[event.status] || 'bg-stone-100 text-stone-700'}>
                              {event.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEventDetail(event); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Event Detail Dialog */}
          <Dialog open={eventDetailOpen} onOpenChange={setEventDetailOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedEvent?.name}</span>
                  <div className="flex items-center gap-2">
                    {!editingEvent && (
                      <>
                        <Button size="sm" variant="outline" onClick={startEditEvent}>
                          <Edit className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => handleDeleteEvent(selectedEvent?.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              {selectedEvent && (
                <div className="space-y-6 pt-4">
                  {editingEvent ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Naam</Label>
                          <Input
                            value={eventForm.name}
                            onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(eventTypeLabels).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Datum</Label>
                          <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Starttijd</Label>
                          <Input type="time" value={eventForm.start_time} onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Eindtijd</Label>
                          <Input type="time" value={eventForm.end_time} onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Verwacht gasten</Label>
                          <Input type="number" value={eventForm.expected_guests} onChange={(e) => setEventForm({ ...eventForm, expected_guests: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select value={eventForm.status} onValueChange={(v) => setEventForm({ ...eventForm, status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gepland">Gepland</SelectItem>
                              <SelectItem value="bevestigd">Bevestigd</SelectItem>
                              <SelectItem value="afgelopen">Afgelopen</SelectItem>
                              <SelectItem value="geannuleerd">Geannuleerd</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Contactpersoon</Label>
                          <Input value={eventForm.organizer_name} onChange={(e) => setEventForm({ ...eventForm, organizer_name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Bedrijf</Label>
                          <Input value={eventForm.organizer_company} onChange={(e) => setEventForm({ ...eventForm, organizer_company: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input type="email" value={eventForm.organizer_email} onChange={(e) => setEventForm({ ...eventForm, organizer_email: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Telefoon</Label>
                          <Input value={eventForm.organizer_phone} onChange={(e) => setEventForm({ ...eventForm, organizer_phone: e.target.value })} />
                        </div>
                      </div>
                      
                      {/* Financial Fields in Edit Mode */}
                      <div className="space-y-3 p-4 bg-emerald-50 rounded-lg">
                        <h4 className="font-semibold text-emerald-900 flex items-center gap-2">
                          <Euro className="w-4 h-4" />
                          Financieel
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Ticketprijs p.p. (€)</Label>
                            <Input type="number" step="0.01" value={eventForm.ticket_price} onChange={(e) => setEventForm({ ...eventForm, ticket_price: e.target.value })} placeholder="25.00" />
                          </div>
                          <div className="space-y-2">
                            <Label>Inkoopprijs p.p. (€)</Label>
                            <Input type="number" step="0.01" value={eventForm.cost_per_person} onChange={(e) => setEventForm({ ...eventForm, cost_per_person: e.target.value })} placeholder="15.00" />
                          </div>
                          <div className="space-y-2">
                            <Label>Aanbetaling (€)</Label>
                            <Input type="number" step="0.01" value={eventForm.deposit_paid} onChange={(e) => setEventForm({ ...eventForm, deposit_paid: e.target.value })} placeholder="500.00" />
                          </div>
                        </div>
                        {/* Real-time calculation in edit mode */}
                        {(eventForm.expected_guests || eventForm.actual_guests) && (eventForm.ticket_price || eventForm.cost_per_person) && (() => {
                          const guests = parseInt(eventForm.actual_guests) || parseInt(eventForm.expected_guests) || 0;
                          const ticketPrice = parseFloat(eventForm.ticket_price) || 0;
                          const costPerPerson = parseFloat(eventForm.cost_per_person) || 0;
                          const totalRevenue = guests * ticketPrice;
                          const totalCosts = guests * costPerPerson;
                          const profit = totalRevenue - totalCosts;
                          const marginPct = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;
                          
                          return (
                            <div className="grid grid-cols-4 gap-2 mt-3 p-3 bg-white rounded border">
                              <div className="text-center">
                                <p className="text-xs text-[#57534E]">Omzet</p>
                                <p className="font-bold text-emerald-600">{formatCurrency(totalRevenue)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-[#57534E]">Kosten</p>
                                <p className="font-bold text-rose-600">{formatCurrency(totalCosts)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-[#57534E]">Winst</p>
                                <p className={`font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(profit)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-[#57534E]">Marge</p>
                                <p className={`font-bold ${marginPct >= 20 ? 'text-emerald-600' : marginPct > 0 ? 'text-amber-600' : 'text-stone-400'}`}>{marginPct.toFixed(0)}%</p>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <div className="space-y-2">
                        <Label>Notities</Label>
                        <Textarea value={eventForm.notes} onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })} rows={3} />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleUpdateEvent} className="btn-primary">Opslaan</Button>
                        <Button variant="outline" onClick={() => setEditingEvent(false)}>Annuleren</Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <>
                      {/* Event Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-xs text-[#57534E]">Type</p>
                          <p className="font-medium">{eventTypeLabels[selectedEvent.event_type] || selectedEvent.event_type}</p>
                        </div>
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-xs text-[#57534E]">Datum</p>
                          <p className="font-medium">{formatDate(selectedEvent.event_date)}</p>
                          {selectedEvent.start_time && <p className="text-xs">{selectedEvent.start_time} - {selectedEvent.end_time}</p>}
                        </div>
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-xs text-[#57534E]">Gasten</p>
                          <p className="font-medium">
                            {selectedEvent.actual_guests || '-'} 
                            {selectedEvent.expected_guests > 0 && <span className="text-[#57534E] text-sm"> / {selectedEvent.expected_guests}</span>}
                          </p>
                        </div>
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-xs text-[#57534E]">Status</p>
                          <Badge className={eventStatusColors[selectedEvent.status]}>{selectedEvent.status}</Badge>
                        </div>
                      </div>

                      {/* Organizer */}
                      {(selectedEvent.organizer_name || selectedEvent.organizer_company) && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
                            <Building2 className="w-4 h-4" />
                            Organisator
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {selectedEvent.organizer_name && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-600" />
                                <span>{selectedEvent.organizer_name}</span>
                              </div>
                            )}
                            {selectedEvent.organizer_company && (
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                <span>{selectedEvent.organizer_company}</span>
                              </div>
                            )}
                            {selectedEvent.organizer_email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-600" />
                                <span>{selectedEvent.organizer_email}</span>
                              </div>
                            )}
                            {selectedEvent.organizer_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600" />
                                <span>{selectedEvent.organizer_phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Financial Summary with Cost Price & Margin */}
                      <div className="space-y-4">
                        {/* Price Info Row */}
                        {(selectedEvent.ticket_price > 0 || selectedEvent.cost_per_person > 0) && (
                          <div className="grid grid-cols-3 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="text-center">
                              <p className="text-xs text-amber-700 mb-1">Ticketprijs p.p.</p>
                              <p className="text-lg font-bold text-amber-800">
                                {selectedEvent.ticket_price > 0 ? formatCurrency(selectedEvent.ticket_price) : '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-amber-700 mb-1">Inkoopprijs p.p.</p>
                              <p className="text-lg font-bold text-amber-800">
                                {selectedEvent.cost_per_person > 0 ? formatCurrency(selectedEvent.cost_per_person) : '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-amber-700 mb-1">Marge p.p.</p>
                              {selectedEvent.ticket_price > 0 && selectedEvent.cost_per_person > 0 ? (
                                <>
                                  <p className={`text-lg font-bold ${(selectedEvent.ticket_price - selectedEvent.cost_per_person) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                    {formatCurrency(selectedEvent.ticket_price - selectedEvent.cost_per_person)}
                                  </p>
                                  <p className="text-xs text-amber-600">
                                    ({(((selectedEvent.ticket_price - selectedEvent.cost_per_person) / selectedEvent.ticket_price) * 100).toFixed(0)}%)
                                  </p>
                                </>
                              ) : (
                                <p className="text-lg font-bold text-stone-400">-</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Total Financial Summary */}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="p-4 bg-rose-50 rounded-lg text-center">
                            <p className="text-xs text-rose-600 mb-1">Totale Kosten</p>
                            <p className="text-xl font-bold text-rose-700">{formatCurrency(selectedEvent.total_costs || 0)}</p>
                            {selectedEvent.cost_per_person > 0 && (selectedEvent.actual_guests || selectedEvent.expected_guests) && (
                              <p className="text-xs text-rose-500">
                                {selectedEvent.actual_guests || selectedEvent.expected_guests} × {formatCurrency(selectedEvent.cost_per_person)}
                              </p>
                            )}
                          </div>
                          <div className="p-4 bg-emerald-50 rounded-lg text-center">
                            <p className="text-xs text-emerald-600 mb-1">Totale Omzet</p>
                            <p className="text-xl font-bold text-emerald-700">{formatCurrency(selectedEvent.total_revenue || 0)}</p>
                            {selectedEvent.ticket_price > 0 && (selectedEvent.actual_guests || selectedEvent.expected_guests) && (
                              <p className="text-xs text-emerald-500">
                                {selectedEvent.actual_guests || selectedEvent.expected_guests} × {formatCurrency(selectedEvent.ticket_price)}
                              </p>
                            )}
                          </div>
                          <div className={`p-4 rounded-lg text-center ${(selectedEvent.total_revenue - selectedEvent.total_costs) >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                            <p className="text-xs mb-1">Winst</p>
                            <p className={`text-xl font-bold ${(selectedEvent.total_revenue - selectedEvent.total_costs) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {formatCurrency((selectedEvent.total_revenue || 0) - (selectedEvent.total_costs || 0))}
                            </p>
                          </div>
                          <div className={`p-4 rounded-lg text-center ${(() => {
                            const marginPct = selectedEvent.total_revenue > 0 
                              ? (((selectedEvent.total_revenue - selectedEvent.total_costs) / selectedEvent.total_revenue) * 100) 
                              : 0;
                            return marginPct >= 20 ? 'bg-emerald-100' : marginPct > 0 ? 'bg-amber-100' : 'bg-stone-100';
                          })()}`}>
                            <p className="text-xs mb-1">Marge</p>
                            <p className={`text-xl font-bold ${(() => {
                              const marginPct = selectedEvent.total_revenue > 0 
                                ? (((selectedEvent.total_revenue - selectedEvent.total_costs) / selectedEvent.total_revenue) * 100) 
                                : 0;
                              return marginPct >= 20 ? 'text-emerald-700' : marginPct > 0 ? 'text-amber-700' : 'text-stone-500';
                            })()}`}>
                              {selectedEvent.total_revenue > 0 
                                ? `${(((selectedEvent.total_revenue - selectedEvent.total_costs) / selectedEvent.total_revenue) * 100).toFixed(0)}%`
                                : '-'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Linked Costs */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-[#1C1917] flex items-center gap-2">
                            <Receipt className="w-4 h-4" />
                            Kosten
                          </h4>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setManualCostDialogOpen(true)}>
                              <Plus className="w-4 h-4 mr-1" />
                              Toevoegen
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { fetchAvailableCosts(); setLinkCostDialogOpen(true); }}>
                              <Link className="w-4 h-4 mr-1" />
                              Koppelen
                            </Button>
                          </div>
                        </div>
                        
                        {/* Manual costs */}
                        {selectedEvent.manual_costs?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Handmatig toegevoegd</p>
                            {selectedEvent.manual_costs.map(cost => (
                              <div key={cost.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-200">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded bg-rose-100 flex items-center justify-center">
                                    <Euro className="w-4 h-4 text-rose-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{cost.description}</p>
                                    <p className="text-xs text-[#57534E]">{cost.category} • {cost.date}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-rose-700">{formatCurrency(cost.amount)}</span>
                                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteManualCost(cost.id)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Linked costs */}
                        {selectedEvent.linked_costs_details?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-rose-500 uppercase tracking-wide">Gekoppeld</p>
                            {selectedEvent.linked_costs_details.map(cost => (
                              <div key={cost.id} className="flex items-center justify-between p-3 bg-rose-50/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded bg-rose-100 flex items-center justify-center">
                                    <Receipt className="w-4 h-4 text-rose-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{cost.supplier || cost.description}</p>
                                    <p className="text-xs text-[#57534E]">{cost.type} • {cost.date}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-rose-700">{formatCurrency(cost.amount)}</span>
                                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleUnlinkCost(cost.id)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {(!selectedEvent.manual_costs?.length && !selectedEvent.linked_costs_details?.length) && (
                          <p className="text-sm text-[#57534E] text-center py-4">Geen kosten toegevoegd</p>
                        )}
                      </div>

                      {/* Linked Revenue */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-[#1C1917] flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Omzet
                          </h4>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setManualRevenueDialogOpen(true)}>
                              <Plus className="w-4 h-4 mr-1" />
                              Toevoegen
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { fetchAvailableRevenue(); setLinkRevenueDialogOpen(true); }}>
                              <Link className="w-4 h-4 mr-1" />
                              Koppelen
                            </Button>
                          </div>
                        </div>
                        
                        {/* Manual revenue */}
                        {selectedEvent.manual_revenue?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Handmatig toegevoegd</p>
                            {selectedEvent.manual_revenue.map(rev => (
                              <div key={rev.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center">
                                    <Euro className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{rev.description}</p>
                                    <p className="text-xs text-[#57534E]">{rev.category} • {rev.date} {rev.guests > 0 && `• ${rev.guests} gasten`}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-emerald-700">{formatCurrency(rev.amount)}</span>
                                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteManualRevenue(rev.id)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Linked revenue */}
                        {selectedEvent.linked_revenue_details?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide">Gekoppeld</p>
                            {selectedEvent.linked_revenue_details.map(rev => (
                              <div key={rev.id} className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center">
                                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{rev.date}</p>
                                    <p className="text-xs text-[#57534E]">{rev.channel} • {rev.guests} gasten</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-emerald-700">{formatCurrency(rev.amount)}</span>
                                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleUnlinkRevenue(rev.id)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {(!selectedEvent.manual_revenue?.length && !selectedEvent.linked_revenue_details?.length) && (
                          <p className="text-sm text-[#57534E] text-center py-4">Geen omzet toegevoegd</p>
                        )}
                      </div>

                      {/* Notes */}
                      {selectedEvent.description && (
                        <div className="p-4 bg-stone-50 rounded-lg">
                          <h4 className="font-semibold text-[#1C1917] mb-2">Notities</h4>
                          <p className="text-sm text-[#57534E]">{selectedEvent.description}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Link Cost Dialog */}
          <Dialog open={linkCostDialogOpen} onOpenChange={setLinkCostDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kosten koppelen aan event</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
                {availableCosts.length === 0 ? (
                  <p className="text-center text-[#57534E] py-8">Geen facturen of bestellingen beschikbaar</p>
                ) : (
                  availableCosts.filter(c => !selectedEvent?.linked_costs?.includes(c.id)).map(cost => (
                    <div key={cost.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer" onClick={() => { handleLinkCost(cost.id); setLinkCostDialogOpen(false); }}>
                      <div>
                        <p className="font-medium">{cost.displayName}</p>
                        <p className="text-xs text-[#57534E]">{cost.costType} • {cost.invoice_date || cost.order_date}</p>
                      </div>
                      <Plus className="w-4 h-4 text-[#C2410C]" />
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Link Revenue Dialog */}
          <Dialog open={linkRevenueDialogOpen} onOpenChange={setLinkRevenueDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Omzet koppelen aan event</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
                {availableRevenue.length === 0 ? (
                  <p className="text-center text-[#57534E] py-8">Geen omzet entries beschikbaar</p>
                ) : (
                  availableRevenue.filter(r => !selectedEvent?.linked_revenue?.includes(r.id)).map(rev => (
                    <div key={rev.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer" onClick={() => { handleLinkRevenue(rev.id); setLinkRevenueDialogOpen(false); }}>
                      <div>
                        <p className="font-medium">{formatCurrency(rev.amount)}</p>
                        <p className="text-xs text-[#57534E]">{rev.date} • {rev.channel} • {rev.guests} gasten</p>
                      </div>
                      <Plus className="w-4 h-4 text-[#C2410C]" />
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual Cost Entry Dialog */}
          <Dialog open={manualCostDialogOpen} onOpenChange={setManualCostDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-rose-600" />
                  Kosten toevoegen aan "{selectedEvent?.name}"
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddManualCost} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Omschrijving *</Label>
                  <Input
                    value={manualCostForm.description}
                    onChange={(e) => setManualCostForm({ ...manualCostForm, description: e.target.value })}
                    placeholder="Bijv. Catering, Decoratie, DJ..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bedrag (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualCostForm.amount}
                      onChange={(e) => setManualCostForm({ ...manualCostForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select value={manualCostForm.category} onValueChange={(v) => setManualCostForm({ ...manualCostForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="food">Food</SelectItem>
                        <SelectItem value="beverage">Dranken</SelectItem>
                        <SelectItem value="personeel">Personeel</SelectItem>
                        <SelectItem value="materiaal">Materiaal</SelectItem>
                        <SelectItem value="locatie">Locatie</SelectItem>
                        <SelectItem value="overig">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={manualCostForm.date}
                    onChange={(e) => setManualCostForm({ ...manualCostForm, date: e.target.value })}
                  />
                </div>
                <p className="text-xs text-[#57534E] bg-amber-50 p-3 rounded-lg">
                  💡 Deze kosten worden automatisch meegenomen in de <strong>Kosten & Marges</strong> module en zijn daar traceerbaar naar dit event.
                </p>
                <Button type="submit" className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Kosten Toevoegen
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Manual Revenue Entry Dialog */}
          <Dialog open={manualRevenueDialogOpen} onOpenChange={setManualRevenueDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Omzet toevoegen aan "{selectedEvent?.name}"
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddManualRevenue} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Omschrijving *</Label>
                  <Input
                    value={manualRevenueForm.description}
                    onChange={(e) => setManualRevenueForm({ ...manualRevenueForm, description: e.target.value })}
                    placeholder="Bijv. Tickets, Consumpties, Merchandise..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bedrag (€) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualRevenueForm.amount}
                      onChange={(e) => setManualRevenueForm({ ...manualRevenueForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select value={manualRevenueForm.category} onValueChange={(v) => setManualRevenueForm({ ...manualRevenueForm, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tickets">Tickets</SelectItem>
                        <SelectItem value="consumpties">Consumpties</SelectItem>
                        <SelectItem value="extra">Extra's</SelectItem>
                        <SelectItem value="overig">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={manualRevenueForm.date}
                      onChange={(e) => setManualRevenueForm({ ...manualRevenueForm, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aantal gasten</Label>
                    <Input
                      type="number"
                      value={manualRevenueForm.guests}
                      onChange={(e) => setManualRevenueForm({ ...manualRevenueForm, guests: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#57534E] bg-emerald-50 p-3 rounded-lg">
                  💡 Deze omzet wordt automatisch meegenomen in de <strong>Omzet & Verkoop</strong> module en is daar traceerbaar naar dit event.
                </p>
                <Button type="submit" className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Omzet Toevoegen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      
      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog} 
      />
    </div>
  );
};

export default MarketingActiesPage;
